(function () {
  'use strict';

  var ROOT_SELECTOR = '#swish-court-builder, [data-swish-court-builder]';
  var EVENT_NAMES = [
    'swish:dimensions-changed',
    'swish:units-changed',
    'swish:dimension-display-changed',
    'swish:ruleset-changed',
    'swish:builder-state-changed'
  ];
  var rootStates = typeof WeakMap === 'function' ? new WeakMap() : null;
  var fallbackStates = [];
  var globalRefreshBound = false;
  var globalConfigWatchTimer = 0;
  var BASE_COLOR_LABELS = {
    main: 'Main court area',
    key: 'Key',
    lines: 'Court lines',
    border: 'Border',
    edge: 'Edges',
    hoopHardware: 'Hoop hardware'
  };
  var SPORT_AREA_COLOR_LABELS = {
    fiba: { main: 'Main court', key: 'Key' },
    nba: { main: 'Main court', key: 'Key' },
    ncaa: { main: 'Main court', key: 'Key' },
    usahs: { main: 'Main court', key: 'Key' },
    usahs3: { main: 'Main court', key: 'Key' },
    'us-highschool': { main: 'Main court', key: 'Key' },
    'us-highschool3': { main: 'Main court', key: 'Key' },
    'us-high-school': { main: 'Main court', key: 'Key' },
    'high-school': { main: 'Main court', key: 'Key' },
    hs: { main: 'Main court', key: 'Key' },
    basketball: { main: 'Main court', key: 'Key' },
    tennis: { main: 'Backcourt & alleys', key: 'Service courts' },
    pickleball: { main: 'Service courts', key: 'Kitchen' },
    padel: { main: 'Backcourts', key: 'Service boxes' },
    volleyball: { main: 'Back zones', key: 'Front zones' },
    shuffleboard: { main: 'Scoring areas', key: 'Details' },
    cornhole: { main: 'Boards & lanes', key: 'Details' },
    futsal: { main: 'Court halves', key: 'Penalty areas' },
    netball: { main: 'Thirds', key: 'Goal circles' },
    racquetball: { main: 'Racquetball court', key: 'Service zone' }
  };
  var SERIES_LABELS = {
    elastic: 'Elastic Tiles',
    elite: 'Elite Tiles',
    'large-elite': 'Large Elite Tiles',
    hardwood: 'Hardwood L1',
    hardwood2: 'Hardwood L2',
    hardwood3: 'Hardwood L3'
  };
  var ORDER_SERIES_IDS = ['elastic', 'large-elite', 'elite'];
  var MAX_ORDER_DESIGN_BYTES = 4 * 1024 * 1024;
  var ORDER_DESIGN_PREVIEW_DELAY_MS = 50;
  var ORDER_DESIGN_WATCH_INTERVAL_MS = 150;
  // Keep the in-panel Order Now artwork at checkout quality. The right-side
  // panel can display the image at more than 640 CSS pixels on high-density
  // screens, so the former 640 x 360 snapshot was visibly upscaled and soft.
  var ORDER_DESIGN_PREVIEW_WIDTH = 1600;
  var ORDER_DESIGN_PREVIEW_HEIGHT = 900;
  var ORDER_DESIGN_CHECKOUT_WIDTH = 1600;
  var ORDER_DESIGN_CHECKOUT_HEIGHT = 900;
  var COURT_CHANGED_DURING_PREPARATION_MESSAGE =
    'The court changed while checkout was being prepared. Please try again after finishing the design.';
  var SQUARE_FEET_PER_SQUARE_METRE = 10.763910416709722;
  var TILE_SERIES_SPECS = {
    elastic: {
      sizeMm: [303, 303, 15],
      edgeMm: 72.5,
      weightG: 340,
      weightToleranceG: 10,
      temperatureMinC: -50,
      temperatureMaxC: 70,
      use: 'Indoor and outdoor',
      warranty: '10 years',
      lifespan: '25+ years',
      benefits: [
        '50 medium-sized rubber pads provide excellent elasticity.',
        'Suitable for basketball, pickleball, racquetball, tennis, netball, and more.',
        'Park your car on the court.',
        'Protect your court by attaching it to the surface.'
      ]
    },
    elite: {
      sizeMm: [305, 305, 19],
      edgeMm: 80,
      weightG: 460,
      weightToleranceG: 10,
      temperatureMinC: -50,
      temperatureMaxC: 70,
      use: 'Indoor and outdoor',
      warranty: '15 years',
      lifespan: '35+ years',
      benefits: [
        '32 large rubber pads provide increased shock absorption.',
        'Designed for commercial and public courts.',
        'FIBA 3\u00D73 certified with a 15-year warranty.',
        'Protect your court by attaching it to the surface.'
      ]
    },
    'large-elite': {
      sizeMm: [500, 500, 19],
      edgeMm: 50,
      weightG: 940,
      weightToleranceG: 10,
      temperatureMinC: -50,
      temperatureMaxC: 70,
      use: 'Indoor and outdoor',
      warranty: '10 years',
      lifespan: '25+ years',
      benefits: [
        '84 large rubber pads provide increased shock absorption.',
        'Larger tiles make installation faster and easier.',
        'The elegant surface texture gives the court an exclusive appearance.'
      ]
    }
  };
  var BASE_COLOR_INPUT_IDS = {
    main: 'swishcb-color-main',
    key: 'swishcb-color-key',
    lines: 'swishcb-color-lines',
    border: 'swishcb-color-border',
    edge: 'swishcb-color-edge'
  };
  var THREE_POINT_DETAIL_IDS = [
    'leftThreePointLine',
    'rightThreePointLine',
    'topThreePointLine',
    'bottomThreePointLine'
  ];

  function toNumber(value) {
    var number = Number(String(value == null ? '' : value).trim().replace(',', '.'));
    return Number.isFinite(number) ? number : null;
  }

  function positiveNumber(value) {
    var number = toNumber(value);
    return number != null && number > 0 ? number : null;
  }

  function round(value, places) {
    var power = Math.pow(10, places == null ? 2 : places);
    return Math.round((Number(value) + Number.EPSILON) * power) / power;
  }

  function normalizeSport(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[_\s]+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  function getRoots() {
    var nodes = Array.prototype.slice.call(document.querySelectorAll(ROOT_SELECTOR));
    return nodes.filter(function (node, index) { return nodes.indexOf(node) === index; });
  }

  function getDefaultOrderMode(root) {
    var countryCode = '';
    if (root && root.dataset) countryCode = root.dataset.swishCountryCode || '';
    if (!countryCode && root && root.querySelector) {
      var orderPanel = root.querySelector('[data-swishcb-order-panel]');
      if (orderPanel && orderPanel.dataset) countryCode = orderPanel.dataset.swishcbOrderCountryCode || '';
    }
    if (!countryCode && window.SWISH_SYSTEM_LOCALIZATION) {
      countryCode = window.SWISH_SYSTEM_LOCALIZATION.countryCode || window.SWISH_SYSTEM_LOCALIZATION.country || '';
    }
    return String(countryCode).trim().toUpperCase() === 'US' ? 'order' : 'contact';
  }

  function getState(root) {
    if (rootStates) {
      var state = rootStates.get(root);
      if (!state) {
        state = {
          dimensions: null,
          result: null,
          orderMode: getDefaultOrderMode(root),
          refreshTimer: 0,
          pendingRefreshDetail: null,
          designTimer: 0,
          designWatchTimer: 0,
          observedDesignSignature: '',
          observedDesignSignatureCount: 0,
          designScheduledSignature: '',
          designToken: 0,
          designPromise: null,
          designPendingSignature: '',
          designSignature: '',
          designAsset: null,
          designDisplayUrl: '',
          uploadedDesignSignature: '',
          uploadedDesignUrl: '',
          uploadedDesignMeta: null,
          orderPanelSession: null,
          orderPanelTransition: false
        };
        rootStates.set(root, state);
      }
      return state;
    }
    for (var i = 0; i < fallbackStates.length; i += 1) {
      if (fallbackStates[i].root === root) return fallbackStates[i].state;
    }
    var fallback = {
      dimensions: null,
      result: null,
      orderMode: getDefaultOrderMode(root),
      refreshTimer: 0,
      pendingRefreshDetail: null,
      designTimer: 0,
      designWatchTimer: 0,
      observedDesignSignature: '',
      observedDesignSignatureCount: 0,
      designScheduledSignature: '',
      designToken: 0,
      designPromise: null,
      designPendingSignature: '',
      designSignature: '',
      designAsset: null,
      designDisplayUrl: '',
      uploadedDesignSignature: '',
      uploadedDesignUrl: '',
      uploadedDesignMeta: null,
      orderPanelSession: null,
      orderPanelTransition: false
    };
    fallbackStates.push({ root: root, state: fallback });
    return fallback;
  }

  function readConfig(root) {
    var input = root.querySelector('[data-bind="config-json"]');
    if (!input || !input.value) return null;
    try {
      return JSON.parse(input.value);
    } catch (_) {
      return null;
    }
  }

  function orderDesignSignature(root, panel) {
    var configInput = root && root.querySelector ? root.querySelector('[data-bind="config-json"]') : null;
    var builderSeries = root && root.querySelector ? root.querySelector('#swishcb-tile-series') : null;
    var orderSeries = panel && panel.querySelector ? panel.querySelector('[data-swishcb-order-series-select]') : null;
    var series = getOrderSeriesId(
      builderSeries && builderSeries.value ? builderSeries.value : (orderSeries && orderSeries.value)
    ) || 'none';
    var config = configInput ? String(configInput.value || '') : '';
    if (!config) {
      var widthInput = root && root.querySelector ? root.querySelector('#swishcb-width') : null;
      var lengthInput = root && root.querySelector ? root.querySelector('#swishcb-length') : null;
      config = JSON.stringify({
        urlState: String(window.location && window.location.hash || ''),
        width: widthInput ? String(widthInput.value || '') : '',
        length: lengthInput ? String(lengthInput.value || '') : ''
      });
    }
    try {
      var designConfig = JSON.parse(config);
      if (designConfig && typeof designConfig === 'object') {
        // Camera/view state changes continuously during 3D auto-rotation but does
        // not alter the canonical, unrotated 2D court artwork.
        delete designConfig.view3d;
        delete designConfig.rotation;
        config = JSON.stringify(designConfig);
      }
    } catch (_) {}
    // The compact config omits some controls, including hoop selection and
    // overhangs. Include the builder's canonical share state so these edits
    // invalidate the 2D image even when the overall dimensions stay the same.
    var courtSignature = '';
    try {
      if (root && typeof root.__swishcbBuildShareHashFromCurrentState === 'function') {
        var courtState = new URLSearchParams(String(root.__swishcbBuildShareHashFromCurrentState())
          .replace(/^#?swishcb=/, ''));
        courtState.delete('rot');
        courtState.delete('view');
        courtState.delete('ar');
        // The manifest URL is a storage pointer created while originals are
        // persisted. Logo source and placement changes are already covered by
        // artworkSignature, so this URL must not masquerade as a design edit.
        courtState.delete('lu');
        courtSignature = courtState.toString();
      }
    } catch (_) {}
    var artworkSignature = '';
    try {
      var api = window.SwishCourtBuilder;
      if (api && typeof api.getArtworkSignature === 'function') {
        artworkSignature = String(api.getArtworkSignature() || '');
      }
    } catch (_) {}
    return config + '|court=' + courtSignature + '|order-series=' + series + '|artwork=' + artworkSignature;
  }

  function setOrderDesignUrl(root, value) {
    if (!root || !root.querySelector) return;
    var input = root.querySelector('[data-bind="design-2d-url"]');
    if (!input) return;
    input.value = value == null ? '' : String(value);
    input.setAttribute('value', input.value);
    try { input.dispatchEvent(new Event('change', { bubbles: true })); } catch (_) {}
  }

  function revokeOrderDesignUrl(value) {
    if (!value || String(value).indexOf('blob:') !== 0) return;
    try { URL.revokeObjectURL(String(value)); } catch (_) {}
  }

  async function decodeOrderDesignImage(value) {
    if (!value || typeof window.Image !== 'function') return;
    var preview = new window.Image();
    preview.decoding = 'async';
    preview.src = String(value);
    if (typeof preview.decode === 'function') {
      await preview.decode();
      return;
    }
    await new Promise(function (resolve, reject) {
      preview.addEventListener('load', resolve, { once: true });
      preview.addEventListener('error', function () {
        reject(new Error('The updated 2D design image could not be displayed.'));
      }, { once: true });
    });
  }

  function getOrderDesignGroup(panel) {
    if (!panel || !panel.querySelector) return null;
    var root = panel.closest('#swish-court-builder');
    return (root && root.querySelector('[data-swishcb-product-order-specifications] [data-swishcb-order-design]'))
      || panel.querySelector('[data-swishcb-order-design]');
  }

  function setOrderDesignUi(panel, stateName, message, asset) {
    if (!panel || !panel.querySelector) return;
    var group = getOrderDesignGroup(panel);
    var image = group && group.querySelector('[data-swishcb-order-design-image]');
    var status = group && group.querySelector('[data-swishcb-order-design-status]');
    if (!group || !image || !status) return;
    if (stateName !== 'ready') closeOrderMediaZoom(panel);
    group.dataset.state = stateName || '';
    group.setAttribute('aria-busy', stateName === 'loading' ? 'true' : 'false');
    if (message != null) setText(status, message);
    if (stateName === 'ready' && asset && asset.displayUrl) {
      image.src = asset.displayUrl;
      image.alt = asset.alt || 'Current court design in 2D';
      if (asset.width) image.width = asset.width;
      if (asset.height) image.height = asset.height;
      group.dataset.hasPreview = 'true';
      image.hidden = false;
      return;
    }
    var hasPreview = !!(image.currentSrc || image.getAttribute('src'));
    group.dataset.hasPreview = hasPreview ? 'true' : 'false';
    image.hidden = !hasPreview;
  }

  function invalidateUploadedOrderDesign(root, state) {
    if (!state) return;
    state.uploadedDesignSignature = '';
    state.uploadedDesignUrl = '';
    state.uploadedDesignMeta = null;
    setOrderDesignUrl(root, '');
  }

  function waitForOrderDesignPaint() {
    return new Promise(function (resolve) {
      if (typeof window.requestAnimationFrame !== 'function') {
        window.setTimeout(resolve, 0);
        return;
      }
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(resolve);
      });
    });
  }

  async function dataUrlToBlob(dataUrl) {
    if (!dataUrl || String(dataUrl).indexOf('data:') !== 0) return null;
    try {
      var response = await window.fetch(String(dataUrl));
      return await response.blob();
    } catch (_) {
      return null;
    }
  }

  function safeOrderDesignFileName(root, panel) {
    var title = panel && panel.querySelector ? panel.querySelector('[data-swishcb-order-generated-title]') : null;
    var base = String(title && title.textContent || 'swish-court-design')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 90);
    return (base || 'swish-court-design') + '-2d.png';
  }

  async function generateOrderDesign(root, panel, options) {
    options = options || {};
    var state = getState(root);
    var signature = orderDesignSignature(root, panel);
    if (!signature) {
      setOrderDesignUi(panel, 'loading', 'Loading');
      return null;
    }
    if (!options.force && state.designAsset && state.designSignature === signature) {
      setOrderDesignUi(panel, 'ready', '', state.designAsset);
      return state.designAsset;
    }
    if (!options.force && state.designPromise && state.designPendingSignature === signature) {
      return state.designPromise;
    }

    state.designToken += 1;
    var token = state.designToken;
    state.designPendingSignature = signature;
    invalidateUploadedOrderDesign(root, state);
    setOrderDesignUi(panel, 'loading', 'Loading');

    var promise = (async function () {
      await waitForOrderDesignPaint();
      var api = window.SwishCourtBuilder;
      if (!api || typeof api.getSnapshotData !== 'function') {
        throw new Error('The 2D design renderer is still loading.');
      }
      var checkoutQuality = !!options.checkoutQuality;
      var renderWidth = checkoutQuality ? ORDER_DESIGN_CHECKOUT_WIDTH : ORDER_DESIGN_PREVIEW_WIDTH;
      var renderHeight = checkoutQuality ? ORDER_DESIGN_CHECKOUT_HEIGHT : ORDER_DESIGN_PREVIEW_HEIGHT;
      // Redraw from current settings even when only line colors/visibility change.
      // Reusing the live canvas can retain old artwork or trigger a deferred
      // redraw during encoding, repeatedly invalidating the pending design.
      var snapshot = await api.getSnapshotData({
        type: 'image/png',
        quality: 0.96,
        maxWidth: renderWidth,
        maxHeight: renderHeight,
        renderWidth: renderWidth,
        renderHeight: renderHeight,
        renderAtMaxSize: true,
        noRotation: true,
        whiteBg: true,
        saveStyle: true,
        includeBlob: true,
        includeDataUrl: false
      });
      if (!snapshot || (!snapshot.blob && !snapshot.dataUrl)) {
        throw new Error('The current 2D design could not be generated.');
      }
      var blob = snapshot.blob || await dataUrlToBlob(snapshot.dataUrl);
      if (!blob) throw new Error('The current 2D design file could not be generated.');
      if (blob.size > MAX_ORDER_DESIGN_BYTES) {
        throw new Error('The current 2D design is too large to attach to checkout.');
      }
      var currentSignature = orderDesignSignature(root, panel);
      if (token !== state.designToken || currentSignature !== signature) return null;

      var displayUrl = '';
      try { displayUrl = URL.createObjectURL(blob); } catch (_) { displayUrl = snapshot.dataUrl; }
      var asset = {
        signature: signature,
        blob: blob,
        displayUrl: displayUrl,
        dataUrl: snapshot.dataUrl,
        fileName: safeOrderDesignFileName(root, panel),
        mimeType: String(blob.type || snapshot.type || 'image/png'),
        width: Number(snapshot.width) || null,
        height: Number(snapshot.height) || null,
        alt: (function () {
          var seriesId = getOrderSeriesId(root.querySelector('#swishcb-tile-series') && root.querySelector('#swishcb-tile-series').value);
          return seriesId
            ? 'Current ' + seriesId + ' tile court design in 2D'
            : 'Current court design in 2D';
        }())
      };
      try {
        await decodeOrderDesignImage(displayUrl);
      } catch (error) {
        revokeOrderDesignUrl(displayUrl);
        throw error;
      }
      currentSignature = orderDesignSignature(root, panel);
      if (token !== state.designToken || currentSignature !== signature) {
        revokeOrderDesignUrl(displayUrl);
        return null;
      }
      var previousDisplayUrl = state.designDisplayUrl;
      state.designDisplayUrl = displayUrl;
      state.designAsset = asset;
      state.designSignature = signature;
      setOrderDesignUi(panel, 'ready', '', asset);
      if (previousDisplayUrl !== displayUrl) revokeOrderDesignUrl(previousDisplayUrl);
      return asset;
    })();

    state.designPromise = promise;
    try {
      return await promise;
    } catch (error) {
      if (token === state.designToken) {
        setOrderDesignUi(
          panel,
          'error',
          error && error.message ? String(error.message) : 'The current 2D design could not be generated.'
        );
      }
      throw error;
    } finally {
      if (state.designPromise === promise) {
        state.designPromise = null;
        state.designPendingSignature = '';
      }
    }
  }

  function scheduleOrderDesignUpdate(root, panel, delay) {
    if (!root || !panel) return;
    // Preview-card editors prepare checkout artwork only after Order Now.
    if (root.hasAttribute('data-swish-preview-order-context')) return;
    // The existing watcher retries when the panel enters view. Do not build a
    // checkout-size PNG for an order panel the visitor has not reached yet.
    if (document.hidden) return;
    // Product summaries own the design image even when the separate tile-info
    // panel is collapsed. Render only when the actual image group is visible.
    var group = getOrderDesignGroup(panel);
    if (!group) return;
    var panelRect = group.getBoundingClientRect();
    if (!panelRect.width || !panelRect.height || panelRect.bottom <= 0 || panelRect.top >= window.innerHeight) return;
    var state = getState(root);
    var signature = orderDesignSignature(root, panel);
    if (!signature) return;
    if (state.designSignature !== signature) {
      invalidateUploadedOrderDesign(root, state);
      setOrderDesignUi(panel, 'loading', 'Loading');
    }
    if (state.designAsset && state.designSignature === signature) return;
    if (state.designTimer && state.designScheduledSignature === signature) return;
    if (state.designTimer) window.clearTimeout(state.designTimer);
    state.designScheduledSignature = signature;
    state.designTimer = window.setTimeout(function () {
      state.designTimer = 0;
      state.designScheduledSignature = '';
      generateOrderDesign(root, panel).catch(function () {});
    }, Number.isFinite(Number(delay)) ? Number(delay) : ORDER_DESIGN_PREVIEW_DELAY_MS);
  }

  function readConfigDimensions(root) {
    var config = readConfig(root);
    if (!config) return null;
    try {
      var candidates = [
        config,
        config && config.state,
        config && config.court,
        config && config.dimensions,
        config && config.mainCourt
      ];
      for (var i = 0; i < candidates.length; i += 1) {
        var candidate = candidates[i];
        if (!candidate || typeof candidate !== 'object') continue;
        var width = positiveNumber(candidate.width != null ? candidate.width : candidate.w);
        var length = positiveNumber(candidate.length != null ? candidate.length : candidate.l);
        if (width && length) return { widthM: width, lengthM: length };
      }
    } catch (_) {}
    return null;
  }

  function readConfigInstalledDimensions(root) {
    var config = readConfig(root);
    if (!config) return null;
    var candidates = [
      config.installedSurfaceM,
      config.state && config.state.installedSurfaceM,
      config.court && config.court.installedSurfaceM
    ];
    for (var i = 0; i < candidates.length; i += 1) {
      var candidate = candidates[i];
      if (!candidate || typeof candidate !== 'object') continue;
      var width = positiveNumber(candidate.width != null ? candidate.width : candidate.width_m);
      var length = positiveNumber(candidate.length != null ? candidate.length : candidate.length_m);
      if (width && length) return { widthM: width, lengthM: length };
    }
    return null;
  }

  function readDisplayedDimensions(root) {
    var widthInput = root.querySelector('#swishcb-width');
    var lengthInput = root.querySelector('#swishcb-length');
    if (!widthInput || !lengthInput) return null;
    var width = positiveNumber(widthInput.value);
    var length = positiveNumber(lengthInput.value);
    if (!width || !length) return null;
    var unitInput = root.querySelector('input[name="swishcb-units"]:checked');
    var units = unitInput ? String(unitInput.value || '').toLowerCase() : 'm';
    if (units === 'ft') {
      width *= 0.3048;
      length *= 0.3048;
    }
    return { widthM: width, lengthM: length };
  }

  function readDimensions(root, eventDetail) {
    // Use the canonical installed surface first. Imperial inputs are rounded
    // for display, and converting those rounded values back to metres would
    // otherwise change the court area and total when the unit toggle changes.
    var installedDimensions = readConfigInstalledDimensions(root);
    if (installedDimensions) return installedDimensions;
    // The displayed fields are the fallback for legacy configurations. They
    // already include court borders and any selected edge ramps.
    var displayedDimensions = readDisplayedDimensions(root);
    if (displayedDimensions) return displayedDimensions;
    if (eventDetail && typeof eventDetail === 'object') {
      var eventWidth = positiveNumber(eventDetail.width);
      var eventLength = positiveNumber(eventDetail.length);
      if (eventWidth && eventLength) return { widthM: eventWidth, lengthM: eventLength };
    }
    return readConfigDimensions(root);
  }

  function readSport(root) {
    var typeSelect = root.querySelector('#court-xxx, [data-swishcb-layout-block-type="swish_row_type"] select');
    if (!typeSelect) return '';
    return normalizeSport(typeSelect.value || (typeSelect.selectedOptions && typeSelect.selectedOptions[0] && typeSelect.selectedOptions[0].textContent));
  }

  function getSelectedOptionText(root, selector) {
    var select = root.querySelector(selector);
    if (!select || !select.options || select.selectedIndex < 0) return '';
    return String(select.options[select.selectedIndex].textContent || '').trim();
  }

  function getBuilderPayload(root) {
    try {
      if (window.SwishCourtBuilder && typeof window.SwishCourtBuilder.getContactPayload === 'function') {
        var payload = window.SwishCourtBuilder.getContactPayload();
        if (payload && typeof payload === 'object') {
          var currentConfig = readConfig(root) || {};
          if (Array.isArray(currentConfig.texts)) payload.texts = currentConfig.texts;
          if (!payload.config) payload.config = currentConfig;
          return payload;
        }
      }
    } catch (_) {}
    var config = readConfig(root) || {};
    var logoFiles = Array.prototype.reduce.call(
      root.querySelectorAll('input[type="file"][id="swishcb-logo"]'),
      function (files, input) {
        return files.concat(Array.prototype.slice.call(input.files || []));
      },
      []
    );
    return {
      units: config.units || 'm',
      widthM: positiveNumber(config.width),
      lengthM: positiveNumber(config.length),
      ruleset: config.ruleset || '',
      tilesEnabled: !!config.tilesEnabled,
      hardwoodEnabled: !!config.hardwoodEnabled,
      tileSeries: config.tileSeries || '',
      colors: config.colors || {},
      pattern: config.pattern || null,
      detailColors: config.detailColors || {},
      hiddenDetails: config.hiddenDetails || [],
      detailVisibility: {},
      extraSports: config.extras || {},
      extraSportLayerOrder: config.extraSportLayerOrder || [],
      texts: config.texts || [],
      logos: logoFiles.map(function (file, index) {
        return {
          label: 'Logo ' + (index + 1),
          fileName: file.name || '',
          fileType: file.type || '',
          fileSize: Number(file.size) || 0,
          fileLastModified: Number(file.lastModified) || 0
        };
      }),
      logoFiles: logoFiles,
      config: config
    };
  }

  function isHardwoodSeriesSelected(root, payload) {
    var select = root && root.querySelector ? root.querySelector('#swishcb-tile-series') : null;
    var value = String(select && select.value || '').trim().toLowerCase();
    if (value === 'none') return false;
    if (value === 'hardwood' || value === 'hardwood2' || value === 'hardwood3') return true;
    if (payload && payload.hardwoodEnabled === true) return true;
    var config = readConfig(root) || {};
    return config.hardwoodEnabled === true;
  }

  function getArtworkPlacements(payload) {
    payload = payload && typeof payload === 'object' ? payload : {};
    var placements = [];
    (Array.isArray(payload.logos) ? payload.logos : []).forEach(function (logo, index) {
      var widthM = positiveNumber(logo && logo.widthM);
      var heightM = positiveNumber(logo && logo.heightM);
      if (!widthM || !heightM) return;
      placements.push({
        kind: 'logo',
        label: String(logo.label || ('Logo ' + (index + 1))).trim(),
        widthM: widthM,
        heightM: heightM,
        areaM2: widthM * heightM
      });
    });
    (Array.isArray(payload.texts) ? payload.texts : []).forEach(function (entry, index) {
      var text = String(entry && entry.text || '').trim();
      var widthM = positiveNumber(entry && entry.widthM);
      var heightM = positiveNumber(entry && entry.heightM);
      if (!text || !widthM || !heightM) return;
      placements.push({
        kind: 'text',
        label: String(entry.label || ('Text ' + (index + 1))).trim(),
        widthM: widthM,
        heightM: heightM,
        areaM2: widthM * heightM
      });
    });
    return placements;
  }

  function getArtworkPricingOptions(panel) {
    var freeAreaM2 = toNumber(panel && panel.dataset.swishcbOrderArtworkFreeAreaM2);
    var ratePerM2 = toNumber(panel && panel.dataset.swishcbOrderArtworkRatePerM2);
    return {
      artworkFreeAreaM2: freeAreaM2 != null && freeAreaM2 >= 0 ? freeAreaM2 : 4,
      artworkRatePerM2: ratePerM2 != null && ratePerM2 >= 0 ? ratePerM2 : 50
    };
  }

  function getArtworkUsage(panel, placements) {
    var calculator = window.SwishCBPricing || (window.SwishCB && window.SwishCB.pricing);
    var options = getArtworkPricingOptions(panel);
    if (calculator && typeof calculator.calculateArtwork === 'function') {
      return calculator.calculateArtwork(Object.assign({ artworkPlacements: placements }, options), isUsOrderMarket(panel) ? 'usa' : 'international');
    }
    var totalAreaM2 = placements.reduce(function (total, placement) { return total + (Number(placement.areaM2) || 0); }, 0);
    var includedAreaM2 = placements.length ? Math.min(Number(placements[0].areaM2) || 0, options.artworkFreeAreaM2) : 0;
    return {
      placementCount: placements.length,
      totalAreaM2: totalAreaM2,
      totalAreaFt2: totalAreaM2 * SQUARE_FEET_PER_SQUARE_METRE,
      includedAreaM2: includedAreaM2,
      includedAreaFt2: includedAreaM2 * SQUARE_FEET_PER_SQUARE_METRE,
      chargeableAreaM2: Math.max(0, totalAreaM2 - includedAreaM2),
      chargeableAreaFt2: Math.max(0, totalAreaM2 - includedAreaM2) * SQUARE_FEET_PER_SQUARE_METRE,
      freeAreaLimitM2: options.artworkFreeAreaM2,
      ratePerM2: options.artworkRatePerM2
    };
  }

  function resolvePlayableDimensions(root, fallbackDimensions, payload) {
    var liveConfig = readConfig(root);
    var config = liveConfig && typeof liveConfig === 'object'
      ? liveConfig
      : (payload && payload.config && typeof payload.config === 'object' ? payload.config : null);
    var playable = config && config.playableBox && typeof config.playableBox === 'object'
      ? config.playableBox
      : null;
    var playableWidth = playable && positiveNumber(playable.width_m != null ? playable.width_m : playable.width);
    var playableLength = playable && positiveNumber(playable.length_m != null ? playable.length_m : playable.length);
    if (playableWidth && playableLength) return { widthM: playableWidth, lengthM: playableLength };
    var widthM = fallbackDimensions && positiveNumber(fallbackDimensions.widthM);
    var lengthM = fallbackDimensions && positiveNumber(fallbackDimensions.lengthM);
    if (!widthM) widthM = positiveNumber(payload && payload.widthM);
    if (!lengthM) lengthM = positiveNumber(payload && payload.lengthM);
    return widthM && lengthM ? { widthM: widthM, lengthM: lengthM } : null;
  }

  function resolveInstalledDimensions(root, fallbackDimensions, payload) {
    var displayedWidth = fallbackDimensions && positiveNumber(fallbackDimensions.widthM);
    var displayedLength = fallbackDimensions && positiveNumber(fallbackDimensions.lengthM);
    if (displayedWidth && displayedLength) return { widthM: displayedWidth, lengthM: displayedLength };

    var liveConfig = readConfig(root);
    var config = liveConfig && typeof liveConfig === 'object'
      ? liveConfig
      : (payload && payload.config && typeof payload.config === 'object' ? payload.config : null);
    var installed = config && config.installedSurfaceM && typeof config.installedSurfaceM === 'object'
      ? config.installedSurfaceM
      : (payload && payload.installedSurfaceM && typeof payload.installedSurfaceM === 'object'
        ? payload.installedSurfaceM
        : null);
    var installedWidth = installed && positiveNumber(installed.width_m != null ? installed.width_m : installed.width);
    var installedLength = installed && positiveNumber(installed.length_m != null ? installed.length_m : installed.length);
    if (installedWidth && installedLength) return { widthM: installedWidth, lengthM: installedLength };

    var footprintWidth = displayedWidth;
    var footprintLength = displayedLength;
    if (!footprintWidth) footprintWidth = positiveNumber(payload && payload.widthM) || positiveNumber(config && config.width);
    if (!footprintLength) footprintLength = positiveNumber(payload && payload.lengthM) || positiveNumber(config && config.length);
    if (!footprintWidth || !footprintLength) return null;

    var edgeSizeM = positiveNumber(payload && payload.tileEdgeSizeM);
    var edgePrefs = payload && payload.tileEdges && typeof payload.tileEdges === 'object'
      ? payload.tileEdges
      : null;
    if (edgeSizeM && edgePrefs) {
      var edgeEnabled = function (side) {
        return edgePrefs[side + 'On'] === true || edgePrefs[side] === true;
      };
      footprintLength += (edgeEnabled('left') ? edgeSizeM : 0) + (edgeEnabled('right') ? edgeSizeM : 0);
      footprintWidth += (edgeEnabled('top') ? edgeSizeM : 0) + (edgeEnabled('bottom') ? edgeSizeM : 0);
    }
    return { widthM: footprintWidth, lengthM: footprintLength };
  }

  function getOrderSeriesId(value) {
    var id = String(value || '').trim().toLowerCase();
    return ORDER_SERIES_IDS.indexOf(id) >= 0 ? id : '';
  }

  function normalizeOrderSeries(value) {
    return getOrderSeriesId(value) || 'elastic';
  }

  function readSeriesCatalog(panel) {
    var script = panel && panel.querySelector('[data-swishcb-order-series-catalog]');
    if (!script) return {};
    try {
      var parsed = JSON.parse(String(script.textContent || '{}'));
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_) {
      return {};
    }
  }

  function hasPickleballLinework(result) {
    result = result && typeof result === 'object' ? result : {};
    var payload = result.payload && typeof result.payload === 'object' ? result.payload : {};
    var mainSport = normalizeSport(payload.courtType || result.sport || payload.ruleset);
    if (mainSport === 'pickleball') return true;
    var extras = payload.extraSports && typeof payload.extraSports === 'object'
      ? payload.extraSports
      : (payload.extras && typeof payload.extras === 'object' ? payload.extras : {});
    return Object.keys(extras).some(function (id) {
      var entry = extras[id];
      if (!(entry === true || entry && entry.enabled)) return false;
      var definition = getExtraDefinition(id);
      var baseId = normalizeSport(
        definition && (definition.instanceOf || definition.id)
          || entry && (entry.instanceOf || entry.sport || entry.type)
          || id
      ).replace(/-\d+$/, '');
      return baseId === 'pickleball';
    });
  }

  function isMainPickleballCourt(result) {
    result = result && typeof result === 'object' ? result : {};
    var payload = result.payload && typeof result.payload === 'object' ? result.payload : {};
    return normalizeSport(payload.courtType || result.sport || payload.ruleset) === 'pickleball';
  }

  function setPickleballSeriesAvailability(select, restricted) {
    if (!select || !select.options) return false;
    Array.prototype.forEach.call(select.options, function (option) {
      var id = String(option.value || '').trim().toLowerCase();
      if (id !== 'elite' && id !== 'large-elite') return;
      option.disabled = !!restricted;
      option.hidden = !!restricted;
      option.setAttribute('aria-hidden', restricted ? 'true' : 'false');
    });
    var current = String(select.value || '').trim().toLowerCase();
    if (restricted && (current === 'elite' || current === 'large-elite')) {
      select.value = 'elastic';
      return true;
    }
    return false;
  }

  function applyPickleballSeriesRestrictions(root, panel, result) {
    var restricted = hasPickleballLinework(result);
    var mainPickleball = isMainPickleballCourt(result);
    var builderSelect = root && root.querySelector('#swishcb-tile-series');
    var orderSelect = panel && panel.querySelector('[data-swishcb-order-series-select]');
    var orderSelectorRow = panel && panel.querySelector('[data-swishcb-order-series-selector-row]');
    var builderChanged = setPickleballSeriesAvailability(builderSelect, restricted);
    setPickleballSeriesAvailability(orderSelect, restricted);
    // Keep the Order Now tile selector available for every court type. Pickleball
    // compatibility is still enforced above by disabling unsupported series.
    if (orderSelectorRow) {
      orderSelectorRow.hidden = false;
      orderSelectorRow.setAttribute('aria-hidden', 'false');
    }
    if (root) root.dataset.swishcbPickleballSeriesRestricted = restricted ? 'true' : 'false';
    if (root) root.dataset.swishcbMainPickleballCourt = mainPickleball ? 'true' : 'false';
    if (panel) {
      panel.dataset.swishcbPickleballSeriesRestricted = restricted ? 'true' : 'false';
      panel.dataset.swishcbMainPickleballCourt = mainPickleball ? 'true' : 'false';
    }
    if (result) result.pickleballSeriesRestricted = restricted;
    if (result) result.mainPickleballCourt = mainPickleball;
    if (builderChanged && builderSelect) {
      builderSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
    return restricted;
  }

  function syncOrderSeries(root, panel, result) {
    var orderSelect = panel && panel.querySelector('[data-swishcb-order-series-select]');
    var builderSelect = root.querySelector('#swishcb-tile-series');
    applyPickleballSeriesRestrictions(root, panel, result);
    var builderValue = builderSelect ? String(builderSelect.value || '').toLowerCase() : '';
    var isPreviewOrderCard = root.classList.contains('swishcb--preview-order-card');
    var selected = builderValue === 'none' && !isPreviewOrderCard
      ? 'none'
      : (
          getOrderSeriesId(builderValue) ||
          getOrderSeriesId(orderSelect && orderSelect.value) ||
          (isPreviewOrderCard ? 'elastic' : 'none')
        );
    if (orderSelect && orderSelect.value !== selected) orderSelect.value = selected;
    return selected;
  }

  function getDefaultOrderSeries() {
    return 'elastic';
  }

  function capturePreOrderDimensions(root) {
    var dimensions = readDimensions(root);
    if (!dimensions) return null;
    var widthM = positiveNumber(dimensions.widthM);
    var lengthM = positiveNumber(dimensions.lengthM);
    return widthM && lengthM ? { widthM: widthM, lengthM: lengthM } : null;
  }

  function applyBuilderSeries(root, panel, seriesId) {
    var selected = getOrderSeriesId(seriesId) || 'none';
    var orderSelect = panel && panel.querySelector('[data-swishcb-order-series-select]');
    var builderSelect = root && root.querySelector('#swishcb-tile-series');
    if (orderSelect && orderSelect.value !== selected) orderSelect.value = selected;
    if (builderSelect && builderSelect.value !== selected) {
      builderSelect.value = selected;
      builderSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
    updateSeriesPresentation(panel, selected, selected !== 'none');
    return selected;
  }

  function setOrderPanelActive(root, panel, active) {
    if (!root || !panel) return;
    var state = getState(root);
    if (state.orderPanelTransition) return;
    state.orderPanelTransition = true;
    try {
      if (active) {
        state.orderPanelSession = {
          dimensions: capturePreOrderDimensions(root)
        };
        root.dataset.swishcbOrderPanelActive = 'true';
        panel.dataset.swishcbOrderActive = 'true';
        var builderSelect = root.querySelector('#swishcb-tile-series');
        var currentSeries = getOrderSeriesId(builderSelect && builderSelect.value);
        var currentOption = currentSeries && builderSelect
          ? builderSelect.querySelector('option[value="' + currentSeries + '"]')
          : null;
        var orderSeries = currentOption && !currentOption.disabled && !currentOption.hidden
          ? currentSeries
          : getDefaultOrderSeries();
        applyBuilderSeries(root, panel, orderSeries);
      } else {
        var session = state.orderPanelSession;
        delete root.dataset.swishcbOrderPanelActive;
        delete panel.dataset.swishcbOrderActive;
        applyBuilderSeries(root, panel, 'none');
        var dimensions = session && session.dimensions;
        var builderApi = window.SwishCourtBuilder;
        if (
          dimensions &&
          builderApi &&
          typeof builderApi.setContactDimensions === 'function'
        ) {
          builderApi.setContactDimensions({
            width: dimensions.widthM,
            length: dimensions.lengthM,
            units: 'm'
          });
        }
        state.orderPanelSession = null;
        if (state.designTimer) window.clearTimeout(state.designTimer);
        state.designTimer = 0;
        state.designScheduledSignature = '';
        state.designToken += 1;
      }
    } finally {
      state.orderPanelTransition = false;
    }
    scheduleRefresh(root);
    scheduleSettledRefresh(root);
  }

  function updateSeriesPresentation(panel, seriesId, showVideos) {
    if (!panel) return null;
    var id = getOrderSeriesId(seriesId);
    var productSummaryPanel = panel.closest('.swishcb--summary-advanced');
    var productSummaryPanelTitle = productSummaryPanel
      ? panel.querySelector('[data-swishcb-order-panel-title]')
      : null;
    var select = panel.querySelector('[data-swishcb-order-series-select]');
    var heading = panel.querySelector('[data-swishcb-order-series]');
    var description = panel.querySelector('[data-swishcb-order-series-description]');
    var image = panel.querySelector('[data-swishcb-order-series-image]');
    var link = panel.querySelector('[data-swishcb-order-series-link]');
    var thumbnails = panel.querySelector('[data-swishcb-order-series-thumbnails]');
    var videos = panel.querySelector('[data-swishcb-order-series-videos]');
    var projectReferences = panel.querySelector('[data-swishcb-order-project-references]');
    var projectReferencesTitle = panel.querySelector('[data-swishcb-order-project-references-title]');
    if (!id) {
      setText(productSummaryPanelTitle, productSummaryPanel && productSummaryPanel.dataset.swishcbConfigurator === 'cards' ? 'Court Surface' : 'Tiles');
      closeOrderMediaZoom(panel);
      closeOrderVideoPopup(panel.closest('#swish-court-builder'));
      if (select && select.value !== 'none') select.value = 'none';
      setText(heading, '');
      setText(description, '');
      setText(projectReferencesTitle, '');
      if (heading) heading.hidden = true;
      if (description) description.hidden = true;
      if (link) link.hidden = true;
      if (thumbnails) {
        thumbnails.replaceChildren();
        thumbnails.hidden = true;
        delete thumbnails.dataset.swishcbGallerySignature;
      }
      if (videos) {
        videos.querySelectorAll('video').forEach(function (video) {
          try { video.pause(); } catch (_) {}
        });
        videos.replaceChildren();
        videos.hidden = true;
        delete videos.dataset.swishcbSeriesVideos;
      }
      if (projectReferences) projectReferences.hidden = true;
      return null;
    }
    var catalog = readSeriesCatalog(panel);
    var item = catalog[id] || {};
    var label = String(item.label || SERIES_LABELS[id] || humanizeId(id));
    setText(productSummaryPanelTitle, productSummaryPanel && productSummaryPanel.dataset.swishcbConfigurator === 'cards' ? 'Court Surface' : label);
    var images = Array.isArray(item.images) ? item.images.filter(Boolean) : [];
    if (!images.length && item.image) images.push(item.image);
    var gallerySignature = JSON.stringify({ id: id, images: images });
    var galleryChanged = !thumbnails || thumbnails.dataset.swishcbGallerySignature !== gallerySignature;
    if (galleryChanged) closeOrderMediaZoom(panel);
    if (select && select.value !== id) select.value = id;
    setText(heading, label);
    setText(description, item.description || '');
    setText(projectReferencesTitle, label);
    if (heading) heading.hidden = false;
    if (description) description.hidden = false;
    if (image) {
      var currentImage = image.getAttribute('src') || '';
      var activeImage = !galleryChanged && images.indexOf(currentImage) >= 0
        ? currentImage
        : (images[0] || item.image || '');
      if (activeImage && image.getAttribute('src') !== activeImage) image.setAttribute('src', activeImage);
      image.setAttribute('alt', label);
    }
    if (link) {
      link.hidden = false;
      if (item.productUrl && link.tagName === 'A') link.setAttribute('href', item.productUrl);
      link.setAttribute('aria-label', 'Zoom ' + label + ' image');
    }
    if (thumbnails) {
      thumbnails.setAttribute('aria-label', label + ' images');
      if (galleryChanged) {
        var fragment = document.createDocumentFragment();
        images.forEach(function (source, index) {
          var button = document.createElement('button');
          button.type = 'button';
          button.className = 'swishcb__order-series-thumbnail';
          button.dataset.swishcbOrderSeriesThumbnail = source;
          button.dataset.swishcbOrderSeriesThumbnailIndex = String(index);
          button.setAttribute('aria-label', 'Show ' + label + ' image ' + (index + 1));
          button.setAttribute('aria-current', index === 0 ? 'true' : 'false');
          var thumbnailImage = document.createElement('img');
          thumbnailImage.src = source;
          thumbnailImage.alt = '';
          thumbnailImage.loading = 'lazy';
          thumbnailImage.width = 180;
          thumbnailImage.height = 180;
          button.appendChild(thumbnailImage);
          button.addEventListener('click', function (event) {
            event.preventDefault();
            event.stopPropagation();
            selectGalleryImage(panel, button);
          });
          fragment.appendChild(button);
        });
        thumbnails.replaceChildren(fragment);
        thumbnails.dataset.swishcbGallerySignature = gallerySignature;
      }
      thumbnails.hidden = !images.length;
    }
    if (videos && showVideos === false) {
      closeOrderVideoPopup(panel.closest('#swish-court-builder'));
      videos.querySelectorAll('video').forEach(function (video) {
        try { video.pause(); } catch (_) {}
      });
      videos.replaceChildren();
      delete videos.dataset.swishcbSeriesVideos;
      videos.hidden = true;
      if (projectReferences) projectReferences.hidden = true;
    } else if (videos && videos.dataset.swishcbSeriesVideos !== id) {
      closeOrderVideoPopup(panel.closest('#swish-court-builder'));
      videos.querySelectorAll('video').forEach(function (video) {
        try { video.pause(); } catch (_) {}
      });
      var videoTemplate = panel.querySelector('[data-swishcb-order-series-videos-template="' + id + '"]');
      if (videoTemplate && videoTemplate.content) {
        // Import into the live document so video poster URLs resolve outside the inert template.
        videos.replaceChildren(document.importNode(videoTemplate.content, true));
      } else {
        videos.replaceChildren();
      }
      videos.querySelectorAll('video').forEach(function (video) {
        video.addEventListener('play', function () {
          videos.querySelectorAll('video').forEach(function (candidate) {
            if (candidate === video) return;
            try { candidate.pause(); } catch (_) {}
          });
        });
      });
      videos.dataset.swishcbSeriesVideos = id;
      videos.hidden = !videos.children.length;
      if (projectReferences) projectReferences.hidden = !videos.children.length;
    } else if (projectReferences && videos) {
      projectReferences.hidden = videos.hidden || !videos.children.length;
    }
    return {
      id: id,
      label: label,
      description: String(item.description || ''),
      image: String(images[0] || item.image || ''),
      images: images.slice(),
      productUrl: String(item.productUrl || '')
    };
  }

  function formatDecimal(value, places) {
    var number = toNumber(value);
    if (number == null) return '';
    return String(round(number, places == null ? 2 : places));
  }

  function formatFeetAndInches(meters) {
    var numericMeters = Number(meters);
    var sign = numericMeters < 0 ? '-' : '';
    var totalInches = Math.round((Math.abs(numericMeters) / 0.0254) + Number.EPSILON);
    var feet = Math.floor(totalInches / 12);
    var inches = totalInches % 12;
    return sign + feet + ' ft' + (inches ? ' ' + inches + ' in' : '');
  }

  function formatLength(meters, units) {
    var value = toNumber(meters);
    if (value == null) return '';
    return units === 'ft'
      ? formatFeetAndInches(value)
      : formatDecimal(value, 2) + ' m';
  }

  function formatOrderDimension(meters, units, useTilePrecision) {
    var value = toNumber(meters);
    if (value == null) return '';
    if (units === 'ft') return formatFeetAndInches(value);
    return formatDecimal(value, useTilePrecision ? 3 : 2) + ' m';
  }

  function formatHoopModelLabel(label, units) {
    var text = String(label || '').trim();
    if (!text || /^custom$/i.test(text)) return text;
    var match = text.match(/^(.*?)\s+[—-]\s+([0-9]+(?:[.,][0-9]+)?)\s*(cm|m|ft)$/i);
    if (!match) return text;
    var value = toNumber(match[2]);
    if (value == null) return text;
    var sourceUnit = String(match[3]).toLowerCase();
    var meters = sourceUnit === 'cm' ? value / 100 : (sourceUnit === 'ft' ? value * 0.3048 : value);
    return String(match[1]).trim() + ' — ' + formatLength(meters, units);
  }

  function titleCaseLineLabel(value) {
    var label = String(value || '').trim();
    if (!label) return '';
    label = label.charAt(0).toUpperCase() + label.slice(1);
    return label
      .replace(/3-point/gi, '3-Point')
      .replace(/\blines\b/gi, 'Lines')
      .replace(/\bline\b/gi, 'Line');
  }

  function humanizeId(value) {
    return String(value || '')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, function (character) { return character.toUpperCase(); })
      .trim();
  }

  function getCourtLabel(sport, payload) {
    var config = window.SwishCBConfig || {};
    var courtType = normalizeSport((payload && payload.courtType) || sport);
    var courtTypes = Array.isArray(config.COURT_TYPES) ? config.COURT_TYPES : [];
    var match = courtTypes.find(function (item) {
      return item && normalizeSport(item.id) === courtType;
    });
    if (match && match.label) return String(match.label);
    var labels = {
      fiba: 'FIBA',
      nba: 'NBA',
      ncaa: 'NCAA',
      usahs: 'USA High School',
      'us-highschool': 'USA High School',
      tennis: 'Tennis',
      pickleball: 'Pickleball',
      padel: 'Padel',
      volleyball: 'Volleyball',
      shuffleboard: 'Shuffleboard',
      futsal: 'Futsal',
      netball: 'Netball',
      racquetball: 'Racquetball'
    };
    return labels[courtType] || humanizeId(courtType || (payload && payload.ruleset) || 'Custom');
  }

  function updateOrderPanelCopy(panel, result) {
    if (!panel) return;
    var payload = result && result.payload && typeof result.payload === 'object' ? result.payload : {};
    var sport = normalizeSport(payload.courtType || (result && result.sport) || payload.ruleset);
    var description = 'Confirm the design and order your court today.';
    // Official builder pages keep their established Order Now copy. Product
    // summary pages use the merchant-configured disclosure title (Review in
    // the v2 template), so a live refresh must not overwrite it.
    if (!panel.closest('.swishcb--summary-advanced')) {
      setText(panel.querySelector('[data-swishcb-order-panel-title]'), 'Order Now');
    }
    setText(panel.querySelector('[data-swishcb-order-panel-description]'), description);
    panel.dataset.swishcbOrderCopySport = sport || 'custom';
  }

  function getExtraDefinition(extraId) {
    var config = window.SwishCBConfig || {};
    try {
      if (typeof config.getExtraLineTypeDefinition === 'function') {
        var definition = config.getExtraLineTypeDefinition(extraId);
        if (definition) return definition;
      }
    } catch (_) {}
    var definitions = Array.isArray(config.EXTRA_LINE_TYPES) ? config.EXTRA_LINE_TYPES : [];
    return definitions.find(function (item) {
      return item && String(item.id || '').toLowerCase() === String(extraId || '').toLowerCase();
    }) || null;
  }

  function getEnabledExtraSports(payload) {
    var entries = payload && payload.extraSports && typeof payload.extraSports === 'object'
      ? payload.extraSports
      : {};
    var order = Array.isArray(payload && payload.extraSportLayerOrder)
      ? payload.extraSportLayerOrder.slice()
      : [];
    Object.keys(entries).forEach(function (id) {
      if (order.indexOf(id) < 0) order.push(id);
    });
    return order.reduce(function (items, id) {
      var entry = entries[id];
      if (!entry || !entry.enabled) return items;
      var definition = getExtraDefinition(id);
      items.push({
        id: id,
        label: titleCaseLineLabel(definition && definition.label ? definition.label : humanizeId(id) + ' Lines'),
        color: normalizeHex(entry.color || (definition && definition.defaultColor) || '')
      });
      return items;
    }, []);
  }

  function joinLabels(labels) {
    if (!labels.length) return '';
    if (labels.length === 1) return labels[0];
    if (labels.length === 2) return labels[0] + ' and ' + labels[1];
    return labels.slice(0, -1).join(', ') + ', and ' + labels[labels.length - 1];
  }

  function getSeriesLabel(root, payload) {
    if (payload && payload.hardwoodEnabled) {
      return SERIES_LABELS[String(payload.hardwoodVariant || 'hardwood').toLowerCase()] || 'Hardwood Flooring';
    }
    if (payload && payload.tilesEnabled) {
      return SERIES_LABELS[String(payload.tileSeries || '').toLowerCase()] || humanizeId(payload.tileSeries) || 'Sport Tiles';
    }
    var select = root.querySelector('#swishcb-tile-series');
    if (!select || String(select.value || '').toLowerCase() === 'none') return '';
    return getSelectedOptionText(root, '#swishcb-tile-series');
  }

  function normalizeHex(value) {
    var text = String(value || '').trim().toUpperCase();
    if (!text) return '';
    if (text.charAt(0) !== '#') text = '#' + text;
    return /^#[0-9A-F]{3}(?:[0-9A-F]{3})?$/.test(text) ? text : '';
  }

  function getActiveHoopSides(root, payload) {
    var selection = payload && payload.hoopSelection && typeof payload.hoopSelection === 'object'
      ? payload.hoopSelection
      : null;
    return ['left', 'right', 'top', 'bottom'].filter(function (side) {
      if (selection) return !!selection[side + 'On'];
      var checkbox = root.querySelector('#swishcb-' + side + '-hoop');
      return !!(checkbox && checkbox.checked);
    });
  }

  function readLengthInputMeters(root, selector, units) {
    var input = root.querySelector(selector);
    var value = input ? positiveNumber(input.value) : null;
    if (!value) return null;
    return units === 'ft' ? value * 0.3048 : value;
  }

  function getKeyDimensionsMeters(root, payload, units) {
    var dimensions = payload && payload.keyDimensionsM && typeof payload.keyDimensionsM === 'object'
      ? payload.keyDimensionsM
      : null;
    var widthInput = root.querySelector('#swishcb-key-width');
    var lengthInput = root.querySelector('#swishcb-key-length');
    var widthM = dimensions && positiveNumber(dimensions.width);
    var lengthM = dimensions && positiveNumber(dimensions.length);
    var customWidthM = dimensions && positiveNumber(dimensions.customWidth);
    var customLengthM = dimensions && positiveNumber(dimensions.customLength);
    var officialWidthM = dimensions && positiveNumber(dimensions.officialWidth);
    var officialLengthM = dimensions && positiveNumber(dimensions.officialLength);
    if (!widthM) widthM = readLengthInputMeters(root, '#swishcb-key-width', units);
    if (!lengthM) lengthM = readLengthInputMeters(root, '#swishcb-key-length', units);
    return widthM && lengthM ? {
      widthM: widthM,
      lengthM: lengthM,
      customWidthM: customWidthM,
      customLengthM: customLengthM,
      officialWidthM: officialWidthM,
      officialLengthM: officialLengthM,
      widthAdjusted: typeof dimensions.isOfficialWidth === 'boolean'
        ? !dimensions.isOfficialWidth
        : !!(officialWidthM && Math.abs(widthM - officialWidthM) > 0.000001),
      lengthAdjusted: typeof dimensions.isOfficialLength === 'boolean'
        ? !dimensions.isOfficialLength
        : !!(officialLengthM && Math.abs(lengthM - officialLengthM) > 0.000001)
    } : null;
  }

  function getThreePointDistanceDetails(root, payload, units) {
    var distance = payload && payload.threePointDistance && typeof payload.threePointDistance === 'object'
      ? payload.threePointDistance
      : null;
    var input = root.querySelector('#swishcb-three-point-distance');
    var distanceM = distance && positiveNumber(distance.displayedM);
    var customM = distance && positiveNumber(distance.customM);
    var officialM = distance && positiveNumber(distance.officialM);
    if (!distanceM) distanceM = positiveNumber(payload && payload.threePointDistanceM);
    if (!distanceM) distanceM = readLengthInputMeters(root, '#swishcb-three-point-distance', units);
    return distanceM ? {
      valueM: distanceM,
      customM: customM,
      officialM: officialM,
      adjusted: officialM != null
        ? Math.abs(distanceM - officialM) > 0.000001
        : (customM != null || !!(input && input.dataset.swishcbThreePointDistanceCustom === 'true'))
    } : null;
  }

  function isThreePointColorControlAvailable(row) {
    if (!row) return false;
    if (row.hasAttribute('hidden') || row.getAttribute('aria-hidden') === 'true' || row.style && row.style.display === 'none') {
      return false;
    }
    // Advanced color rows can remain individually enabled while their complete
    // detail group is unavailable for the current court footprint. Treat that
    // group state as authoritative without relying on whether the outer Colors
    // accordion happens to be open on screen.
    var group = row.closest && row.closest('[data-detail-group]');
    if (group && (
      group.hasAttribute('hidden') ||
      group.getAttribute('aria-hidden') === 'true' ||
      group.style && group.style.display === 'none'
    )) return false;
    return true;
  }

  function hasThreePointGeometryRoom(root, payload, distanceM) {
    var radius = positiveNumber(distanceM);
    var widthM = positiveNumber(payload && payload.widthM);
    var lengthM = positiveNumber(payload && payload.lengthM);
    if (!radius || !widthM || !lengthM) return true;
    var hoopSides = getActiveHoopSides(root, payload);
    if (!hoopSides.length) return false;
    var overhangs = payload && payload.hoopOverhangsM && typeof payload.hoopOverhangsM === 'object'
      ? payload.hoopOverhangsM
      : {};
    return hoopSides.some(function (side) {
      var horizontal = side === 'left' || side === 'right';
      var axisLengthM = horizontal ? lengthM : widthM;
      var crossWidthM = horizontal ? widthM : lengthM;
      var overhangM = positiveNumber(overhangs[side]);
      if (!overhangM) {
        var input = root && root.querySelector ? root.querySelector('#swishcb-' + side + '-hoop-distance') : null;
        var inputCm = input ? positiveNumber(input.value) : null;
        if (inputCm) overhangM = inputCm / 100;
      }
      if (!overhangM) overhangM = 1.2;
      var longitudinalReachM = Math.max(0, axisLengthM - overhangM);
      var lateralReachM = Math.max(0, crossWidthM / 2);
      return Math.hypot(longitudinalReachM, lateralReachM) + 0.001 >= radius;
    });
  }

  function hasVisibleThreePointLine(root, payload, distanceM) {
    if (!hasThreePointGeometryRoom(root, payload, distanceM)) return false;
    var published = String(root && root.dataset ? root.dataset.swishcbThreePointLineVisible || '' : '').toLowerCase();
    if (published === 'true' || published === 'false') return published === 'true';
    if (payload && typeof payload.threePointLineVisible === 'boolean') {
      return payload.threePointLineVisible;
    }
    var rows = THREE_POINT_DETAIL_IDS.map(function (id) {
      return root.querySelector('[data-detail-row="' + id + '"]');
    }).filter(Boolean);
    if (!rows.length) return false;
    return rows.some(isThreePointColorControlAvailable);
  }

  function getVisibleBorders(root, payload) {
    var liveConfig = readConfig(root);
    var config = liveConfig && typeof liveConfig === 'object'
      ? liveConfig
      : (payload && payload.config && typeof payload.config === 'object' ? payload.config : {});
    var fallbackSizes = config.buffers_m && typeof config.buffers_m === 'object' ? config.buffers_m : {};
    var selection = payload && payload.borderSelection && typeof payload.borderSelection === 'object'
      ? payload.borderSelection
      : null;
    var sizes = payload && payload.borderSizesM && typeof payload.borderSizesM === 'object'
      ? payload.borderSizesM
      : fallbackSizes;
    return ['top', 'bottom', 'left', 'right'].reduce(function (visible, side) {
      var size = toNumber(sizes[side]);
      if (!(size > 0)) {
        var input = root && root.querySelector ? root.querySelector('[data-border-size="' + side + '"]') : null;
        var inputValue = input ? toNumber(input.value) : null;
        var inputUnitHost = input && input.closest ? input.closest('[data-unit]') : null;
        var inputUnit = String(inputUnitHost && inputUnitHost.dataset && inputUnitHost.dataset.unit || 'm').toLowerCase();
        if (inputValue != null) {
          if (inputUnit === 'cm') size = inputValue / 100;
          else if (inputUnit === 'ft') size = inputValue * 0.3048;
          else if (inputUnit === 'in') size = inputValue * 0.0254;
          else size = inputValue;
        }
      }
      var selectedValue = selection ? selection[side + 'On'] : null;
      var enabled = selection
        ? selectedValue === true || selectedValue === 1 || String(selectedValue).toLowerCase() === 'true' || String(selectedValue) === '1'
        : size != null && size > 0;
      if (enabled && size != null && size > 0) visible[side] = size;
      return visible;
    }, {});
  }

  function getActiveTileEdgeSides(root, payload) {
    var selection = payload && payload.tileEdges && typeof payload.tileEdges === 'object'
      ? payload.tileEdges
      : null;
    return ['left', 'right', 'bottom', 'top'].filter(function (side) {
      if (selection) return !!selection[side + 'On'];
      var checkbox = root.querySelector('#swishcb-tile-edge-' + side);
      return !!(checkbox && checkbox.checked);
    });
  }

  function isUsOrderMarket(panel) {
    return String(panel && panel.dataset.swishcbOrderCountryCode || '').trim().toUpperCase() === 'US';
  }

  function getOrderDisplayUnits(root, panel) {
    var unitInput = root && root.querySelector('input[name="swishcb-units"]:checked');
    var selectedUnits = String(unitInput && unitInput.value || '').trim().toLowerCase();
    if (selectedUnits === 'm' || selectedUnits === 'ft') return selectedUnits;
    return isUsOrderMarket(panel) ? 'ft' : 'm';
  }

  function formatMillimeters(value, useUsUnits) {
    var millimeters = positiveNumber(value);
    if (!millimeters) return '';
    if (useUsUnits) return formatDecimal(millimeters / 25.4, 2) + ' in';
    return String(round(millimeters, Number.isInteger(millimeters) ? 0 : 1)) + ' mm';
  }

  function formatTileSize(sizeMm, useUsUnits) {
    if (!Array.isArray(sizeMm) || !sizeMm.length) return '';
    if (useUsUnits) {
      return sizeMm.map(function (millimeters) {
        return formatDecimal(Number(millimeters) / 25.4, 2);
      }).join(' × ') + ' in';
    }
    return sizeMm.join(' × ') + ' mm';
  }

  function formatTileWeight(tileSpec, useUsUnits) {
    var weightG = positiveNumber(tileSpec && tileSpec.weightG);
    var toleranceG = positiveNumber(tileSpec && tileSpec.weightToleranceG);
    if (!weightG) return '';
    if (useUsUnits) {
      var gramsPerPound = 453.59237;
      var pounds = formatDecimal(weightG / gramsPerPound, 2);
      var tolerancePounds = toleranceG ? formatDecimal(toleranceG / gramsPerPound, 2) : '';
      return pounds + (tolerancePounds ? ' \u00B1 ' + tolerancePounds : '') + ' lb';
    }
    return formatDecimal(weightG, 1) + (toleranceG ? ' \u00B1 ' + formatDecimal(toleranceG, 1) : '') + ' g';
  }

  function formatSignedTemperature(value, suffix) {
    var number = Number(value);
    if (!Number.isFinite(number)) return '';
    return (number > 0 ? '+' : '') + formatDecimal(number, 1) + suffix;
  }

  function formatTileTemperature(tileSpec, useUsUnits) {
    var minimumC = Number(tileSpec && tileSpec.temperatureMinC);
    var maximumC = Number(tileSpec && tileSpec.temperatureMaxC);
    if (!Number.isFinite(minimumC) || !Number.isFinite(maximumC)) return '';
    if (useUsUnits) {
      return formatSignedTemperature((minimumC * 9 / 5) + 32, '\u00B0F') +
        ' to ' + formatSignedTemperature((maximumC * 9 / 5) + 32, '\u00B0F');
    }
    return formatSignedTemperature(minimumC, '\u00B0C') +
      ' to ' + formatSignedTemperature(maximumC, '\u00B0C');
  }

  function getDetailColorItems(payload, activeHoopSides) {
    var colors = payload && payload.detailColors && typeof payload.detailColors === 'object'
      ? payload.detailColors
      : {};
    var hidden = new Set(Array.isArray(payload && payload.hiddenDetails) ? payload.hiddenDetails : []);
    var visibility = payload && payload.detailVisibility && typeof payload.detailVisibility === 'object'
      ? payload.detailVisibility
      : {};
    var config = window.SwishCBConfig || {};
    var library = config.DETAIL_LIBRARY && config.DETAIL_LIBRARY[String(payload && payload.ruleset || '').toUpperCase()];
    var parts = library && Array.isArray(library.parts) ? library.parts : [];
    var groups = library && Array.isArray(library.groups) ? library.groups : [];
    return Object.keys(colors).reduce(function (items, id) {
      var hex = normalizeHex(colors[id]);
      if (!hex || hidden.has(id) || visibility[id] === false) return items;
      var fourPointSide = /^(left|right|top|bottom)FourPointLine$/.exec(id);
      if (fourPointSide && activeHoopSides.indexOf(fourPointSide[1]) === -1) return items;
      var part = parts.find(function (candidate) { return candidate && candidate.id === id; });
      var group = part && groups.find(function (candidate) { return candidate && candidate.id === part.group; });
      var label = part && part.label ? String(part.label) : humanizeId(id);
      if (group && group.label && !/^center$/i.test(group.label)) label = group.label + ' — ' + label;
      items.push({ label: label, hex: hex, targetKind: 'detail', targetId: id });
      return items;
    }, []);
  }

  function getBaseColorItems(payload, sport, activeHoopSides, options) {
    var colors = payload && payload.colors && typeof payload.colors === 'object' ? payload.colors : {};
    var normalizedSport = normalizeSport(sport);
    var activeLabels = SPORT_AREA_COLOR_LABELS[normalizedSport] || { main: 'Main court', key: 'Secondary court areas' };
    var labels = Object.assign({}, BASE_COLOR_LABELS, {
      main: activeLabels.main,
      key: activeLabels.key,
      border: options && Number(options.visibleBorderCount) === 1 ? 'Border' : 'Borders'
    });
    var keys = ['main', 'key'];
    if (!options || options.hasVisibleBorder !== false) keys.push('border');
    keys.push('lines');
    var includeEdges = options
      ? options.hasVisibleEdges === true
      : !!(payload && payload.tilesEnabled);
    if (includeEdges) keys.push('edge');
    return keys.reduce(function (items, key) {
      var hex = normalizeHex(colors[key]);
      if (hex && BASE_COLOR_INPUT_IDS[key]) {
        items.push({ label: labels[key], hex: hex, targetKind: 'base', targetId: key });
      }
      return items;
    }, []);
  }

  function colorItemText(item) {
    if (!item || typeof item !== 'object') return String(item || '');
    return String(item.label || '') + ': ' + String(item.hex || '');
  }

  function logoFileMatches(logo, file) {
    if (!logo || !file) return false;
    var expectedName = String(logo.fileName || '').trim();
    var actualName = String(file.name || '').trim();
    if (expectedName && actualName && expectedName !== actualName) return false;
    var expectedSize = Number(logo.fileSize) || 0;
    var actualSize = Number(file.size) || 0;
    if (expectedSize && actualSize && expectedSize !== actualSize) return false;
    var expectedModified = Number(logo.fileLastModified) || 0;
    var actualModified = Number(file.lastModified) || 0;
    if (expectedModified && actualModified && expectedModified !== actualModified) return false;
    return !!(expectedName || expectedSize || expectedModified);
  }

  function getLogoAttachment(payload, logo, index, usedFileIndexes) {
    logo = logo && typeof logo === 'object' ? logo : {};
    var files = Array.isArray(payload && payload.logoFiles) ? payload.logoFiles : [];
    var matchedIndex = -1;
    for (var fileIndex = 0; fileIndex < files.length; fileIndex += 1) {
      if (usedFileIndexes.indexOf(fileIndex) < 0 && logoFileMatches(logo, files[fileIndex])) {
        matchedIndex = fileIndex;
        break;
      }
    }
    if (matchedIndex < 0 && files.length === (payload.logos || []).length && files[index]) {
      matchedIndex = index;
    }
    if (matchedIndex >= 0) usedFileIndexes.push(matchedIndex);
    var file = matchedIndex >= 0 ? files[matchedIndex] : null;
    var downloadName = String(logo.fileName || file && file.name || ('court-logo-' + (index + 1))).trim();
    return {
      type: 'download',
      label: 'Attached file',
      downloadName: downloadName,
      file: file,
      url: String(logo.remoteSourceUrl || logo.remoteRenderUrl || '').trim(),
      fileSize: Number(logo.fileSize || file && file.size) || 0,
      fileLastModified: Number(logo.fileLastModified || file && file.lastModified) || 0
    };
  }

  function orderSpecificationValueText(value) {
    if (!value || typeof value !== 'object') return String(value || '');
    if (value.type === 'download') return String(value.label || 'Attached file');
    if (value.type === 'emphasized-label') return String(value.label || '') + ': ' + String(value.value || '');
    return colorItemText(value);
  }

  function checkoutSpecificationValueText(value) {
    if (!value || typeof value !== 'object') return String(value || '').trim();
    if (value.type === 'tile-series-selector') {
      var selectedSeries = (Array.isArray(value.options) ? value.options : []).find(function (option) {
        return String(option && option.value || '') === String(value.value || '');
      });
      var selectedSeriesLabel = String(selectedSeries && selectedSeries.label || SERIES_LABELS[value.value] || humanizeId(value.value));
      return selectedSeriesLabel ? 'Series: ' + selectedSeriesLabel : '';
    }
    if (value.type === 'add-artwork') {
      return '';
    }
    if (value.type === 'download') {
      return (String(value.prefix || '') + String(value.label || 'Attached file')).trim();
    }
    if (value.type === 'emphasized-label') {
      return (String(value.label || '') + ': ' + String(value.value || '')).trim();
    }
    return colorItemText(value).trim();
  }

  function checkoutDetailsFromSpecs(specs) {
    return (Array.isArray(specs) ? specs : []).map(function (spec) {
      var label = String(spec && spec.label || '').trim();
      function checkoutValue(value, index) {
        var dimension = Array.isArray(spec && spec.editableDimensions)
          ? spec.editableDimensions[index]
          : spec && spec.editableDimension;
        var overhang = Array.isArray(spec && spec.editableOverhangs)
          ? spec.editableOverhangs[index]
          : null;
        var measurement = dimension || overhang;
        var meters = measurement && toNumber(measurement.valueM);
        if (measurement && meters != null) {
          var units = measurement.unit === 'ft' ? 'ft' : 'm';
          var measurementLabel = measurement.label || (overhang
            ? (overhang.side ? humanizeId(overhang.side) + ' hoop overhang' : 'Hoop overhang')
            : label);
          // Match the editable View details fields, including their precision.
          return measurementLabel + ': ' + formatDecimal(units === 'ft' ? meters / 0.3048 : meters, 3) + ' ' + units;
        }
        return checkoutSpecificationValueText(value);
      }
      var values = Array.isArray(spec && spec.values)
        ? spec.values.map(checkoutValue)
        : [checkoutValue(spec && spec.value, 0)];
      var repeatedLabelPattern = label
        ? new RegExp('^' + label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*:\\s*', 'i')
        : null;
      return {
        label: label,
        values: values.map(function (value) {
          var normalized = String(value || '').trim();
          return repeatedLabelPattern ? normalized.replace(repeatedLabelPattern, '').trim() : normalized;
        }).filter(Boolean)
      };
    }).filter(function (detail) {
      return detail.label && detail.values.length;
    });
  }

  function checkoutPlainSpecifications(details) {
    return (Array.isArray(details) ? details : []).map(function (detail) {
      return String(detail.label || '') + ': ' + (Array.isArray(detail.values) ? detail.values.join('; ') : '');
    }).filter(Boolean).join(' | ');
  }

  function formatArtworkArea(areaM2, useUsUnits) {
    var normalizedAreaM2 = Math.max(0, Number(areaM2) || 0);
    var value = useUsUnits ? normalizedAreaM2 * SQUARE_FEET_PER_SQUARE_METRE : normalizedAreaM2;
    return formatNumber(value, 2) + ' ' + (useUsUnits ? 'ft\u00B2' : 'm\u00B2');
  }

  function buildOrderSummary(root, panel, result, seriesId) {
    var payload = getBuilderPayload(root);
    var units = getOrderDisplayUnits(root, panel);
    var useUsUnits = units === 'ft';
    var liveConfig = readConfig(root);
    var config = liveConfig && typeof liveConfig === 'object'
      ? liveConfig
      : (payload && payload.config && typeof payload.config === 'object' ? payload.config : {});
    var playableSurface = config.playableBox && typeof config.playableBox === 'object' ? config.playableBox : {};
    var installedSurface = payload && payload.installedSurfaceM && typeof payload.installedSurfaceM === 'object'
      ? payload.installedSurfaceM
      : (config.installedSurfaceM && typeof config.installedSurfaceM === 'object' ? config.installedSurfaceM : {});
    var totalWidthM = positiveNumber(result.installedWidthM) || positiveNumber(installedSurface.width_m) || positiveNumber(installedSurface.width) || positiveNumber(result.widthM) || positiveNumber(payload.widthM);
    var totalLengthM = positiveNumber(result.installedLengthM) || positiveNumber(installedSurface.length_m) || positiveNumber(installedSurface.length) || positiveNumber(result.lengthM) || positiveNumber(payload.lengthM);
    var selectedTileSeries = getOrderSeriesId(seriesId || payload.tileSeries);
    var usesPhysicalTileDimensions = !!(
      selectedTileSeries &&
      payload.tilesEnabled !== false &&
      positiveNumber(result.footprintWidthM) &&
      positiveNumber(result.footprintLengthM)
    );
    // For modular courts, "playing surface" means the actual whole-tile
    // footprint shown in the design. The regulation line area remains in the
    // builder controls and may be fractionally different in Clean/Painted.
    var playingWidthM = usesPhysicalTileDimensions
      ? positiveNumber(result.footprintWidthM)
      : (positiveNumber(result.playableWidthM) || positiveNumber(playableSurface.width_m) || positiveNumber(playableSurface.width) || positiveNumber(payload.widthM));
    var playingLengthM = usesPhysicalTileDimensions
      ? positiveNumber(result.footprintLengthM)
      : (positiveNumber(result.playableLengthM) || positiveNumber(playableSurface.length_m) || positiveNumber(playableSurface.length) || positiveNumber(payload.lengthM));
    var sport = normalizeSport(payload.courtType || result.sport);
    var courtLabel = getCourtLabel(sport, payload);
    var extras = getEnabledExtraSports(payload);
    var extraLabels = extras.map(function (item) { return item.label; });
    var widthLabel = formatOrderDimension(totalWidthM, units, usesPhysicalTileDimensions);
    var lengthLabel = formatOrderDimension(totalLengthM, units, usesPhysicalTileDimensions);
    var basketballSports = ['fiba', 'nba', 'ncaa', 'usahs', 'us-highschool'];
    var isBasketballCourt = basketballSports.indexOf(sport) >= 0;
    var courtProductLabel = courtLabel + (isBasketballCourt ? ' Basketball Court' : ' Court');
    var title = widthLabel && lengthLabel
      ? widthLabel + ' Wide × ' + lengthLabel + ' Long ' + courtProductLabel
      : courtProductLabel;
    if (extraLabels.length) title += ' with ' + joinLabels(extraLabels);
    var selectedSeriesId = getOrderSeriesId(seriesId);
    var series = selectedSeriesId ? (SERIES_LABELS[selectedSeriesId] || getSeriesLabel(root, payload)) : '';
    var specs = [];

    var courtDimensionValues = [];
    var editableCourtDimensions = [];
    if (totalWidthM) {
      courtDimensionValues.push('Total court width: ' + formatOrderDimension(totalWidthM, units, usesPhysicalTileDimensions));
      editableCourtDimensions.push({ kind: 'width', label: 'Total court width', valueM: totalWidthM, unit: units });
    }
    if (totalLengthM) {
      courtDimensionValues.push('Total court length: ' + formatOrderDimension(totalLengthM, units, usesPhysicalTileDimensions));
      editableCourtDimensions.push({ kind: 'length', label: 'Total court length', valueM: totalLengthM, unit: units });
    }
    if (playingWidthM) {
      courtDimensionValues.push('Playing surface width: ' + formatOrderDimension(playingWidthM, units, usesPhysicalTileDimensions));
      editableCourtDimensions.push(null);
    }
    if (playingLengthM) {
      courtDimensionValues.push('Playing surface length: ' + formatOrderDimension(playingLengthM, units, usesPhysicalTileDimensions));
      editableCourtDimensions.push(null);
    }
    if (courtDimensionValues.length) {
      specs.push({
        label: 'Court dimensions',
        values: courtDimensionValues,
        editableDimensions: editableCourtDimensions
      });
    }

    var keyDimensions = basketballSports.indexOf(sport) >= 0
      ? getKeyDimensionsMeters(root, payload, units)
      : null;
    var keyDimensionSpec = null;
    var threePointSpec = null;
    if (keyDimensions) {
      keyDimensionSpec = {
        label: 'Key dimensions',
        values: [
          'Key width: ' + formatLength(keyDimensions.widthM, units),
          'Key length: ' + formatLength(keyDimensions.lengthM, units)
        ],
        editableDimensions: [
          {
            kind: 'key-width',
            label: 'Key width',
            valueM: keyDimensions.widthM,
            unit: units,
            resettable: keyDimensions.widthAdjusted === true
          },
          {
            kind: 'key-length',
            label: 'Key length',
            valueM: keyDimensions.lengthM,
            unit: units,
            resettable: keyDimensions.lengthAdjusted === true
          }
        ]
      };
      var threePointDistance = getThreePointDistanceDetails(root, payload, units);
      if (threePointDistance && hasVisibleThreePointLine(root, payload, threePointDistance.valueM)) {
        threePointSpec = {
          label: '3-point line',
          value: formatLength(threePointDistance.valueM, units),
          editableDimension: {
            kind: 'three-point-line',
            label: '3-point line',
            valueM: threePointDistance.valueM,
            unit: units,
            resettable: threePointDistance.adjusted === true
          }
        };
      }
    }

    var hoopSides = getActiveHoopSides(root, payload);
    var hoopSpec = null;
    if (hoopSides.length) {
      var overhangs = payload.hoopOverhangsM || {};
      var overhangValues = hoopSides.reduce(function (items, side) {
        var value = toNumber(overhangs[side]);
        if (value == null) {
          var input = root.querySelector('#swishcb-' + side + '-hoop-distance');
          value = input ? toNumber(input.value) : null;
          if (value != null) value /= 100;
        }
        if (value != null) items.push({ side: side, valueM: value, value: formatLength(value, units), unit: units });
        return items;
      }, []);
      if (overhangValues.length === 1) {
        hoopSpec = {
          label: 'Hoop overhang',
          value: overhangValues[0].value,
          editableOverhangs: overhangValues
        };
      } else if (overhangValues.length > 1) {
        hoopSpec = {
          label: 'Hoop overhangs',
          values: overhangValues.map(function (item) {
            return humanizeId(item.side) + ' hoop: ' + item.value;
          }),
          editableOverhangs: overhangValues
        };
      }
    }

    var visibleBorders = getVisibleBorders(root, payload);
    var borderLabels = {
      top: 'Top sideline border',
      bottom: 'Bottom sideline border',
      left: 'Left baseline border',
      right: 'Right baseline border'
    };
    var borderValues = ['top', 'bottom', 'left', 'right'].reduce(function (items, side) {
      if (visibleBorders[side]) items.push(borderLabels[side] + ': ' + formatLength(visibleBorders[side], units));
      return items;
    }, []);
    var borderSpec = borderValues.length ? { label: 'Borders', values: borderValues } : null;

    var normalizedSeriesId = selectedSeriesId;
    if (!normalizedSeriesId && root && root.classList.contains('swishcb--preview-order-card')) {
      normalizedSeriesId = 'elastic';
      series = SERIES_LABELS.elastic || 'Elastic Tiles';
    }
    var tileSpec = TILE_SERIES_SPECS[normalizedSeriesId];
    var productTileCount = getOrderTileCount(result, normalizedSeriesId);
    var productTiles = normalizedSeriesId ? {
      label: 'Tiles',
      values: [
        'Series: ' + (series || SERIES_LABELS[normalizedSeriesId] || humanizeId(normalizedSeriesId)),
        'Size: ' + (tileSpec && tileSpec.sizeMm ? formatTileSize(tileSpec.sizeMm, useUsUnits) : '\u2014'),
        'Total: ' + (productTileCount ? formatNumber(productTileCount, 0) + ' tiles' : '\u2014')
      ]
    } : null;
    var edgeSides = getActiveTileEdgeSides(root, payload);
    var edgesAreAvailable = !!(
      payload.tilesEnabled ||
      root.querySelector('#swishcb-tile-series') && ORDER_SERIES_IDS.indexOf(String(root.querySelector('#swishcb-tile-series').value || '').toLowerCase()) >= 0 ||
      root.classList.contains('swishcb--preview-order-card') && normalizedSeriesId
    );
    var edgeSpec = null;
    var productEdgeValue = '';
    if (edgesAreAvailable && edgeSides.length && tileSpec) {
      var payloadEdgeM = positiveNumber(payload.tileEdgeSizeM);
      var edgeMm = payloadEdgeM ? payloadEdgeM * 1000 : tileSpec.edgeMm;
      var edgeCoverage = edgeSides.length === 4
        ? 'all sides'
        : edgeSides.length + ' ' + (edgeSides.length === 1 ? 'side' : 'sides');
      productEdgeValue = 'Edges: ' + formatMillimeters(edgeMm, useUsUnits) + ' on ' + edgeCoverage;
      edgeSpec = {
        label: 'Edges',
        values: [
          'Sides: ' + edgeSides.map(function (side) { return String(side || '').toLowerCase(); }).join(', '),
          'Size: ' + formatMillimeters(edgeMm, useUsUnits) + ' each'
        ]
      };
    }
    if (productTiles && productEdgeValue) productTiles.values.push(productEdgeValue);

    if (borderSpec) specs.push(borderSpec);
    if (edgeSpec) specs.push(edgeSpec);
    if (hoopSpec) specs.push(hoopSpec);
    if (threePointSpec) specs.push(threePointSpec);
    if (keyDimensionSpec) specs.push(keyDimensionSpec);
    var tileSpecification = null;
    var tileBenefits = null;
    if (tileSpec && Array.isArray(tileSpec.benefits) && tileSpec.benefits.length) {
      tileBenefits = {
        label: 'Benefits',
        placement: 'series',
        values: tileSpec.benefits.slice()
      };
    }
    if (tileSpec && tileSpec.sizeMm) {
      tileSpecification = {
        label: 'Tile specifications',
        placement: 'series',
        values: [
          'Size: ' + formatTileSize(tileSpec.sizeMm, useUsUnits),
          'Weight: ' + formatTileWeight(tileSpec, useUsUnits),
          'Tested: ' + formatTileTemperature(tileSpec, useUsUnits),
          'Use: ' + tileSpec.use,
          'Warranty: ' + tileSpec.warranty,
          'Lifespan: ' + tileSpec.lifespan
        ]
      };
    }

    if (extraLabels.length) specs.push({ label: 'Additional sports', values: extraLabels });

    var logos = Array.isArray(payload.logos) ? payload.logos : [];
    var usedLogoFileIndexes = [];
    logos.forEach(function (logo, index) {
      var widthM = positiveNumber(logo && logo.widthM);
      var heightM = positiveNumber(logo && logo.heightM);
      var values = [getLogoAttachment(payload, logo, index, usedLogoFileIndexes)];
      if (widthM && heightM) {
        values.push('Size: ' + formatLength(widthM, units) + ' × ' + formatLength(heightM, units));
        values.push('Area: ' + formatArtworkArea(widthM * heightM, useUsUnits));
      }
      specs.push({
        label: logos.length === 1 ? 'Logo' : 'Logo ' + (index + 1),
        values: values
      });
    });

    var texts = Array.isArray(payload.texts) ? payload.texts : [];
    texts.forEach(function (entry, index) {
      var value = String(entry && entry.text || '').trim();
      if (!value) return;
      var font = String(entry.customFontName || entry.font || 'Poppins').trim();
      var widthM = positiveNumber(entry && entry.widthM);
      var heightM = positiveNumber(entry && entry.heightM);
      var values = ['Text: ' + value, 'Font: ' + font];
      if (widthM && heightM) {
        values.push('Size: ' + formatLength(widthM, units) + ' × ' + formatLength(heightM, units));
        values.push('Area: ' + formatArtworkArea(widthM * heightM, useUsUnits));
      }
      specs.push({
        label: texts.length === 1 ? 'Text' : 'Text ' + (index + 1),
        values: values
      });
    });

    var colorValues = getBaseColorItems(payload, sport, hoopSides, {
      hasVisibleBorder: borderValues.length > 0,
      visibleBorderCount: Object.keys(visibleBorders).length,
      hasVisibleEdges: edgesAreAvailable && edgeSides.length > 0
    })
      .concat(getDetailColorItems(payload, hoopSides))
      .concat(extras.reduce(function (items, extra) {
        if (extra.color) items.push({ label: extra.label, hex: extra.color, targetKind: 'extra', targetId: extra.id });
        return items;
      }, []));
    var pattern = payload && payload.pattern && typeof payload.pattern === 'object'
      ? payload.pattern
      : (config && config.pattern && typeof config.pattern === 'object' ? config.pattern : null);
    if (pattern && pattern.enabled === true) {
      var patternTargetSelect = root.querySelector('#swishcb-pattern-target');
      var patternTargetLabel = patternTargetSelect && patternTargetSelect.selectedOptions && patternTargetSelect.selectedOptions[0]
        ? String(patternTargetSelect.selectedOptions[0].textContent || '').trim()
        : humanizeId(pattern.target || 'all');
      specs.push({
        label: 'Pattern',
        values: [
          'Type: Waves',
          'Apply to: ' + (patternTargetLabel || 'Entire court'),
          'Color 1: ' + (normalizeHex(pattern.color1) || ''),
          'Color 2: ' + (normalizeHex(pattern.color2) || '')
        ]
      });
    }
    if (colorValues.length) specs.push({ label: 'Colors', values: colorValues });
    if (tileBenefits) specs.push(tileBenefits);
    if (tileSpecification) specs.push(tileSpecification);

    var plainSpecifications = specs.map(function (spec) {
      return spec.label + (spec.values ? ' ' : ': ') + (spec.values ? spec.values.map(orderSpecificationValueText).join('; ') : orderSpecificationValueText(spec.value));
    }).join(' | ');
    var orderSourceUrl = String(root.dataset.swishcbOrderSourceUrl || window.location.href);
    var orderShareUrl = String(payload.shareUrl || orderSourceUrl);
    // Embedded preview editors return a store-relative builder link. Checkout
    // requires an absolute URL, resolved against the card's storefront source.
    try { orderShareUrl = new URL(orderShareUrl, orderSourceUrl).href; } catch (_) {}
    return {
      title: title,
      series: series,
      specs: specs,
      productTiles: productTiles,
      shareUrl: orderShareUrl,
      plainSpecifications: plainSpecifications,
      signature: JSON.stringify({ title: title, series: series, specs: specs, productTiles: productTiles })
    };
  }

  function readRules(panel) {
    var script = panel && panel.querySelector('[data-swishcb-order-rules]');
    if (!script) return [];
    var raw = String(script.textContent || '').trim();
    if (!raw) return [];
    try {
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : (Array.isArray(parsed.rules) ? parsed.rules : []);
    } catch (_) {
      return [];
    }
  }

  function ruleNumber(rule, camelKey, snakeKey) {
    if (!rule || typeof rule !== 'object') return null;
    return toNumber(rule[camelKey] != null ? rule[camelKey] : rule[snakeKey]);
  }

  function ruleMatches(rule, sport, areaM2) {
    if (!rule || typeof rule !== 'object') return false;
    var ruleSport = normalizeSport(rule.sport || rule.courtType || rule.court_type || '*');
    if (ruleSport && ruleSport !== '*' && ruleSport !== sport) return false;
    var min = ruleNumber(rule, 'minAreaM2', 'min_area_m2');
    var max = ruleNumber(rule, 'maxAreaM2', 'max_area_m2');
    if (min != null && areaM2 < min) return false;
    if (max != null) {
      var inclusive = rule.maxInclusive === true || rule.max_inclusive === true;
      if (inclusive ? areaM2 > max : areaM2 >= max) return false;
    }
    return true;
  }

  function findMatchingRule(panel, sport, areaM2) {
    var rules = readRules(panel);
    for (var i = 0; i < rules.length; i += 1) {
      if (ruleMatches(rules[i], sport, areaM2)) return rules[i];
    }
    return null;
  }

  function findVariantControl(panel) {
    return panel && panel.querySelector('[data-swishcb-order-variant]');
  }

  function selectedVariantData(panel) {
    var control = findVariantControl(panel);
    if (!control) return null;
    if (control.tagName === 'SELECT') {
      var option = control.options && control.options[control.selectedIndex];
      return option ? {
        id: String(option.value || ''),
        price: option.dataset.price || '',
        comparePrice: option.dataset.comparePrice || '',
        available: String(option.dataset.available || '').toLowerCase() === 'true'
      } : null;
    }
    return {
      id: String(control.value || ''),
      price: control.dataset.price || '',
      comparePrice: control.dataset.comparePrice || '',
      available: String(control.dataset.available || '').toLowerCase() === 'true'
    };
  }

  function selectRuleVariant(panel, variantId) {
    if (!variantId) return true;
    var control = findVariantControl(panel);
    if (!control) return false;
    var targetId = String(variantId);
    if (control.tagName === 'SELECT') {
      var option = Array.prototype.find.call(control.options || [], function (item) {
        return String(item.value) === targetId;
      });
      if (!option) return false;
      if (control.value !== targetId) {
        control.value = targetId;
        control.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return true;
    }
    if (String(control.value || '') !== targetId) return false;
    return true;
  }

  function setText(element, value) {
    if (!element) return;
    var nextValue = value == null ? '' : String(value);
    if (element.textContent !== nextValue) element.textContent = nextValue;
  }

  function releaseOrderDownloadUrls(list) {
    var urls = list && Array.isArray(list.swishcbOrderDownloadUrls)
      ? list.swishcbOrderDownloadUrls
      : [];
    urls.forEach(function (url) {
      try { URL.revokeObjectURL(url); } catch (_) {}
    });
    if (list) list.swishcbOrderDownloadUrls = [];
  }

  function appendOrderSpecificationValue(container, value, list, prefix) {
    if (value && typeof value === 'object' && value.type === 'add-artwork') {
      var artworkPrefix = prefix || String(value.prefix || '');
      if (artworkPrefix) container.appendChild(document.createTextNode(artworkPrefix));
      if (value.showEmptyLabel !== false) {
        var emptyLabel = document.createElement('span');
        emptyLabel.className = 'swishcb__order-artwork-empty';
        emptyLabel.textContent = String(value.emptyLabel || 'None');
        container.appendChild(emptyLabel);
        container.appendChild(document.createTextNode(' '));
      }
      var addButton = document.createElement('button');
      addButton.type = 'button';
      addButton.className = 'swishcb__order-artwork-add' + (value.mode === 'new' ? ' swishcb__order-artwork-add--more' : '');
      addButton.dataset.swishcbProductAddArtwork = value.kind === 'text' ? 'text' : 'logo';
      if (value.mode === 'new') addButton.dataset.swishcbProductAddArtworkMode = 'new';
      addButton.textContent = String(value.actionLabel || 'Upload');
      addButton.setAttribute(
        'aria-label',
        value.mode === 'new'
          ? (value.kind === 'text' ? 'Add another court text' : 'Add another court logo')
          : (value.kind === 'text' ? 'Add court text' : 'Upload a court logo')
      );
      container.appendChild(addButton);
      return;
    }
    if (value && typeof value === 'object' && value.type === 'download') {
      var link = document.createElement('a');
      link.className = 'swishcb__order-file-link';
      link.textContent = String(value.label || 'Attached file');
      link.download = String(value.downloadName || 'court-logo');
      var canCreateObjectUrl = value.file && typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function';
      if (canCreateObjectUrl) {
        var objectUrl = URL.createObjectURL(value.file);
        link.href = objectUrl;
        list.swishcbOrderDownloadUrls.push(objectUrl);
      } else if (value.url) {
        link.href = value.url;
      } else {
        link.removeAttribute('href');
        link.setAttribute('aria-disabled', 'true');
      }
      var downloadPrefix = prefix || String(value.prefix || '');
      if (downloadPrefix) container.appendChild(document.createTextNode(downloadPrefix));
      container.appendChild(link);
      return;
    }
    if (value && typeof value === 'object' && value.type === 'emphasized-label') {
      if (prefix) container.appendChild(document.createTextNode(prefix));
      var emphasizedLabel = document.createElement('strong');
      emphasizedLabel.className = 'swishcb__order-inline-label';
      emphasizedLabel.textContent = String(value.label || '') + ':';
      container.appendChild(emphasizedLabel);
      container.appendChild(document.createTextNode(' ' + String(value.value || '')));
      return;
    }
    if (prefix) container.appendChild(document.createTextNode(prefix));
    container.appendChild(document.createTextNode(String(value || '')));
  }

  function syncProductMeasurementEditorWidth(input) {
    if (!input) return;
    var editor = input.closest('.swishcb__product-order-overhang-editor, .swishcb__product-order-dimension-editor');
    if (!editor) return;
    var mirror = editor.querySelector('.swishcb__product-order-measurement-mirror');
    if (!mirror) {
      mirror = document.createElement('span');
      mirror.className = 'swishcb__product-order-measurement-mirror';
      mirror.setAttribute('aria-hidden', 'true');
      editor.appendChild(mirror);
    }
    // Let the mirror size the shared grid cell. Unlike field-sizing or a
    // one-time pixel measurement, this also works in Safari, while the summary
    // is hidden, and when the font or viewport changes after it is rendered.
    var text = String(input.value || '0');
    if (mirror.textContent !== text) mirror.textContent = text;
    input.style.removeProperty('width');
  }

  function appendProductOverhangEditor(container, overhang, prefix) {
    if (prefix) container.appendChild(document.createTextNode(prefix));
    var units = overhang && overhang.unit === 'ft' ? 'ft' : 'm';
    var meters = toNumber(overhang && overhang.valueM);
    var displayValue = meters == null ? '' : (units === 'ft' ? meters / 0.3048 : meters);
    var editor = document.createElement('span');
    editor.className = 'swishcb__product-order-overhang-editor';
    editor.dataset.unit = units;
    var input = document.createElement('input');
    input.type = 'text';
    input.inputMode = 'decimal';
    input.autocomplete = 'off';
    input.spellcheck = false;
    input.pattern = '[0-9]*[.,]?[0-9]*';
    input.value = displayValue === '' ? '' : formatDecimal(displayValue, 3);
    input.dataset.swishcbProductOverhangInput = 'true';
    input.dataset.swishcbOverhangSide = String(overhang && overhang.side || '');
    makeMeasurementTrigger(input);
    input.setAttribute('aria-label', (overhang && overhang.side ? humanizeId(overhang.side) + ' hoop overhang' : 'Hoop overhang') + ' in ' + (units === 'ft' ? 'feet' : 'metres'));
    editor.appendChild(input);
    container.appendChild(editor);
    appendMeasurementEditButton(container, input);
    syncProductMeasurementEditorWidth(input);
  }

  function appendProductDimensionEditor(container, dimension, prefix) {
    if (prefix) container.appendChild(document.createTextNode(prefix));
    var units = dimension && dimension.unit === 'ft' ? 'ft' : 'm';
    var meters = toNumber(dimension && dimension.valueM);
    var displayValue = meters == null ? '' : (units === 'ft' ? meters / 0.3048 : meters);
    var editor = document.createElement('span');
    editor.className = 'swishcb__product-order-dimension-editor';
    editor.dataset.unit = units;
    var input = document.createElement('input');
    input.type = 'text';
    input.inputMode = 'decimal';
    input.autocomplete = 'off';
    input.spellcheck = false;
    input.pattern = '[0-9]*[.,]?[0-9]*';
    input.value = displayValue === '' ? '' : formatDecimal(displayValue, 3);
    input.dataset.swishcbProductDimensionInput = 'true';
    input.dataset.swishcbDimensionKind = String(dimension && dimension.kind || '');
    makeMeasurementTrigger(input);
    input.setAttribute('aria-label', String(dimension && dimension.label || 'Court dimension') + ' in ' + (units === 'ft' ? 'feet' : 'metres'));
    editor.appendChild(input);
    container.appendChild(editor);
    appendMeasurementEditButton(container, input);
    if (dimension && dimension.resettable) {
      var resetButton = document.createElement('button');
      resetButton.type = 'button';
      resetButton.className = 'swishcb__product-order-dimension-reset';
      resetButton.textContent = 'Set official';
      resetButton.dataset.swishcbProductDimensionReset = String(dimension.kind || '');
      resetButton.setAttribute('aria-label', 'Set ' + String(dimension.label || 'court dimension').toLowerCase() + ' to the official regulation value');
      container.appendChild(resetButton);
    }
    syncProductMeasurementEditorWidth(input);
  }

  function writeMetersToSourceInput(source, meters) {
    if (!source || meters == null) return false;
    var sourceShell = source.closest('[data-unit]');
    var sourceUnit = String(sourceShell && sourceShell.dataset.unit || 'm').toLowerCase();
    var sourceValue = meters;
    if (sourceUnit === 'cm') sourceValue = meters * 100;
    else if (sourceUnit === 'in') sourceValue = meters / 0.0254;
    else if (sourceUnit === 'ft') sourceValue = meters / 0.3048;
    // Keep the stored measurement exact when restoring defaults or converting
    // units. The summary formats its own display; rounding here changes geometry
    // (for example a backboard-anchored official key length) on Reset.
    source.value = formatDecimal(sourceValue, 9);
    source.dispatchEvent(new Event('input', { bubbles: true }));
    source.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function updateProductOverhang(root, input) {
    if (!root || !input) return false;
    var rawValue = String(input.value || '').trim();
    if (!rawValue) return false;
    var value = toNumber(rawValue.replace(',', '.'));
    if (value == null || value < 0) return false;
    var editor = input.closest('.swishcb__product-order-overhang-editor');
    var displayUnit = editor && editor.dataset.unit === 'ft' ? 'ft' : 'm';
    var meters = displayUnit === 'ft' ? value * 0.3048 : value;
    var side = String(input.dataset.swishcbOverhangSide || '').trim();
    var source = side ? root.querySelector('#swishcb-' + side + '-hoop-distance') : null;
    if (!source) source = root.querySelector('[data-hoop-distance]');
    if (!source) return false;
    return writeMetersToSourceInput(source, meters);
  }

  function updateProductDimension(root, input) {
    if (!root || !input) return false;
    var rawValue = String(input.value || '').trim();
    if (!rawValue) return false;
    var value = toNumber(rawValue.replace(',', '.'));
    if (value == null || value <= 0) return false;
    var editor = input.closest('.swishcb__product-order-dimension-editor');
    var displayUnit = editor && editor.dataset.unit === 'ft' ? 'ft' : 'm';
    var meters = displayUnit === 'ft' ? value * 0.3048 : value;
    var kind = String(input.dataset.swishcbDimensionKind || '').toLowerCase();
    var sourceSelectors = {
      width: '#swishcb-width',
      length: '#swishcb-length',
      'key-width': '#swishcb-key-width',
      'key-length': '#swishcb-key-length',
      'three-point-line': '#swishcb-three-point-distance'
    };
    var sourceSelector = sourceSelectors[kind];
    if (!sourceSelector) return false;
    var source = root.querySelector(sourceSelector);
    return writeMetersToSourceInput(source, meters);
  }

  function resetProductDimension(root, button) {
    if (!root || !button) return false;
    var kind = String(button.dataset.swishcbProductDimensionReset || '').toLowerCase();
    var sourceSelectors = {
      'key-width': '#swishcb-key-width',
      'key-length': '#swishcb-key-length',
      'three-point-line': '#swishcb-three-point-distance'
    };
    var sourceSelector = sourceSelectors[kind];
    if (!sourceSelector) return false;
    var source = root.querySelector(sourceSelector);
    if (!source) return false;
    source.value = '';
    source.dispatchEvent(new Event('input', { bubbles: true }));
    source.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function makeMeasurementTrigger(input) {
    input.readOnly = true;
    input.inputMode = 'none';
    input.dataset.swishcbMeasurementTrigger = 'true';
    input.setAttribute('role', 'button');
    input.setAttribute('aria-haspopup', 'dialog');
    input.setAttribute('aria-expanded', 'false');
  }

  function measurementKey(input) {
    if (input.dataset.swishcbMeasurementKey) return input.dataset.swishcbMeasurementKey;
    return input.hasAttribute('data-swishcb-product-overhang-input')
      ? 'overhang:' + input.dataset.swishcbOverhangSide
      : input.dataset.swishcbDimensionKind;
  }

  function appendMeasurementEditButton(container, input) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'swishcb__order-edit';
    button.textContent = 'Edit';
    button.dataset.swishcbMeasurementTrigger = 'true';
    button.dataset.swishcbMeasurementEdit = 'true';
    button.dataset.swishcbMeasurementKey = measurementKey(input);
    button.setAttribute('aria-label', 'Edit ' + input.getAttribute('aria-label'));
    button.setAttribute('aria-haspopup', 'dialog');
    button.setAttribute('aria-expanded', 'false');
    container.appendChild(button);
  }

  function measurementSource(root, key) {
    var id = {
      width: 'swishcb-width', length: 'swishcb-length',
      'key-width': 'swishcb-key-width', 'key-length': 'swishcb-key-length',
      'three-point-line': 'swishcb-three-point-distance'
    }[key];
    if (key.indexOf('overhang:') === 0) id = 'swishcb-' + key.slice(9) + '-hoop-distance';
    return id ? root.querySelector('#' + id) : null;
  }

  function measurementTrigger(root, panel) {
    var scope = panel && panel.list && panel.list.isConnected ? panel.list : root;
    return Array.from(scope.querySelectorAll('[data-swishcb-measurement-trigger]')).find(function (input) {
      return measurementKey(input) === panel.key
        && input.hasAttribute('data-swishcb-measurement-edit') === panel.triggerIsEdit;
    });
  }

  function syncMeasurementState(root, summary) {
    var state = getState(root), values = {};
    var context = root.getAttribute('data-swish-preview-order-context') || '';
    if (state.measurementContext !== context) {
      var contexts = state.measurementContexts || (state.measurementContexts = new Map());
      if (state.measurementContext != null) contexts.set(state.measurementContext, {
        values: state.measurementDefaults, locked: state.measurementDefaultsLocked
      });
      var saved = contexts.get(context);
      state.measurementContext = context;
      state.measurementDefaults = saved && saved.values;
      state.measurementDefaultsLocked = !!(saved && saved.locked);
      var previousPanel = state.measurementPanel;
      if (previousPanel) {
        window.clearTimeout(previousPanel.inputTimer);
        previousPanel.inputTimer = 0;
        previousPanel.dirty = false;
        previousPanel.element.hidden = true;
      }
    }
    (summary.specs || []).forEach(function (spec) {
      (spec.editableDimensions || []).concat(spec.editableDimension || []).forEach(function (dimension) {
        if (dimension && measurementSource(root, dimension.kind)) values[dimension.kind] = dimension;
      });
      (spec.editableOverhangs || []).forEach(function (overhang) {
        if (!overhang) return;
        values['overhang:' + overhang.side] = Object.assign({}, overhang, {
          label: (spec.editableOverhangs.length > 1 ? humanizeId(overhang.side) + ' hoop' : 'Hoop') + ' overhang'
        });
      });
    });
    state.measurementValues = values;
    // Startup can normalize tiles and edge pieces. Capture that settled preview
    // until the first customer interaction, then retain its values in metres.
    if (!state.measurementDefaultsLocked) state.measurementDefaults = Object.assign({}, values);
    updateMeasurementPanel(root);
  }

  function measurementStepButton(root, key, direction) {
    var source = measurementSource(root, key);
    if (!source) return null;
    var selector = key === 'width' || key === 'length'
      ? '[data-size-adjust="' + key + '"]'
      : '[data-value-adjust="' + source.id + '"]';
    return root.querySelector(selector + '[data-direction="' + direction + '"]');
  }

  function updateMeasurementPanel(root, force) {
    var state = getState(root), panel = state.measurementPanel;
    if (!panel || panel.element.hidden) return;
    var value = state.measurementValues && state.measurementValues[panel.key];
    if (!value) { closeMeasurementPanel(root, false); return; }
    var units = value.unit === 'ft' ? 'ft' : 'm';
    var display = formatDecimal(units === 'ft' ? value.valueM / 0.3048 : value.valueM, 3);
    if (force || !panel.dirty || panel.unit !== units) panel.input.value = display;
    panel.unit = units;
    panel.units.textContent = units;
    panel.input.setAttribute('aria-label', value.label + ' in ' + (units === 'ft' ? 'feet' : 'metres'));
    panel.title.textContent = value.label;
    var original = state.measurementDefaults && state.measurementDefaults[panel.key];
    panel.reset.disabled = !original;
    panel.reset.title = original ? 'Reset to ' + formatDecimal(units === 'ft' ? original.valueM / 0.3048 : original.valueM, 3) + ' ' + units : '';
    [-1, 1].forEach(function (direction) {
      var sourceButton = measurementStepButton(root, panel.key, direction);
      panel.steps[direction].disabled = !sourceButton || sourceButton.disabled;
    });
    var trigger = measurementTrigger(root, panel);
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
      trigger.setAttribute('aria-controls', panel.element.id);
    }
    positionMeasurementPanel(root);
  }

  function positionMeasurementPanel(root) {
    var panel = getState(root).measurementPanel;
    if (!panel || panel.element.hidden) return;
    var trigger = measurementTrigger(root, panel);
    var anchor = trigger && (panel.triggerIsEdit ? trigger : trigger.closest('[data-unit]'));
    var rect = anchor && anchor.getBoundingClientRect();
    var viewport = window.visualViewport;
    var left = viewport ? viewport.offsetLeft : 0, top = viewport ? viewport.offsetTop : 0;
    var right = left + (viewport ? viewport.width : document.documentElement.clientWidth);
    var bottom = top + (viewport ? viewport.height : window.innerHeight);
    if (!root.isConnected || !rect || !rect.width || rect.right < left || rect.left > right || rect.bottom < top || rect.top > bottom) {
      closeMeasurementPanel(root, false); return;
    }
    var minX = left + 12, maxX = right - 12, minY = top + 12, maxY = bottom - 12;
    var element = panel.element;
    element.style.width = Math.min(288, maxX - minX) + 'px';
    element.style.minWidth = '0';
    element.style.maxHeight = Math.max(80, maxY - minY) + 'px';
    var natural = element.getBoundingClientRect(), gap = 8;
    var candidates = [
      [rect.left - gap, rect.top, -1, 1], [rect.left - gap, rect.bottom, -1, -1],
      [rect.right + gap, rect.top, 1, 1], [rect.right + gap, rect.bottom, 1, -1],
      [rect.left, rect.bottom + gap, 1, 1], [rect.right, rect.bottom + gap, -1, 1],
      [rect.left, rect.top - gap, 1, -1], [rect.right, rect.top - gap, -1, -1]
    ];
    var best;
    candidates.forEach(function (candidate) {
      // Keep the complete editor at its natural size. Prefer an adjacent
      // placement that fits, then shift inward instead of narrowing a corner.
      var x = candidate[0] - (candidate[2] < 0 ? natural.width : 0);
      var y = candidate[1] - (candidate[3] < 0 ? natural.height : 0);
      var fitX = Math.max(minX, Math.min(x, maxX - natural.width));
      var fitY = Math.max(minY, Math.min(y, maxY - natural.height));
      var score = Math.pow(fitX - x, 2) + Math.pow(fitY - y, 2);
      if (!best || score < best.score - 0.5) best = { x: fitX, y: fitY, score: score };
    });
    if (!best) return;
    element.style.left = best.x + 'px';
    element.style.top = best.y + 'px';
  }

  function commitMeasurementPanel(root) {
    var panel = getState(root).measurementPanel;
    if (!panel) return false;
    window.clearTimeout(panel.inputTimer);
    panel.inputTimer = 0;
    if (!panel.dirty) return true;
    var text = String(panel.input.value).trim();
    var number = text ? toNumber(text.replace(',', '.')) : null;
    var isOverhang = panel.key.indexOf('overhang:') === 0;
    if (number == null || number < 0 || (!isOverhang && number === 0)) {
      panel.input.setAttribute('aria-invalid', 'true');
      panel.error.textContent = isOverhang ? 'Enter zero or a positive measurement.' : 'Enter a measurement greater than zero.';
      panel.error.hidden = false;
      return false;
    }
    panel.input.removeAttribute('aria-invalid');
    panel.error.hidden = true;
    var source = measurementSource(root, panel.key);
    var result = writeMetersToSourceInput(source, panel.unit === 'ft' ? number * 0.3048 : number);
    scheduleRefresh(root);
    scheduleSettledRefresh(root);
    return result;
  }

  function closeMeasurementPanel(root, restoreFocus) {
    var state = getState(root), panel = state.measurementPanel;
    if (!panel || panel.element.hidden) return;
    commitMeasurementPanel(root);
    panel.element.hidden = true;
    panel.dirty = false;
    if (panel.element.contains(document.activeElement)) document.activeElement.blur();
    var trigger = measurementTrigger(root, panel);
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (restoreFocus && trigger) {
      panel.restoringFocus = true;
      trigger.focus({ preventScroll: true });
      panel.restoringFocus = false;
    }
  }

  function openMeasurementPanel(root, trigger) {
    var state = getState(root);
    var key = measurementKey(trigger);
    if (!state.measurementValues || !state.measurementValues[key]) refresh(root);
    if (!state.measurementValues || !state.measurementValues[key]) return;
    state.measurementDefaultsLocked = true;
    var panel = state.measurementPanel;
    if (panel && panel.restoringFocus) return;
    if (panel && !panel.element.hidden && panel.key === key
      && panel.triggerIsEdit === trigger.hasAttribute('data-swishcb-measurement-edit')) return;
    if (panel) closeMeasurementPanel(root, false);
    if (!panel) {
      var element = document.createElement('div');
      element.className = 'swishcb__color-popover swishcb__measurement-popover';
      element.dataset.swishcbViewportAnchor = 'true';
      element.setAttribute('role', 'dialog');
      element.setAttribute('aria-modal', 'false');
      element.tabIndex = -1;
      element.id = 'swishcb-measurement-panel-' + (document.querySelectorAll('.swishcb__measurement-popover').length + 1);
      element.innerHTML = '<div class="swishcb__color-popover__title" data-measurement-title></div>' +
        '<button type="button" class="swishcb__color-popover__close-x" data-measurement-close aria-label="Close measurement panel">×</button>' +
        '<div class="swishcb__measurement-stepper"><button type="button" data-measurement-step="-1" aria-label="Decrease measurement">−</button>' +
        '<label class="swishcb__measurement-value"><input type="text" inputmode="decimal" autocomplete="off" spellcheck="false" data-measurement-value><span data-measurement-unit></span></label>' +
        '<button type="button" data-measurement-step="1" aria-label="Increase measurement">+</button></div>' +
        '<p class="swishcb__measurement-error" data-measurement-error role="alert" hidden></p>' +
        '<div class="swishcb__color-popover__actions"><button type="button" class="swishcb__btn swishcb__popover-button" data-measurement-reset>Reset</button>' +
        '<button type="button" class="swishcb__btn swishcb__popover-button swishcb__popover-button--primary" data-measurement-apply>Apply</button></div>';
      document.body.appendChild(element);
      panel = state.measurementPanel = {
        element: element, input: element.querySelector('[data-measurement-value]'),
        title: element.querySelector('[data-measurement-title]'), units: element.querySelector('[data-measurement-unit]'),
        error: element.querySelector('[data-measurement-error]'),
        reset: element.querySelector('[data-measurement-reset]'), steps: {}
      };
      panel.title.id = element.id + '-title';
      element.setAttribute('aria-labelledby', panel.title.id);
      [-1, 1].forEach(function (direction) {
        panel.steps[direction] = element.querySelector('[data-measurement-step="' + direction + '"]');
        panel.steps[direction].addEventListener('click', function () {
          if (!commitMeasurementPanel(root)) return;
          panel.dirty = false;
          var button = measurementStepButton(root, panel.key, direction);
          if (button && !button.disabled) button.click();
          refresh(root);
          updateMeasurementPanel(root, true);
          panel.steps[direction].focus({ preventScroll: true });
        });
      });
      panel.input.addEventListener('input', function () {
        panel.dirty = true;
        window.clearTimeout(panel.inputTimer);
        panel.inputTimer = window.setTimeout(function () { commitMeasurementPanel(root); }, 180);
      });
      panel.input.addEventListener('change', function () {
        if (commitMeasurementPanel(root)) { panel.dirty = false; refresh(root); updateMeasurementPanel(root, true); }
      });
      panel.reset.addEventListener('click', function () {
        window.clearTimeout(panel.inputTimer);
        panel.dirty = false;
        panel.error.hidden = true;
        panel.input.removeAttribute('aria-invalid');
        var original = state.measurementDefaults && state.measurementDefaults[panel.key];
        if (original) {
          var official = original.resettable === false &&
            ['key-width', 'key-length', 'three-point-line'].indexOf(panel.key) >= 0;
          if (official) resetProductDimension(root, { dataset: { swishcbProductDimensionReset: panel.key } });
          else writeMetersToSourceInput(measurementSource(root, panel.key), original.valueM);
        }
        refresh(root);
        updateMeasurementPanel(root, true);
        scheduleSettledRefresh(root);
      });
      element.querySelectorAll('[data-measurement-close]').forEach(function (button) {
        button.addEventListener('click', function () { closeMeasurementPanel(root, true); });
      });
      element.querySelector('[data-measurement-apply]').addEventListener('click', function () {
        if (!commitMeasurementPanel(root)) { panel.input.focus({ preventScroll: true }); return; }
        panel.dirty = false;
        closeMeasurementPanel(root, true);
      });
      element.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); closeMeasurementPanel(root, true); }
        if (event.key === 'Enter' && event.target === panel.input) {
          event.preventDefault();
          if (commitMeasurementPanel(root)) { panel.dirty = false; refresh(root); updateMeasurementPanel(root, true); }
        }
      });
      var reposition = function () { positionMeasurementPanel(root); };
      window.addEventListener('resize', reposition);
      if (window.ResizeObserver) new ResizeObserver(reposition).observe(element);
      // Page/container scrolling dismisses the editor. Its own overflow and
      // visual viewport changes (including the keyboard) remain usable.
      document.addEventListener('scroll', function (event) {
        if (event.target instanceof Node && element.contains(event.target)) return;
        closeMeasurementPanel(root, false);
      }, { passive: true, capture: true });
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', reposition);
        window.visualViewport.addEventListener('scroll', reposition);
      }
      document.addEventListener('pointerdown', function (event) {
        if (element.hidden || element.contains(event.target)) return;
        closeMeasurementPanel(root, false);
      }, true);
      document.addEventListener('focusin', function (event) {
        if (element.hidden || element.contains(event.target)) return;
        if (event.target === measurementTrigger(root, panel)) return;
        closeMeasurementPanel(root, false);
      });
      document.addEventListener('swishcb:popover-open', function (event) {
        if (event.detail.element !== element) closeMeasurementPanel(root, false);
      });
      window.addEventListener('pagehide', function () { closeMeasurementPanel(root, false); });
    }
    document.dispatchEvent(new CustomEvent('swishcb:popover-open', { detail: { element: panel.element } }));
    panel.key = key;
    panel.triggerIsEdit = trigger.hasAttribute('data-swishcb-measurement-edit');
    panel.list = trigger.closest('[data-swishcb-product-order-specifications]');
    panel.dirty = false;
    panel.error.hidden = true;
    panel.input.removeAttribute('aria-invalid');
    panel.element.hidden = false;
    updateMeasurementPanel(root, true);
    panel.element.focus({ preventScroll: true });
  }

  function openProductArtworkManager(root, kind, addNew) {
    if (!root) return false;
    var artworkKind = kind === 'text' ? 'text' : 'logo';
    var panel = root.querySelector('[data-swishcb-panel="logos"]');
    if (!panel) return false;
    var panelSummary = panel.querySelector(':scope > summary');
    var manager = panel.querySelector('[data-media-manager="' + artworkKind + '"]');
    if (artworkKind === 'text') {
      for (var parent = panel.parentElement; parent && parent !== root; parent = parent.parentElement) {
        if (parent.matches('details[data-swishcb-panel]')) {
          parent.open = true;
          parent.dataset.swishcbSummaryCollapsed = 'false';
          var parentSummary = parent.querySelector(':scope > summary');
          if (parentSummary) parentSummary.setAttribute('aria-expanded', 'true');
        }
      }
      panel.open = true;
      panel.dataset.swishcbSummaryCollapsed = 'false';
      if (panelSummary) panelSummary.setAttribute('aria-expanded', 'true');
      if (manager) {
        manager.open = true;
        var managerSummary = manager.querySelector(':scope > summary');
        if (managerSummary) managerSummary.setAttribute('aria-expanded', 'true');
      }
      var scrollTarget = manager || panel;
      if (scrollTarget && typeof scrollTarget.scrollIntoView === 'function') {
        scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    if (artworkKind === 'logo') {
      var uploadButton = null;
      if (addNew) {
        var addAnotherLogoButton = manager && manager.querySelector('[data-logo-add]');
        if (addAnotherLogoButton) {
          addAnotherLogoButton.dataset.swishcbSuppressScrollOnce = 'true';
          addAnotherLogoButton.dataset.swishcbPreserveOrderNavigationOnce = 'true';
          addAnotherLogoButton.click();
        }
        var addedLogoUploadButtons = manager
          ? manager.querySelectorAll('[data-logo-upload]')
          : [];
        uploadButton = addedLogoUploadButtons.length
          ? addedLogoUploadButtons[addedLogoUploadButtons.length - 1]
          : null;
      } else {
        uploadButton = manager && manager.querySelector('[data-logo-upload]');
      }
      if (!uploadButton) {
        var addLogoButton = manager && manager.querySelector('[data-logo-add]');
        if (addLogoButton) {
          addLogoButton.dataset.swishcbSuppressScrollOnce = 'true';
          addLogoButton.dataset.swishcbPreserveOrderNavigationOnce = 'true';
          addLogoButton.click();
        }
        var logoUploadButtons = manager ? manager.querySelectorAll('[data-logo-upload]') : [];
        uploadButton = logoUploadButtons.length ? logoUploadButtons[logoUploadButtons.length - 1] : null;
      }
      if (uploadButton) {
        uploadButton.dataset.swishcbPreserveOrderNavigationOnce = 'true';
        uploadButton.click();
        return true;
      }
      return false;
    }

    var textInputs = manager ? Array.prototype.slice.call(manager.querySelectorAll('[data-text-value]')) : [];
    var textInput = null;
    if (addNew) {
      var addAnotherTextButton = manager && manager.querySelector('[data-text-add]');
      if (addAnotherTextButton) addAnotherTextButton.click();
      textInputs = manager ? Array.prototype.slice.call(manager.querySelectorAll('[data-text-value]')) : [];
      textInput = textInputs.length ? textInputs[textInputs.length - 1] : null;
    } else {
      textInput = textInputs.find(function (candidate) {
        return !String(candidate.value || '').trim();
      }) || textInputs[0] || null;
    }
    if (!textInput) {
      var addTextButton = manager && manager.querySelector('[data-text-add]');
      if (addTextButton) addTextButton.click();
      textInputs = manager ? Array.prototype.slice.call(manager.querySelectorAll('[data-text-value]')) : [];
      textInput = textInputs.length ? textInputs[textInputs.length - 1] : null;
    }
    if (!textInput) return false;
    try { textInput.focus({ preventScroll: true }); } catch (_) { textInput.focus(); }
    try { textInput.select(); } catch (_) {}
    return true;
  }

  // Use the native series select so tile fitting, pricing and shared designs
  // follow the same path as the main builder's tile controls.
  function openSummarySeriesPanel(root, trigger) {
    var state = getState(root), panel = state.summarySeriesPanel;
    var list = trigger.closest('[data-swishcb-product-order-specifications]');
    var select = list && list.querySelector('[data-swishcb-product-series-select]');
    if (!select) return;
    if (!panel) {
      var element = document.createElement('div');
      element.className = 'swishcb__color-popover swishcb__tile-series-popover';
      element.dataset.swishcbViewportAnchor = 'true';
      element.id = 'swishcb-tile-series-panel-' + (document.querySelectorAll('.swishcb__tile-series-popover').length + 1);
      element.setAttribute('role', 'dialog');
      element.setAttribute('aria-modal', 'false');
      element.setAttribute('aria-labelledby', element.id + '-title');
      element.hidden = true;
      element.innerHTML = '<div class="swishcb__color-popover__title" id="' + element.id + '-title">Tile series</div>' +
        '<button type="button" class="swishcb__color-popover__close-x" data-series-close aria-label="Close tile series">×</button>' +
        '<div class="swishcb__tile-series-options" role="group" aria-label="Tile series options"></div>';
      document.body.appendChild(element);
      panel = state.summarySeriesPanel = { element: element };
      panel.anchor = function () {
        return panel.list && panel.list.querySelector(panel.edit ? '.swishcb__order-edit[data-swishcb-product-series-trigger]' : '.swishcb__order-series-value');
      };
      panel.close = function (restoreFocus) {
        if (element.hidden) return;
        element.hidden = true;
        if (panel.list) panel.list.querySelectorAll('[data-swishcb-product-series-trigger]').forEach(function (button) { button.setAttribute('aria-expanded', 'false'); });
        var anchor = panel.anchor();
        if (restoreFocus && anchor) anchor.focus({ preventScroll: true });
      };
      panel.position = function () {
        if (element.hidden) return;
        var anchor = panel.anchor(), rect = anchor && anchor.getBoundingClientRect();
        var viewport = window.visualViewport;
        var left = viewport ? viewport.offsetLeft : 0, top = viewport ? viewport.offsetTop : 0;
        var right = left + (viewport ? viewport.width : document.documentElement.clientWidth);
        var bottom = top + (viewport ? viewport.height : innerHeight);
        if (!root.isConnected || !rect || !rect.width || rect.bottom < top || rect.top > bottom) { panel.close(false); return; }
        element.style.width = Math.min(288, right - left - 24) + 'px';
        element.style.minWidth = '0';
        element.style.maxHeight = Math.max(80, bottom - top - 24) + 'px';
        var size = element.getBoundingClientRect();
        var y = rect.bottom + 8;
        if (y + size.height > bottom - 12) y = rect.top - size.height - 8;
        element.style.left = Math.max(left + 12, Math.min(rect.left, right - size.width - 12)) + 'px';
        element.style.top = Math.max(top + 12, Math.min(y, bottom - size.height - 12)) + 'px';
      };
      element.querySelector('[data-series-close]').addEventListener('click', function () { panel.close(true); });
      element.addEventListener('click', function (event) {
        var option = event.target.closest('[data-swishcb-summary-series-option]');
        if (!option || option.disabled) return;
        var current = panel.list && panel.list.querySelector('[data-swishcb-product-series-select]');
        var nativeOption = current && current.querySelector('option[value="' + option.dataset.swishcbSummarySeriesOption + '"]');
        if (!nativeOption || nativeOption.disabled) return;
        panel.close(true);
        if (current.value === nativeOption.value) return;
        current.value = nativeOption.value;
        current.dispatchEvent(new Event('change', { bubbles: true }));
      });
      element.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); panel.close(true); }
      });
      document.addEventListener('pointerdown', function (event) {
        if (!element.contains(event.target)) panel.close(false);
      }, true);
      document.addEventListener('focusin', function (event) {
        if (!element.contains(event.target) && !(panel.list && panel.list.contains(event.target) && event.target.hasAttribute('data-swishcb-product-series-trigger'))) panel.close(false);
      });
      document.addEventListener('scroll', function (event) {
        if (!(event.target instanceof Node && element.contains(event.target))) panel.close(false);
      }, { passive: true, capture: true });
      document.addEventListener('swishcb:popover-open', function (event) {
        if (!event.detail || event.detail.element !== element) panel.close(false);
      });
      window.addEventListener('resize', panel.position);
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', panel.position);
        window.visualViewport.addEventListener('scroll', panel.position);
      }
      window.addEventListener('pagehide', function () { panel.close(false); });
    }
    panel.list = list;
    panel.edit = trigger.classList.contains('swishcb__order-edit');
    var group = panel.element.querySelector('.swishcb__tile-series-options');
    group.replaceChildren();
    var seriesOptions = root.matches('[data-swishcb-configurator="cards"]') && !root.classList.contains('swishcb--preview-order-card')
      ? Array.from(select.options).map(function (option) { return option.value; })
      : ['elastic', 'elite', 'large-elite'];
    seriesOptions.forEach(function (id) {
      var nativeOption = select.querySelector('option[value="' + id + '"]');
      var option = document.createElement('button');
      option.type = 'button';
      option.dataset.swishcbSummarySeriesOption = id;
      option.textContent = nativeOption ? nativeOption.textContent : SERIES_LABELS[id] || humanizeId(id);
      option.disabled = !nativeOption || nativeOption.disabled;
      option.setAttribute('aria-pressed', String(select.value === id));
      group.appendChild(option);
    });
    document.dispatchEvent(new CustomEvent('swishcb:popover-open', { detail: { element: panel.element } }));
    panel.element.hidden = false;
    list.querySelectorAll('[data-swishcb-product-series-trigger]').forEach(function (button) {
      button.setAttribute('aria-expanded', 'true');
      button.setAttribute('aria-controls', panel.element.id);
    });
    panel.position();
    var selected = group.querySelector('[aria-pressed="true"]:not(:disabled)') || group.querySelector('button:not(:disabled)');
    if (selected && !panel.element.hidden) selected.focus({ preventScroll: true });
  }

  function renderOrderSpecifications(list, specs, signature) {
    if (!list) return;
    var activeElement = list.ownerDocument ? list.ownerDocument.activeElement : null;
    var focusedProductEditor = list.hasAttribute('data-swishcb-product-order-specifications') && (
      list.dataset.swishcbOrderEditorActive === 'true'
      || (activeElement && !activeElement.readOnly && list.contains(activeElement) && activeElement.matches && activeElement.matches('[data-swishcb-product-overhang-input], [data-swishcb-product-dimension-input]'))
    );
    if (focusedProductEditor) {
      list.hidden = !specs.length;
      return;
    }
    if (list.dataset.swishcbSummarySignature !== signature) {
      var focusedColorControl = activeElement && list.contains(activeElement) && activeElement.hasAttribute('data-swishcb-order-color-swatch')
        ? { kind: activeElement.dataset.swishcbOrderColorTargetKind, id: activeElement.dataset.swishcbOrderColorTargetId, className: activeElement.className }
        : null;
      var focusedSeriesControl = activeElement && list.contains(activeElement) && activeElement.hasAttribute('data-swishcb-product-series-trigger')
        ? activeElement.className : '';
      releaseOrderDownloadUrls(list);
      list.swishcbOrderDownloadUrls = [];
      var fragment = document.createDocumentFragment();
      var isProductOrderingList = list.hasAttribute('data-swishcb-product-order-specifications');
      var summarySurfacePanel = isProductOrderingList && list.closest('[data-swishcb-configurator="cards"]')
        ? list.querySelector('[data-swishcb-panel="order-now"]') : null;
      var summarySurfaceToggle = summarySurfacePanel ? list.querySelector('.swishcb__flooring-learn-more') : null;
      var focusedSurfaceToggle = summarySurfaceToggle === activeElement;
      var productOrderSummaryHeading = isProductOrderingList
        ? String(list.dataset.swishcbProductOrderSummaryHeading || '').trim()
        : '';
      if (productOrderSummaryHeading) {
        var productOrderSummaryTitle = document.createElement('h2');
        productOrderSummaryTitle.className = 'swishcb__product-order-summary-heading';
        productOrderSummaryTitle.textContent = productOrderSummaryHeading;
        fragment.appendChild(productOrderSummaryTitle);
      }
      specs.forEach(function (spec) {
        var item = document.createElement('div');
        item.className = 'swishcb__order-specification';
        if (isProductOrderingList && spec.label === 'Tiles') item.dataset.swishcbSummaryTiles = 'true';
        if (!spec.hideHeading) {
          var label = document.createElement(isProductOrderingList && list.closest('[data-swishcb-configurator="cards"]') ? 'h3' : 'strong');
          label.className = 'swishcb__order-specification-heading';
          label.textContent = spec.label + (Array.isArray(spec.values) ? '' : ':');
          if (spec.headingAction) {
            var headingRow = document.createElement('div');
            headingRow.className = 'swishcb__order-specification-heading-row';
            headingRow.appendChild(label);
            appendOrderSpecificationValue(headingRow, spec.headingAction, list, '');
            item.appendChild(headingRow);
          } else {
            item.appendChild(label);
          }
        }
        if (Array.isArray(spec.values)) {
          var nested = document.createElement('ul');
          nested.className = 'swishcb__order-specification-values';
          spec.values.forEach(function (value, valueIndex) {
            var nestedItem = document.createElement('li');
            if (
              isProductOrderingList
              && Array.isArray(spec.groupStarts)
              && spec.groupStarts[valueIndex]
            ) {
              nestedItem.classList.add('swishcb__order-artwork-group-start');
            }
            var editableOverhang = isProductOrderingList && Array.isArray(spec.editableOverhangs)
              ? spec.editableOverhangs[valueIndex]
              : null;
            var editableDimension = isProductOrderingList && Array.isArray(spec.editableDimensions)
              ? spec.editableDimensions[valueIndex]
              : null;
            if (isProductOrderingList && value && value.type === 'tile-series-selector') {
              nestedItem.className = 'swishcb__product-order-tile-series-item';
              var seriesRow = document.createElement('span');
              seriesRow.className = 'swishcb__order-series-row';
              seriesRow.appendChild(document.createTextNode('Series: '));
              var seriesControls = document.createElement('span');
              seriesControls.className = 'swishcb__order-series-controls';
              seriesRow.appendChild(seriesControls);
              var seriesSelect = document.createElement('select');
              seriesSelect.className = 'swishcb__product-order-series-select';
              seriesSelect.dataset.swishcbProductSeriesSelect = 'true';
              seriesSelect.setAttribute('aria-label', 'Tile series');
              seriesSelect.hidden = true;
              (Array.isArray(value.options) ? value.options : []).forEach(function (seriesOption) {
                var option = document.createElement('option');
                option.value = String(seriesOption && seriesOption.value || '');
                option.textContent = String(seriesOption && seriesOption.label || humanizeId(option.value));
                option.disabled = !!(seriesOption && seriesOption.disabled);
                option.selected = option.value === String(value.value || '');
                seriesSelect.appendChild(option);
              });
              seriesControls.appendChild(seriesSelect);
              var seriesButton = document.createElement('button');
              seriesButton.type = 'button';
              seriesButton.className = 'swishcb__order-series-value';
              seriesButton.dataset.swishcbProductSeriesTrigger = 'true';
              seriesButton.textContent = SERIES_LABELS[value.value] || humanizeId(value.value);
              seriesButton.setAttribute('aria-label', 'Change tile series, currently ' + seriesButton.textContent);
              seriesButton.setAttribute('aria-haspopup', 'dialog');
              seriesButton.setAttribute('aria-expanded', 'false');
              seriesControls.appendChild(seriesButton);
              var seriesEdit = seriesButton.cloneNode();
              seriesEdit.className = 'swishcb__order-edit';
              seriesEdit.textContent = 'Edit';
              seriesControls.appendChild(seriesEdit);
              if (summarySurfaceToggle) seriesControls.appendChild(summarySurfaceToggle);
              nestedItem.appendChild(seriesRow);
            } else if (editableDimension) {
              appendProductDimensionEditor(nestedItem, editableDimension, editableDimension.label + ': ');
            } else if (editableOverhang) {
              var overhangLabel = String(editableOverhang.label || (humanizeId(editableOverhang.side) + ' hoop')).trim();
              if (editableOverhang.emphasizeLabel) {
                var overhangStrong = document.createElement('strong');
                overhangStrong.className = 'swishcb__order-inline-label';
                overhangStrong.textContent = overhangLabel + ':';
                nestedItem.appendChild(overhangStrong);
                nestedItem.appendChild(document.createTextNode(' '));
                appendProductOverhangEditor(nestedItem, editableOverhang, '');
              } else {
                appendProductOverhangEditor(nestedItem, editableOverhang, overhangLabel + ': ');
              }
            } else if (value && typeof value === 'object' && value.hex) {
              nestedItem.className = 'swishcb__order-color-item';
              var colorRow = document.createElement('span');
              colorRow.className = 'swishcb__order-color-row';
              var colorText = document.createElement('span');
              colorText.className = 'swishcb__order-color-text';
              if (isProductOrderingList) {
                colorText.appendChild(document.createTextNode(String(value.label || '') + ': '));
                var colorHex = document.createElement('button');
                colorHex.type = 'button';
                colorHex.className = 'swishcb__order-color-hex';
                colorHex.textContent = value.hex;
                colorHex.dataset.swishcbOrderColorSwatch = 'true';
                colorHex.dataset.swishcbOrderColorTargetKind = value.targetKind || '';
                colorHex.dataset.swishcbOrderColorTargetId = value.targetId || '';
                colorHex.setAttribute('aria-label', 'Change ' + value.label + ' color, currently ' + value.hex);
                colorText.appendChild(colorHex);
              } else {
                colorText.textContent = colorItemText(value);
              }
              var swatch = document.createElement('button');
              swatch.type = 'button';
              swatch.className = 'swishcb__order-color-swatch';
              swatch.style.backgroundColor = value.hex;
              swatch.dataset.swishcbOrderColorSwatch = 'true';
              swatch.dataset.swishcbOrderColorTargetKind = value.targetKind || '';
              swatch.dataset.swishcbOrderColorTargetId = value.targetId || '';
              swatch.setAttribute('aria-label', 'Change ' + value.label + ' color, currently ' + value.hex);
              colorRow.appendChild(colorText);
              if (isProductOrderingList) {
                colorText.appendChild(swatch);
              } else {
                colorRow.appendChild(swatch);
              }
              var colorEdit = document.createElement('button');
              colorEdit.type = 'button';
              colorEdit.className = 'swishcb__order-edit';
              colorEdit.textContent = 'Edit';
              colorEdit.dataset.swishcbOrderColorSwatch = 'true';
              colorEdit.dataset.swishcbOrderColorEdit = 'true';
              colorEdit.dataset.swishcbOrderColorTargetKind = value.targetKind || '';
              colorEdit.dataset.swishcbOrderColorTargetId = value.targetId || '';
              colorEdit.setAttribute('aria-label', 'Edit ' + value.label + ' color');
              colorEdit.setAttribute('aria-haspopup', 'dialog');
              (isProductOrderingList ? colorText : colorRow).appendChild(colorEdit);
              nestedItem.appendChild(colorRow);
            } else {
              appendOrderSpecificationValue(nestedItem, value, list, '');
            }
            nested.appendChild(nestedItem);
          });
          item.appendChild(nested);
          if (spec.addArtworkAction) {
            var artworkAction = document.createElement('div');
            artworkAction.className = 'swishcb__order-artwork-more';
            appendOrderSpecificationValue(artworkAction, spec.addArtworkAction, list, '');
            item.appendChild(artworkAction);
          }
        } else {
          var valueText = document.createElement('span');
          valueText.className = 'swishcb__order-specification-value';
          var singleOverhang = isProductOrderingList && Array.isArray(spec.editableOverhangs)
            ? spec.editableOverhangs[0]
            : null;
          if (singleOverhang) appendProductOverhangEditor(valueText, singleOverhang, ' ');
          else appendOrderSpecificationValue(valueText, spec.value, list, ' ');
          item.appendChild(valueText);
        }
        if (item.hasAttribute('data-swishcb-summary-tiles') && summarySurfacePanel) item.appendChild(summarySurfacePanel);
        fragment.appendChild(item);
      });
      // Keep the original ordering panel connected even during an empty/loading
      // summary. Its information media, configuration and handlers are reused.
      if (summarySurfacePanel && !fragment.contains(summarySurfacePanel)) fragment.appendChild(summarySurfacePanel);
      if (summarySurfaceToggle && !fragment.contains(summarySurfaceToggle)) {
        var surfaceInformation = summarySurfacePanel.querySelector('.swishcb__flooring-information');
        if (surfaceInformation) surfaceInformation.before(summarySurfaceToggle);
        else summarySurfacePanel.appendChild(summarySurfaceToggle);
      }
      if (isProductOrderingList) {
        var builder = list.closest('#swish-court-builder');
        if (builder && !builder.classList.contains('swishcb--preview-order-card')) {
          // Retain the same live image and zoom control across summary rebuilds.
          // This is visual summary content, not an extra checkout specification.
          var designGroup = builder.querySelector('[data-swishcb-order-design]');
          if (designGroup) {
            fragment.insertBefore(designGroup, fragment.querySelector('.swishcb__order-specification'));
          }
        }
      }
      list.replaceChildren(fragment);
      if (focusedSurfaceToggle && summarySurfaceToggle && !summarySurfaceToggle.hidden) {
        summarySurfaceToggle.focus({ preventScroll: true });
      }
      if (focusedSeriesControl) {
        var replacementSeriesControl = Array.from(list.querySelectorAll('[data-swishcb-product-series-trigger]')).find(function (button) { return button.className === focusedSeriesControl; });
        if (replacementSeriesControl) replacementSeriesControl.focus({ preventScroll: true });
      }
      // Color commits can finish after Escape has returned focus to a summary
      // button. Preserve that focus when the updated row replaces the button.
      if (focusedColorControl) {
        var replacementColorControl = Array.from(list.querySelectorAll('[data-swishcb-order-color-swatch]')).find(function (button) {
          return button.dataset.swishcbOrderColorTargetKind === focusedColorControl.kind
            && button.dataset.swishcbOrderColorTargetId === focusedColorControl.id
            && button.className === focusedColorControl.className;
        });
        if (replacementColorControl) replacementColorControl.focus({ preventScroll: true });
      }
      if (isProductOrderingList) {
        Array.prototype.forEach.call(
          list.querySelectorAll('[data-swishcb-product-overhang-input], [data-swishcb-product-dimension-input]'),
          syncProductMeasurementEditorWidth
        );
      }
      list.dataset.swishcbSummarySignature = signature;
    }
    list.hidden = !specs.length;
  }

  function updateOrderSummary(panel, summary) {
    if (!panel || !summary) return;
    var title = panel.querySelector('[data-swishcb-order-generated-title]');
    var series = panel.querySelector('[data-swishcb-order-series]');
    var description = panel.querySelector('[data-swishcb-order-description]');
    var list = panel.querySelector('[data-swishcb-order-specifications]');
    var seriesList = panel.querySelector('[data-swishcb-order-series-specifications]');
    var root = panel.closest('#swish-court-builder');
    var productList = root ? root.querySelector('[data-swishcb-product-order-specifications]') : null;
    var hideCompactV2CustomizationSummary = !!(root && root.classList.contains('swishcb--hide-primary-builder-panels'));
    var mainSpecs = summary.specs.filter(function (spec) { return spec.placement !== 'series'; });
    var seriesSpecs = summary.specs.filter(function (spec) { return spec.placement === 'series'; });
    var productSport = normalizeSport(root && root.querySelector('#court-xxx') ? root.querySelector('#court-xxx').value : '');
    var basketballProductSports = ['basketball', 'fiba', 'nba', 'ncaa', 'usahs', 'us-highschool'];
    var isBasketballProduct = basketballProductSports.indexOf(productSport) >= 0;
    var sourceCourtDimensions = mainSpecs.find(function (spec) { return String(spec.label || '') === 'Court dimensions'; });
    var sourceKeyDimensions = mainSpecs.find(function (spec) { return String(spec.label || '') === 'Key dimensions'; });
    var sourceThreePoint = mainSpecs.find(function (spec) { return String(spec.label || '') === '3-point line'; });
    var sourceHoopOverhang = mainSpecs.find(function (spec) {
      return ['Hoop overhang', 'Hoop overhangs'].indexOf(String(spec.label || '')) >= 0;
    });
    var sourceColors = mainSpecs.find(function (spec) { return String(spec.label || '') === 'Colors'; });
    var productDimensionValues = [];
    var productEditableDimensions = [];
    var productEditableOverhangs = [];
    var hasProductTotalWidth = false;
    var hasProductTotalLength = false;
    if (sourceCourtDimensions && Array.isArray(sourceCourtDimensions.values)) {
      sourceCourtDimensions.values.forEach(function (value, index) {
        var textValue = String(value || '');
        if (textValue.indexOf('Total court width:') !== 0 && textValue.indexOf('Total court length:') !== 0) return;
        if (textValue.indexOf('Total court width:') === 0) hasProductTotalWidth = true;
        if (textValue.indexOf('Total court length:') === 0) hasProductTotalLength = true;
        productDimensionValues.push(value);
        productEditableDimensions.push(Array.isArray(sourceCourtDimensions.editableDimensions)
          ? sourceCourtDimensions.editableDimensions[index]
          : null);
        productEditableOverhangs.push(null);
      });
    }
    if (isBasketballProduct && sourceHoopOverhang) {
      var sourceOverhangValues = Array.isArray(sourceHoopOverhang.values)
        ? sourceHoopOverhang.values
        : [String(sourceHoopOverhang.label || 'Hoop overhang') + ': ' + String(sourceHoopOverhang.value || '')];
      sourceOverhangValues.forEach(function (value, index) {
        var editableOverhang = Array.isArray(sourceHoopOverhang.editableOverhangs)
          ? sourceHoopOverhang.editableOverhangs[index]
          : null;
        var overhangLabel = sourceOverhangValues.length === 1
          ? 'Hoop overhang'
          : (editableOverhang && editableOverhang.side
            ? humanizeId(editableOverhang.side) + ' hoop overhang'
            : 'Hoop overhang');
        productDimensionValues.push(value);
        productEditableDimensions.push(null);
        productEditableOverhangs.push(editableOverhang
          ? Object.assign({}, editableOverhang, { label: overhangLabel, emphasizeLabel: false })
          : null);
      });
    }
    if (isBasketballProduct && sourceKeyDimensions && Array.isArray(sourceKeyDimensions.values)) {
      sourceKeyDimensions.values.forEach(function (value, index) {
        productDimensionValues.push(value);
        productEditableDimensions.push(Array.isArray(sourceKeyDimensions.editableDimensions)
          ? sourceKeyDimensions.editableDimensions[index]
          : null);
        productEditableOverhangs.push(null);
      });
    }
    if (isBasketballProduct && sourceThreePoint && sourceThreePoint.value) {
      productDimensionValues.push('3-point line: ' + sourceThreePoint.value);
      productEditableDimensions.push(sourceThreePoint.editableDimension || null);
      productEditableOverhangs.push(null);
    }
    var productSpecs = [];
    if (productDimensionValues.length) {
      productSpecs.push({
  label: 'Measurements',
        values: productDimensionValues,
        editableDimensions: productEditableDimensions,
        editableOverhangs: productEditableOverhangs
      });
    }
    if (sourceColors) {
      var productColors = Object.assign({}, sourceColors);
      if (Array.isArray(sourceColors.values)) {
        productColors.values = sourceColors.values.map(function (value) {
          if (!value || typeof value !== 'object' || !value.label) return value;
          var labelMap = {
            'Main court area': 'Main court',
            'Court lines': 'Lines'
          };
          return Object.assign({}, value, { label: labelMap[value.label] || value.label });
        });
      }
      productSpecs.push(productColors);
    }
    // Embedded preview cards keep a compact summary. Standalone builders retain
    // their tile series, dimensions, quantities, and edge specifications.
    var builderSeriesSelect = root && root.querySelector('#swishcb-tile-series');
    var summaryOwnsSurfaceSelection = !!(root && root.matches('[data-swishcb-configurator="cards"]') && !root.classList.contains('swishcb--preview-order-card') && builderSeriesSelect);
    var summaryProductTiles = summary.productTiles;
    if (!summaryProductTiles && summaryOwnsSurfaceSelection) {
      // Quote-only flooring and None still need the same flooring entry point.
      summaryProductTiles = { label: 'Tiles', values: ['Series: ' + (SERIES_LABELS[builderSeriesSelect.value] || humanizeId(builderSeriesSelect.value))] };
    }
    if (summaryProductTiles && !(root && root.classList.contains('swishcb--preview-order-card'))) {
      var productTiles = Object.assign({}, summaryProductTiles);
      var selectedProductSeries = summaryOwnsSurfaceSelection ? builderSeriesSelect.value : getOrderSeriesId(builderSeriesSelect && builderSeriesSelect.value) || 'elastic';
      var productSeriesIds = summaryOwnsSurfaceSelection ? Array.from(builderSeriesSelect.options).map(function (option) { return option.value; }) : ORDER_SERIES_IDS;
      productTiles.values = Array.isArray(summaryProductTiles.values)
        ? summaryProductTiles.values.map(function (value, index) {
          if (index !== 0) return value;
          return {
            type: 'tile-series-selector',
            value: selectedProductSeries,
            options: productSeriesIds.map(function (seriesId) {
              var builderOption = builderSeriesSelect && builderSeriesSelect.querySelector('option[value="' + seriesId + '"]');
              return {
                value: seriesId,
                label: SERIES_LABELS[seriesId] || humanizeId(seriesId),
                disabled: !builderOption || builderOption.disabled || (!summaryOwnsSurfaceSelection && root.dataset.swishcbPickleballSeriesRestricted === 'true' && seriesId !== 'elastic')
              };
            })
          };
        })
        : summaryProductTiles.values;
      if (summaryOwnsSurfaceSelection && productTiles.values.length > 1) productTiles.values.push(productTiles.values.shift());
      productSpecs.push(productTiles);
    }
    var productLogoSpecs = mainSpecs.filter(function (spec) {
      return /^Logo(?: \d+)?$/.test(String(spec.label || ''));
    });
    var productTextSpecs = mainSpecs.filter(function (spec) {
      return /^Text(?: \d+)?$/.test(String(spec.label || ''));
    });
    var productArtworkValues = [];
    var productArtworkGroupStarts = [];
    if (productLogoSpecs.length) {
      productLogoSpecs.forEach(function (spec, index) {
        var values = Array.isArray(spec.values) ? spec.values : [spec.value];
        values.forEach(function (value, valueIndex) {
          productArtworkGroupStarts.push(index > 0 && valueIndex === 0);
          if (valueIndex === 0 && value && typeof value === 'object' && value.type === 'download') {
            productArtworkValues.push(Object.assign({}, value, { prefix: 'Logo ' + (index + 1) + ': ' }));
          } else if (valueIndex === 0) {
            productArtworkValues.push('Logo ' + (index + 1) + ': ' + orderSpecificationValueText(value));
          } else {
            productArtworkValues.push(value);
          }
        });
      });
      productArtworkValues.push({
        type: 'add-artwork',
        kind: 'logo',
        actionLabel: '+ Add logo',
        showEmptyLabel: false,
        mode: 'new'
      });
      productArtworkGroupStarts.push(false);
    }
    if (!hideCompactV2CustomizationSummary) {
      if (productTextSpecs.length) {
        productTextSpecs.forEach(function (spec, index) {
          var values = Array.isArray(spec.values) ? spec.values : [spec.value];
          values.forEach(function (value, valueIndex) {
            productArtworkValues.push(valueIndex === 0
              ? 'Text ' + (index + 1) + ': ' + orderSpecificationValueText(value)
              : value);
            productArtworkGroupStarts.push(valueIndex === 0);
          });
        });
        productArtworkValues.push({
          type: 'add-artwork',
          kind: 'text',
          actionLabel: '+ Add text',
          showEmptyLabel: false,
          mode: 'new'
        });
        productArtworkGroupStarts.push(false);
      }
    }
    var usesPreviewOrderCard = !!(root && root.classList.contains('swishcb--preview-order-card'));
    if (productArtworkValues.length) productSpecs.push({
      label: usesPreviewOrderCard ? 'Logos' : 'Logos & Text',
      hideHeading: usesPreviewOrderCard ? false : hideCompactV2CustomizationSummary,
      values: productArtworkValues,
      groupStarts: productArtworkGroupStarts
    });
    mainSpecs.forEach(function (spec) {
      var label = String(spec.label || '');
      if (/^Logo(?: \d+)?$/.test(label) || /^Text(?: \d+)?$/.test(label)) return;
      if (['Court dimensions', 'Key dimensions', '3-point line', 'Edges', 'Borders', 'Hoop overhang', 'Hoop overhangs', 'Colors'].indexOf(label) >= 0) return;
      if (!isBasketballProduct && ['Hoop overhang', 'Hoop overhangs'].indexOf(label) >= 0) return;
      productSpecs.push(spec);
    });
    setText(title, summary.title || 'Custom Court');
    setText(series, summary.series || '');
    if (series) series.hidden = !summary.series;
    renderOrderSpecifications(list, mainSpecs, summary.signature + '|main');
    var productSummaryReady = hasProductTotalWidth && hasProductTotalLength;
    var checkoutSpecs = (productList && productSummaryReady ? productSpecs : mainSpecs).slice();
    // Compact page cards omit Tiles on screen, but checkout must retain the
    // selected series, tile quantities and edges just like View details.
    if (summary.productTiles && !checkoutSpecs.some(function (spec) { return spec.label === summary.productTiles.label; })) {
      var artworkIndex = checkoutSpecs.findIndex(function (spec) { return /^Logos/.test(String(spec.label || '')); });
      checkoutSpecs.splice(artworkIndex < 0 ? checkoutSpecs.length : artworkIndex, 0, summary.productTiles);
    }
    summary.checkoutSpecs = checkoutSpecs;
    summary.checkoutDetails = checkoutDetailsFromSpecs(checkoutSpecs);
    summary.checkoutPlainSpecifications = checkoutPlainSpecifications(summary.checkoutDetails);
    renderOrderSpecifications(
      productList,
      productSummaryReady ? productSpecs : [],
      summary.signature + '|product|' + productSport + (productSummaryReady ? '|ready' : '|loading')
    );
    renderOrderSpecifications(seriesList, seriesSpecs, summary.signature + '|series');
    if (description) description.hidden = !mainSpecs.length;
  }

  function updateVariantUi(panel, result) {
    var variant = selectedVariantData(panel);
    var currentPrice = panel.querySelector('[data-swishcb-order-current-price]');
    var comparePrice = panel.querySelector('[data-swishcb-order-compare-price]');
    var submit = panel.querySelector('[data-swishcb-order-submit]');
    var submitLabel = panel.querySelector('[data-swishcb-order-submit-label]');
    var dynamicCheckout = panel.querySelector('.shopify-payment-button');
    var checkoutBusy = panel.dataset.swishcbCheckoutState === 'loading';
    if (!variant) {
      var resultAllowsSubmit = (!result || result.purchasable) && !checkoutBusy;
      if (submit) {
        submit.disabled = !resultAllowsSubmit;
        submit.setAttribute('aria-disabled', resultAllowsSubmit ? 'false' : 'true');
      }
      if (dynamicCheckout) dynamicCheckout.hidden = !resultAllowsSubmit;
      return;
    }
    if (currentPrice && variant.price) setText(currentPrice, variant.price);
    if (comparePrice) {
      setText(comparePrice, variant.comparePrice || '');
      comparePrice.hidden = !variant.comparePrice;
    }
    var canSubmit = variant.available && (!result || result.purchasable) && !checkoutBusy;
    if (submit) {
      submit.disabled = !canSubmit;
      submit.setAttribute('aria-disabled', canSubmit ? 'false' : 'true');
    }
    if (submitLabel && !checkoutBusy) {
      setText(submitLabel, variant.available
        ? (panel.dataset.swishcbOrderAddLabel || 'Order')
        : 'Sold out');
    }
    if (dynamicCheckout) dynamicCheckout.hidden = !canSubmit;
  }

  function applyQuantityRule(panel, quantity) {
    var input = panel.querySelector('[data-swishcb-order-quantity]');
    var target = toNumber(quantity);
    if (!input || target == null || target <= 0) return;
    var min = toNumber(input.min) || 1;
    var max = toNumber(input.max);
    var step = toNumber(input.step) || 1;
    target = Math.max(min, target);
    if (max != null) target = Math.min(max, target);
    target = min + Math.round((target - min) / step) * step;
    input.value = String(round(target, 4));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function evaluate(root, panel, dimensions) {
    var payload = getBuilderPayload(root);
    var hardwoodContactOnly = isHardwoodSeriesSelected(root, payload);
    var liveConfig = readConfig(root) || {};
    var installedMeta = liveConfig.installedSurfaceM && typeof liveConfig.installedSurfaceM === 'object'
      ? liveConfig.installedSurfaceM
      : {};
    var tileFootprint = payload && payload.tileFootprintM && typeof payload.tileFootprintM === 'object'
      ? payload.tileFootprintM
      : {};
    var footprintWidthM = positiveNumber(installedMeta.footprintWidth)
      || positiveNumber(tileFootprint.width)
      || positiveNumber(payload && payload.widthM)
      || (dimensions && positiveNumber(dimensions.widthM));
    var footprintLengthM = positiveNumber(installedMeta.footprintLength)
      || positiveNumber(tileFootprint.length)
      || positiveNumber(payload && payload.lengthM)
      || (dimensions && positiveNumber(dimensions.lengthM));
    var playableDimensions = resolvePlayableDimensions(root, dimensions, payload);
    var playableWidthM = playableDimensions && positiveNumber(playableDimensions.widthM);
    var playableLengthM = playableDimensions && positiveNumber(playableDimensions.lengthM);
    var installedDimensions = resolveInstalledDimensions(root, dimensions, payload);
    var widthM = installedDimensions && positiveNumber(installedDimensions.widthM);
    var lengthM = installedDimensions && positiveNumber(installedDimensions.lengthM);
    var areaM2 = widthM && lengthM ? widthM * lengthM : null;
    var customSurface = window.SwishCBCustomSurface && window.SwishCBCustomSurface.resolve(
      liveConfig.customSurface || (payload && payload.customSurface), lengthM, widthM
    );
    if (customSurface) areaM2 = window.SwishCBCustomSurface.area(customSurface, lengthM, widthM);
    var minAreaM2 = toNumber(panel.dataset.swishcbOrderMinAreaM2);
    var maxAreaM2 = toNumber(panel.dataset.swishcbOrderMaxAreaM2);
    if (minAreaM2 == null) minAreaM2 = 0;
    // Zero means no area limit; selected edge ramps remain part of installed dimensions.
    if (maxAreaM2 == null || maxAreaM2 < 0) maxAreaM2 = 0;
    var sport = readSport(root);
    var productReady = panel.dataset.swishcbOrderProductReady === 'true';
    var result = {
      eligible: !!(areaM2 != null && areaM2 >= minAreaM2 && (maxAreaM2 === 0 || areaM2 < maxAreaM2)),
      purchasable: false,
      reason: 'eligible',
      productReady: productReady,
      widthM: widthM,
      lengthM: lengthM,
      installedWidthM: widthM,
      installedLengthM: lengthM,
      footprintWidthM: footprintWidthM,
      footprintLengthM: footprintLengthM,
      playableWidthM: playableWidthM,
      playableLengthM: playableLengthM,
      areaM2: areaM2,
      minAreaM2: minAreaM2,
      maxAreaM2: maxAreaM2,
      sport: sport,
      matchedRule: null,
      variantId: null,
      quantity: null,
      payload: payload,
      hardwoodContactOnly: hardwoodContactOnly,
      customSurfaceQuote: !!customSurface,
      artworkPlacements: getArtworkPlacements(payload)
    };

    if (areaM2 == null) result.reason = 'dimensions_unavailable';
    else if (areaM2 < minAreaM2) result.reason = 'below_minimum';
    else if (maxAreaM2 > 0 && areaM2 >= maxAreaM2) result.reason = 'area_limit';

    var matchedRule = areaM2 == null ? null : findMatchingRule(panel, sport, areaM2);
    if (matchedRule) {
      result.matchedRule = matchedRule;
      if (matchedRule.eligible === false) {
        result.eligible = false;
        result.reason = matchedRule.reason || 'rule_requires_quote';
      } else {
        result.eligible = true;
        result.reason = 'eligible';
      }
      result.variantId = matchedRule.variantId || matchedRule.variant_id || null;
      result.quantity = matchedRule.quantity || null;
    }

    var api = window.SwishCB && window.SwishCB.ordering;
    var resolvers = api && Array.isArray(api.ruleResolvers) ? api.ruleResolvers : [];
    resolvers.forEach(function (resolver) {
      try {
        var override = resolver(Object.assign({}, result), root, panel);
        if (override && typeof override === 'object') result = Object.assign(result, override);
      } catch (_) {}
    });

    // Hardwood courts always require a custom quote. Apply this after every
    // generic rule resolver so integrations cannot accidentally re-enable
    // calculated checkout for any Hardwood variant.
    if (hardwoodContactOnly) {
      result.eligible = false;
      result.reason = 'hardwood_contact_only';
      result.variantId = null;
      result.quantity = null;
    }

    // The checkout service prices rectangles. A cut surface needs a quote so
    // its full bounding rectangle is never sold as the customer's exact floor.
    if (customSurface) {
      result.customSurfaceQuote = true;
      result.eligible = false;
      result.reason = 'custom_surface_quote';
      result.variantId = null;
      result.quantity = null;
    }
    if (result.eligible && result.productReady && result.variantId && !selectRuleVariant(panel, result.variantId)) {
      result.eligible = false;
      result.reason = 'variant_unavailable';
    }
    if (result.quantity != null) applyQuantityRule(panel, result.quantity);
    var selectedVariant = selectedVariantData(panel);
    result.purchasable = !!(
      result.eligible &&
      result.productReady &&
      (!selectedVariant || selectedVariant.available)
    );
    updateVariantUi(panel, result);
    return result;
  }

  function setPanelVisible(panel, visible) {
    if (!panel) return;
    // In the full configurator this is the material card, not the checkout.
    // Keep its original selector usable when a quote-only surface is selected.
    if (panel.matches('[data-swishcb-order-panel]') && panel.closest('[data-swishcb-configurator="cards"]')) {
      visible = true;
      panel.open = true;
    }
    panel.hidden = !visible;
    panel.setAttribute('aria-hidden', visible ? 'false' : 'true');
    if (visible) panel.removeAttribute('inert');
    else panel.setAttribute('inert', '');
  }

  function focusedControlScrollContainer(control) {
    if (!control || typeof window === 'undefined') return null;
    var ancestor = control.parentElement;
    while (ancestor && ancestor !== document.body && ancestor !== document.documentElement) {
      var style = window.getComputedStyle(ancestor);
      var overflowY = String(style && style.overflowY || '');
      if (/(auto|scroll|overlay)/.test(overflowY) && ancestor.scrollHeight > ancestor.clientHeight + 1) {
        return ancestor;
      }
      ancestor = ancestor.parentElement;
    }
    return document.scrollingElement || document.documentElement;
  }

  function captureFocusedControlAnchor(root) {
    if (!root || typeof document === 'undefined') return null;
    var control = document.activeElement;
    if (!control || !root.contains(control) || !control.matches) return null;
    if (!control.matches('input, textarea, select, [contenteditable="true"]')) return null;
    var scroller = focusedControlScrollContainer(control);
    if (!scroller) return null;
    var controlRect = control.getBoundingClientRect();
    var documentScroller = scroller === document.scrollingElement || scroller === document.documentElement || scroller === document.body;
    var scrollerTop = documentScroller ? 0 : scroller.getBoundingClientRect().top;
    return {
      control: control,
      scroller: scroller,
      documentScroller: documentScroller,
      viewportOffset: controlRect.top - scrollerTop
    };
  }

  function restoreFocusedControlAnchor(anchor) {
    if (!anchor || !anchor.control || !anchor.control.isConnected) return;
    if (document.activeElement !== anchor.control) return;
    var currentRect = anchor.control.getBoundingClientRect();
    var scrollerTop = anchor.documentScroller ? 0 : anchor.scroller.getBoundingClientRect().top;
    var currentOffset = currentRect.top - scrollerTop;
    var delta = currentOffset - anchor.viewportOffset;
    if (!Number.isFinite(delta) || Math.abs(delta) < 0.5) return;
    if (anchor.documentScroller) {
      window.scrollBy(0, delta);
    } else {
      anchor.scroller.scrollTop += delta;
    }
  }

  function stabilizeFocusedControl(anchor) {
    if (!anchor) return;
    restoreFocusedControlAnchor(anchor);
    if (typeof window.requestAnimationFrame === 'function') {
      var settledScrollPosition = anchor.documentScroller
        ? (window.scrollY || window.pageYOffset || 0)
        : anchor.scroller.scrollTop;
      window.requestAnimationFrame(function () {
        var currentScrollPosition = anchor.documentScroller
          ? (window.scrollY || window.pageYOffset || 0)
          : anchor.scroller.scrollTop;
        // A wheel/touch scroll can happen between the synchronous refresh and
        // this frame. Never interpret that user movement as a layout shift or
        // the delayed anchor restore will pull the controls back and flicker.
        if (Math.abs(currentScrollPosition - settledScrollPosition) >= 0.5) return;
        restoreFocusedControlAnchor(anchor);
      });
    }
  }

  function setElementVisible(element, visible) {
    if (!element) return;
    element.hidden = !visible;
    element.setAttribute('aria-hidden', visible ? 'false' : 'true');
    if (visible) element.removeAttribute('inert');
    else element.setAttribute('inert', '');
  }

  function setContactContentVisible(root, visible) {
    if (!root || !root.querySelectorAll) return;
    Array.prototype.forEach.call(
      root.querySelectorAll('[data-swishcb-contact-content], [data-swishcb-contact-form], #SwishContactPanel'),
      function (element) { setElementVisible(element, visible); }
    );
  }

  function resolveOrderMode(root, eligible, contactOnly) {
    var state = getState(root);
    if (contactOnly) {
      state.orderMode = 'contact';
      root.dataset.swishcbOrderRequestedMode = 'contact';
    }
    var storedMode = String(root.dataset.swishcbOrderRequestedMode || '');
    var selectedModeControl = root.querySelector('.swishcb__mode-option[data-swishcb-order-mode][aria-checked="true"]');
    var selectedMode = selectedModeControl && selectedModeControl.getAttribute('data-swishcb-order-mode') === 'order' ? 'order' : 'contact';
    var requestedMode = !contactOnly && (storedMode === 'order' || state.orderMode === 'order' || selectedMode === 'order')
      ? 'order'
      : 'contact';
    state.orderMode = requestedMode;
    root.dataset.swishcbOrderRequestedMode = requestedMode;
    var mode = eligible ? requestedMode : 'contact';
    Array.prototype.forEach.call(root.querySelectorAll('.swishcb__mode-option[data-swishcb-order-mode]'), function (control) {
      var selected = control.getAttribute('data-swishcb-order-mode') === mode;
      control.setAttribute('aria-checked', selected ? 'true' : 'false');
      control.classList.toggle('is-selected', selected);
    });
    return mode;
  }

  function setOrderModeLoading(root, loading) {
    if (!root) return;
    setElementVisible(root.querySelector('[data-swishcb-order-mode-loading]'), !!loading);
  }

  function setOrderProperty(panel, key, value) {
    var input = panel.querySelector('[data-swishcb-order-property="' + key + '"]');
    if (!input) return;
    input.value = value == null ? '' : String(value);
    input.setAttribute('value', input.value);
  }

  function formatArea(areaM2) {
    if (areaM2 == null) return '';
    try {
      return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(round(areaM2, 2));
    } catch (_) {
      return String(round(areaM2, 2));
    }
  }

  function formatNumber(value, maximumFractionDigits) {
    try {
      return new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: maximumFractionDigits == null ? 2 : maximumFractionDigits
      }).format(Number(value));
    } catch (_) {
      return String(round(Number(value), maximumFractionDigits == null ? 2 : maximumFractionDigits));
    }
  }

  function formatMoney(value, currencyCode) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(Number(value));
    } catch (_) {
      return String(currencyCode || '') + ' ' + Number(value).toFixed(2);
    }
  }

  function normalizeHoopPurchasePreset(value) {
    return String(value || '').trim().toUpperCase();
  }

  function getHoopPurchaseUi(root) {
    return root && root.querySelector ? root.querySelector('[data-swishcb-hoop-purchase]') : null;
  }

  function getHoopPurchaseCatalog(root) {
    var state = getState(root);
    if (state.hoopPurchaseCatalog) return state.hoopPurchaseCatalog;
    var script = root.querySelector('[data-swishcb-hoop-purchase-catalog]');
    var catalog = { defaultPreset: 'CV72', currencyCode: 'EUR', priceCountry: 'DE', products: [] };
    if (script) {
      try { catalog = Object.assign(catalog, JSON.parse(script.textContent || '{}')); } catch (_) {}
    }
    catalog.products = Array.isArray(catalog.products) ? catalog.products.filter(function (product) {
      return product && product.preset && product.handle && product.variantId;
    }).map(function (product) {
      return Object.assign({}, product, { preset: normalizeHoopPurchasePreset(product.preset) });
    }) : [];
    state.hoopPurchaseCatalog = catalog;
    return catalog;
  }

  function findHoopPurchaseProduct(root, preset) {
    var normalized = normalizeHoopPurchasePreset(preset);
    return getHoopPurchaseCatalog(root).products.find(function (product) {
      return product.preset === normalized;
    }) || null;
  }

  function isHoopPurchaseActive(root) {
    var ui = getHoopPurchaseUi(root);
    return !!(ui
      && ui.dataset.swishcbHoopPurchaseEligible === 'true'
      && ui.dataset.swishcbHoopPurchaseActive === 'true');
  }

  function getHoopPurchaseQuantity(root, result) {
    var payload = result && result.payload ? result.payload : getBuilderPayload(root);
    var count = getActiveHoopSides(root, payload).length;
    return Math.min(4, count);
  }

  function getHoopPurchaseSelection(root, result) {
    if (!isHoopPurchaseActive(root)) return null;
    var ui = getHoopPurchaseUi(root);
    var select = ui && ui.querySelector('[data-swishcb-hoop-purchase-select]');
    var product = findHoopPurchaseProduct(root, select ? select.value : '');
    if (!product) return null;
    var state = getState(root);
    var liveProduct = state.hoopPurchaseProducts && state.hoopPurchaseProducts[product.preset];
    var priceMinor = Number(liveProduct && liveProduct.priceMinor);
    if (!Number.isFinite(priceMinor) || priceMinor < 0) priceMinor = Number(product.priceMinor);
    if (!Number.isFinite(priceMinor) || priceMinor < 0) return null;
    var quantity = getHoopPurchaseQuantity(root, result);
    return {
      preset: product.preset,
      handle: product.handle,
      title: String((liveProduct && liveProduct.title) || product.title || product.preset),
      variantId: String((liveProduct && liveProduct.variantId) || product.variantId || ''),
      available: liveProduct && typeof liveProduct.available === 'boolean' ? liveProduct.available : true,
      quantity: quantity,
      unitPrice: priceMinor / 100,
      total: (priceMinor / 100) * quantity
    };
  }

  function setHoopPurchaseStatus(root, message) {
    var status = root.querySelector('[data-swishcb-hoop-purchase-status]');
    if (!status) return;
    setText(status, message || '');
    status.hidden = !message;
  }

  function loadHoopPurchaseProduct(root, preset) {
    var product = findHoopPurchaseProduct(root, preset);
    if (!product || typeof window.fetch !== 'function') return Promise.resolve(null);
    var state = getState(root);
    state.hoopPurchaseProducts = state.hoopPurchaseProducts || {};
    state.hoopPurchaseProductPromises = state.hoopPurchaseProductPromises || {};
    if (state.hoopPurchaseProducts[product.preset]) {
      setHoopPurchaseStatus(root, state.hoopPurchaseProducts[product.preset].available ? '' : 'The selected hoop is currently unavailable. Choose another model or uncheck Add a basketball hoop.');
      return Promise.resolve(state.hoopPurchaseProducts[product.preset]);
    }
    if (state.hoopPurchaseProductPromises[product.preset]) {
      return state.hoopPurchaseProductPromises[product.preset];
    }
    var routeRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? String(window.Shopify.routes.root)
      : '/';
    routeRoot = routeRoot.replace(/\/?$/, '/');
    var productUrl = routeRoot + 'products/' + encodeURIComponent(product.handle) + '.js';
    var priceCountry = String(getHoopPurchaseCatalog(root).priceCountry || '').trim().toUpperCase();
    if (/^[A-Z]{2}$/.test(priceCountry)) productUrl += '?country=' + encodeURIComponent(priceCountry);
    // Shopify's fetch wrapper cannot resolve relative requests against an
    // about:srcdoc location. The preview editor supplies the storefront base.
    productUrl = new URL(productUrl, document.baseURI).href;
    var request = window.fetch(productUrl, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('Product request failed.');
      return response.json();
    }).then(function (data) {
      var variants = Array.isArray(data && data.variants) ? data.variants : [];
      var expectedId = String(product.variantId || '').replace(/^gid:\/\/shopify\/ProductVariant\//, '');
      var variant = variants.find(function (item) {
        return String(item && item.id || '') === expectedId;
      }) || variants[0];
      if (!variant) throw new Error('The selected hoop is unavailable.');
      var loaded = {
        title: String(data.title || product.title || product.preset),
        variantId: String(variant.id || expectedId),
        priceMinor: Number(variant.price == null ? data.price : variant.price),
        available: variant.available !== false && data.available !== false
      };
      if (!Number.isFinite(loaded.priceMinor) || loaded.priceMinor < 0) {
        throw new Error('The selected hoop price is unavailable.');
      }
      state.hoopPurchaseProducts[product.preset] = loaded;
      delete state.hoopPurchaseProductPromises[product.preset];
      if (isHoopPurchaseActive(root) && getHoopPurchaseSelection(root).preset === product.preset) {
        setHoopPurchaseStatus(root, loaded.available ? '' : 'The selected hoop is currently unavailable. Choose another model or uncheck Add a basketball hoop.');
        scheduleRefresh(root);
      }
      return loaded;
    }).catch(function () {
      delete state.hoopPurchaseProductPromises[product.preset];
      if (isHoopPurchaseActive(root) && getHoopPurchaseSelection(root).preset === product.preset) setHoopPurchaseStatus(root, 'Live hoop availability will be confirmed at checkout.');
      return null;
    });
    state.hoopPurchaseProductPromises[product.preset] = request;
    return request;
  }

  function findBuilderHoopPresetOption(select, preset) {
    var normalized = normalizeHoopPurchasePreset(preset);
    if (!select || !select.options) return null;
    return Array.prototype.find.call(select.options, function (option) {
      return normalizeHoopPurchasePreset(option.value) === normalized;
    }) || null;
  }

  function applyPurchasedHoopPreset(root, preset) {
    var builderSelect = root.querySelector('#swishcb-hoop-preset');
    var option = findBuilderHoopPresetOption(builderSelect, preset);
    if (!builderSelect || !option) return false;
    var state = getState(root);
    state.hoopPurchaseApplyingPreset = true;
    builderSelect.value = option.value;
    builderSelect.dispatchEvent(new Event('change', { bubbles: true }));
    window.setTimeout(function () { state.hoopPurchaseApplyingPreset = false; }, 0);
    return true;
  }

  function captureHoopPurchaseOriginal(root) {
    var state = getState(root);
    if (state.hoopPurchaseOriginal) return;
    var preset = root.querySelector('#swishcb-hoop-preset');
    var distances = {};
    var payload = getBuilderPayload(root);
    ['left', 'right', 'top', 'bottom'].forEach(function (side) {
      var input = root.querySelector('#swishcb-' + side + '-hoop-distance');
      if (input) distances[side] = {
        raw: input.value,
        unit: String(input.closest('[data-unit]')?.dataset.unit || 'cm'),
        meters: payload && payload.hoopOverhangsM && payload.hoopOverhangsM[side]
      };
    });
    state.hoopPurchaseOriginal = {
      preset: preset ? preset.value : '',
      distances: distances
    };
  }

  function restoreHoopPurchaseOriginal(root) {
    var state = getState(root);
    var contextToken = state.hoopPurchaseContextToken || 0;
    var original = state.hoopPurchaseOriginal;
    state.hoopPurchaseOriginal = null;
    if (!original) return;
    var builderSelect = root.querySelector('#swishcb-hoop-preset');
    var option = findBuilderHoopPresetOption(builderSelect, original.preset);
    state.hoopPurchaseApplyingPreset = true;
    if (builderSelect) {
      builderSelect.value = option ? option.value : '';
      builderSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
    var restoreDistances = function () {
      if ((state.hoopPurchaseContextToken || 0) !== contextToken) return;
      Object.keys(original.distances || {}).forEach(function (side) {
        var input = root.querySelector('#swishcb-' + side + '-hoop-distance');
        if (!input || original.distances[side] == null) return;
        var distance = original.distances[side];
        var unit = String(input.closest('[data-unit]')?.dataset.unit || 'cm');
        var factor = unit === 'ft' ? 1 / 0.3048 : unit === 'in' ? 1 / 0.0254 : unit === 'cm' ? 100 : 1;
        var restoredValue = unit === distance.unit || !Number.isFinite(distance.meters)
          ? distance.raw : formatDecimal(distance.meters * factor, 8);
        input.value = restoredValue;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        // The live renderer formats unfocused inputs. Commit the original
        // precision instead of reading that rounded display value back in.
        input.value = restoredValue;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    };
    window.setTimeout(restoreDistances, 0);
    window.setTimeout(function () {
      if ((state.hoopPurchaseContextToken || 0) !== contextToken) return;
      restoreDistances();
      state.hoopPurchaseApplyingPreset = false;
      scheduleRefresh(root);
    }, 80);
  }

  function syncHoopPurchaseVisibility(root) {
    var ui = getHoopPurchaseUi(root);
    if (!ui) return;
    var active = isHoopPurchaseActive(root);
    var toggle = ui.querySelector('[data-swishcb-hoop-purchase-toggle]');
    var activePanel = ui.querySelector('[data-swishcb-hoop-purchase-active]');
    if (toggle) toggle.checked = active;
    if (activePanel) activePanel.hidden = !active;
    root.querySelectorAll('.swishcb__free-shipping').forEach(function (label) {
      setText(label, 'Free shipping on tiles');
    });
    if (!active) {
      var breakdown = ui.querySelector('[data-swishcb-hoop-price-breakdown]');
      if (breakdown) breakdown.hidden = true;
      root.dataset.swishcbHoopPurchaseTotal = '0';
      delete root.dataset.swishcbHoopPurchasePreset;
      delete root.dataset.swishcbHoopPurchaseQuantity;
    }
  }

  function activateHoopPurchase(root) {
    var ui = getHoopPurchaseUi(root);
    if (!ui || ui.dataset.swishcbHoopPurchaseEligible !== 'true') return;
    if (!getHoopPurchaseQuantity(root)) {
      syncHoopPurchaseVisibility(root);
      return;
    }
    captureHoopPurchaseOriginal(root);
    var catalog = getHoopPurchaseCatalog(root);
    var select = ui.querySelector('[data-swishcb-hoop-purchase-select]');
    var preset = normalizeHoopPurchasePreset(catalog.defaultPreset || 'CV72');
    if (select) select.value = preset;
    ui.dataset.swishcbHoopPurchaseActive = 'true';
    syncHoopPurchaseVisibility(root);
    applyPurchasedHoopPreset(root, preset);
    loadHoopPurchaseProduct(root, preset);
    scheduleRefresh(root);
    scheduleSettledRefresh(root);
  }

  function deactivateHoopPurchase(root, restoreOriginal) {
    var ui = getHoopPurchaseUi(root);
    if (!ui) return;
    ui.dataset.swishcbHoopPurchaseActive = 'false';
    setHoopPurchaseStatus(root, '');
    syncHoopPurchaseVisibility(root);
    if (restoreOriginal !== false) restoreHoopPurchaseOriginal(root);
    else getState(root).hoopPurchaseOriginal = null;
    scheduleRefresh(root);
    scheduleSettledRefresh(root);
  }

  function updateHoopPurchasePricing(root, pricing, result) {
    var ui = getHoopPurchaseUi(root);
    var selection = getHoopPurchaseSelection(root, result);
    var courtTotal = Number(pricing && pricing.total);
    if (!Number.isFinite(courtTotal)) courtTotal = 0;
    if (!selection || !selection.available) {
      if (selection && !selection.available && result) {
        result.purchasable = false;
        result.reason = 'hoop_unavailable';
      }
      if (ui) {
        var emptyBreakdown = ui.querySelector('[data-swishcb-hoop-price-breakdown]');
        if (emptyBreakdown) emptyBreakdown.hidden = true;
      }
      root.dataset.swishcbHoopPurchaseTotal = '0';
      return pricing;
    }
    var currencyCode = String(pricing.currencyCode || getHoopPurchaseCatalog(root).currencyCode || 'EUR').toUpperCase();
    pricing.courtTotal = courtTotal;
    pricing.hoopPreset = selection.preset;
    pricing.hoopQuantity = selection.quantity;
    pricing.hoopUnitPrice = selection.unitPrice;
    pricing.hoopTotal = selection.total;
    pricing.total = window.SwishCBPricing.roundTotal(courtTotal + selection.total);
    root.dataset.swishcbHoopPurchaseTotal = String(round(selection.total, 2));
    root.dataset.swishcbHoopPurchasePreset = selection.preset;
    root.dataset.swishcbHoopPurchaseQuantity = String(selection.quantity);
    if (ui) {
      var breakdown = ui.querySelector('[data-swishcb-hoop-price-breakdown]');
      var courtPrice = ui.querySelector('[data-swishcb-court-tiles-price]');
      var hoopPrice = ui.querySelector('[data-swishcb-hoop-price]');
      var hoopLabel = ui.querySelector('[data-swishcb-hoop-price-label]');
      setText(courtPrice, window.SwishCBPricing.formatTotal(courtTotal, currencyCode) + ' ' + currencyCode);
      setText(hoopPrice, window.SwishCBPricing.formatTotal(selection.total, currencyCode) + ' ' + currencyCode);
      var product = findHoopPurchaseProduct(root, selection.preset);
      setText(hoopLabel, (selection.quantity > 1 ? selection.quantity + ' × ' : '') + (product && product.title || selection.title) + ':');
      if (hoopLabel) {
        var routeRoot = String(window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/').replace(/\/?$/, '/');
        hoopLabel.setAttribute('href', routeRoot + 'products/' + encodeURIComponent(selection.handle));
        if (selection.preset === 'CV72') hoopLabel.setAttribute('aria-haspopup', 'dialog');
        else hoopLabel.removeAttribute('aria-haspopup');
      }
      if (breakdown) breakdown.hidden = false;
    }
    return pricing;
  }

  function getOrderPricingDisplay(pricing, units, seriesId) {
    var useSquareFeet = units === 'ft';
    var areaUnit = useSquareFeet ? 'ft\u00B2' : 'm\u00B2';
    var billableArea = useSquareFeet ? pricing.areaFt2 : pricing.areaM2;
    var unitRate = Number(pricing.unitRate) || 0;
    if (pricing.region === 'usa' && !useSquareFeet) {
      unitRate *= SQUARE_FEET_PER_SQUARE_METRE;
    } else if (pricing.region !== 'usa' && useSquareFeet) {
      unitRate /= SQUARE_FEET_PER_SQUARE_METRE;
    }
    var artworkRate = Number(pricing.artwork && pricing.artwork.ratePerM2) || 0;
    if (useSquareFeet) artworkRate /= SQUARE_FEET_PER_SQUARE_METRE;
    var tileSpec = TILE_SERIES_SPECS[normalizeOrderSeries(seriesId || pricing.series)] || {};
    var tileWidthM = Number(tileSpec.sizeMm && tileSpec.sizeMm[0]) / 1000;
    var tileLengthM = Number(tileSpec.sizeMm && tileSpec.sizeMm[1]) / 1000;
    var tileAreaM2 = tileWidthM > 0 && tileLengthM > 0 ? tileWidthM * tileLengthM : 0;
    var tileArea = useSquareFeet ? tileAreaM2 * SQUARE_FEET_PER_SQUARE_METRE : tileAreaM2;
    return {
      units: units,
      areaUnit: areaUnit,
      billableArea: billableArea,
      unitRate: unitRate,
      artworkRate: artworkRate,
      tileAreaM2: tileAreaM2,
      pricePerTile: unitRate * tileArea
    };
  }

  function getOrderTileCount(result, seriesId) {
    result = result && typeof result === 'object' ? result : {};
    var payload = result.payload && typeof result.payload === 'object' ? result.payload : {};
    var materialCounts = payload.tileMaterialCounts && typeof payload.tileMaterialCounts === 'object'
      ? payload.tileMaterialCounts
      : null;
    var materialFootprint = materialCounts && positiveNumber(materialCounts.footprint);
    if (materialFootprint) return Math.max(1, Math.round(materialFootprint));

    var tileCounts = payload.tileCounts && typeof payload.tileCounts === 'object'
      ? payload.tileCounts
      : null;
    var lengthCount = tileCounts && positiveNumber(tileCounts.length);
    var widthCount = tileCounts && positiveNumber(tileCounts.width);
    if (lengthCount && widthCount) {
      return Math.max(1, Math.round(lengthCount) * Math.round(widthCount));
    }

    var tileSpec = TILE_SERIES_SPECS[normalizeOrderSeries(seriesId || payload.tileSeries)] || {};
    var tileLengthM = Number(tileSpec.sizeMm && tileSpec.sizeMm[0]) / 1000;
    var tileWidthM = Number(tileSpec.sizeMm && tileSpec.sizeMm[1]) / 1000;
    var footprint = payload.tileFootprintM && typeof payload.tileFootprintM === 'object'
      ? payload.tileFootprintM
      : {};
    var footprintLengthM = positiveNumber(footprint.length) || positiveNumber(result.footprintLengthM);
    var footprintWidthM = positiveNumber(footprint.width) || positiveNumber(result.footprintWidthM);
    if (!(tileLengthM > 0) || !(tileWidthM > 0) || !footprintLengthM || !footprintWidthM) return null;
    return Math.max(
      1,
      Math.round(footprintLengthM / tileLengthM) * Math.round(footprintWidthM / tileWidthM)
    );
  }

  function updateOrderPricing(root, panel, result, seriesId) {
    var unitPriceElement = panel.querySelector('[data-swishcb-order-unit-price]');
    var totalElement = panel.querySelector('[data-swishcb-order-price-total]');
    var areaElement = panel.querySelector('[data-swishcb-order-price-area]');
    var artworkElement = panel.querySelector('[data-swishcb-order-artwork-breakdown]');
    var currentProductPrice = panel.querySelector('[data-swishcb-order-current-price]');
    var compareProductPrice = panel.querySelector('[data-swishcb-order-compare-price]');
    if (result.customSurfaceQuote) {
      setText(totalElement, 'Contact us for pricing.');
      setText(unitPriceElement, '');
      [unitPriceElement, areaElement, artworkElement].forEach(function (element) {
        if (!element) return;
        setText(element, '');
        element.hidden = true;
        element.setAttribute('aria-hidden', 'true');
      });
      if (currentProductPrice) setText(currentProductPrice, 'Contact us for pricing.');
      if (compareProductPrice) compareProductPrice.hidden = true;
      delete panel.dataset.swishcbOrderPriceSignature;
      return null;
    }
    if (!getOrderSeriesId(seriesId)) {
      setText(totalElement, '\u2014');
      setText(unitPriceElement, 'Select a court tile series to calculate the total.');
      if (areaElement) {
        setText(areaElement, '');
        areaElement.hidden = true;
        areaElement.setAttribute('aria-hidden', 'true');
      }
      if (artworkElement) {
        setText(artworkElement, '');
        artworkElement.hidden = true;
      }
      if (currentProductPrice) setText(currentProductPrice, '\u2014');
      if (compareProductPrice) compareProductPrice.hidden = true;
      delete panel.dataset.swishcbOrderPriceSignature;
      return null;
    }
    var calculator = window.SwishCBPricing || (window.SwishCB && window.SwishCB.pricing);
    var pricing = null;
    // Price the complete installed footprint: tiles plus each selected edge ramp.
    var pricingWidthM = positiveNumber(result.installedWidthM) || positiveNumber(result.widthM);
    var pricingLengthM = positiveNumber(result.installedLengthM) || positiveNumber(result.lengthM);
    if (calculator && typeof calculator.calculate === 'function') {
      pricing = calculator.calculate(Object.assign({
        countryCode: panel.dataset.swishcbOrderCountryCode || '',
        series: seriesId,
        widthM: pricingWidthM,
        lengthM: pricingLengthM,
        artworkPlacements: result.artworkPlacements || []
      }, getArtworkPricingOptions(panel)));
    }
    if (!pricing) {
      setText(unitPriceElement, '\u2014');
      setText(totalElement, 'Enter your court dimensions to calculate the total.');
      if (areaElement) {
        setText(areaElement, '');
        areaElement.hidden = true;
      }
      if (artworkElement) {
        setText(artworkElement, '');
        artworkElement.hidden = true;
      }
      return null;
    }
    var display = getOrderPricingDisplay(pricing, getOrderDisplayUnits(root, panel), seriesId);
    pricing.displayUnits = display.units;
    pricing.displayAreaUnit = display.areaUnit;
    pricing.displayBillableArea = display.billableArea;
    pricing.displayUnitRate = display.unitRate;
    pricing.tileAreaM2 = display.tileAreaM2;
    pricing.pricePerTile = display.pricePerTile;
    pricing = updateHoopPurchasePricing(root, pricing, result);
    var totalText = window.SwishCBPricing.formatTotal(pricing.total, pricing.currencyCode) + ' ' + String(pricing.currencyCode || '').toUpperCase();
    var tileCount = getOrderTileCount(result, seriesId);
    setText(unitPriceElement, '');
    if (unitPriceElement) {
      unitPriceElement.hidden = true;
      unitPriceElement.setAttribute('aria-hidden', 'true');
    }
    setText(totalElement, totalText);
    if (areaElement) {
      setText(areaElement, '');
      areaElement.removeAttribute('aria-label');
      areaElement.hidden = true;
    }
    if (artworkElement) {
      var artwork = pricing.artwork || {};
      var useUsUnits = display.units === 'ft';
      var artworkText = '';
      if (artwork.placementCount) {
        artworkText = 'Logo/text area: ' + formatArtworkArea(artwork.totalAreaM2, useUsUnits)
          + ' total; ' + formatArtworkArea(artwork.includedAreaM2, useUsUnits) + ' included; '
          + formatArtworkArea(artwork.chargeableAreaM2, useUsUnits) + ' chargeable at '
          + formatMoney(display.artworkRate, pricing.currencyCode) + '/' + display.areaUnit;
        if (artwork.surcharge > 0) artworkText += ' (+' + formatMoney(artwork.surcharge, pricing.currencyCode) + ')';
        else artworkText += ' (no extra charge)';
      }
      setText(artworkElement, '');
      artworkElement.hidden = true;
    }
    if (currentProductPrice) setText(currentProductPrice, totalText);
    if (compareProductPrice) compareProductPrice.hidden = true;
    pricing.signature = JSON.stringify({
      region: pricing.region,
      series: pricing.series,
      area: round(pricing.billableArea, 4),
      band: pricing.bandThreshold,
      rate: pricing.unitRate,
      displayUnits: display.units,
      displayArea: round(display.billableArea, 4),
      displayRate: round(display.unitRate, 6),
      tileAreaM2: round(display.tileAreaM2, 6),
      pricePerTile: round(display.pricePerTile, 6),
      tileCount: tileCount || 0,
      artworkAreaM2: round(pricing.artwork && pricing.artwork.totalAreaM2 || 0, 4),
      artworkSurcharge: pricing.artworkSurcharge || 0,
      hoopPreset: pricing.hoopPreset || '',
      hoopQuantity: pricing.hoopQuantity || 0,
      hoopUnitPrice: pricing.hoopUnitPrice || 0,
      hoopTotal: pricing.hoopTotal || 0,
      total: pricing.total
    });
    if (panel.dataset.swishcbOrderPriceSignature !== pricing.signature) {
      panel.dataset.swishcbOrderPriceSignature = pricing.signature;
      var detail = Object.assign({ root: root }, pricing);
      try { root.dispatchEvent(new CustomEvent('swish:order-price-changed', { detail: detail })); } catch (_) {}
      try { window.dispatchEvent(new CustomEvent('swish:order-price-changed', { detail: detail })); } catch (_) {}
    }
    return pricing;
  }

  function updateStatus(root, orderPanel, contactPanel, result) {
    var orderStatus = orderPanel.querySelector('[data-swishcb-order-status="order"]');
    var contactStatus = contactPanel && contactPanel.querySelector('[data-swishcb-order-status="contact"]');
    var displayUnits = getOrderDisplayUnits(root, orderPanel);
    var useSquareFeet = displayUnits === 'ft';
    var areaUnit = useSquareFeet ? 'ft\u00B2' : 'm\u00B2';
    var canonicalArea = Number(result.areaM2);
    var canonicalMaximumArea = Number(result.maxAreaM2);
    var displayArea = Number.isFinite(canonicalArea)
      ? canonicalArea * (useSquareFeet ? SQUARE_FEET_PER_SQUARE_METRE : 1)
      : null;
    var displayMaximumArea = Number.isFinite(canonicalMaximumArea)
      ? canonicalMaximumArea * (useSquareFeet ? SQUARE_FEET_PER_SQUARE_METRE : 1)
      : null;
    var areaText = formatArea(displayArea);
    var checkoutState = String(orderPanel.dataset.swishcbCheckoutState || '');
    if (orderStatus && checkoutState !== 'loading' && checkoutState !== 'error') {
      var orderMessage = '';
      if (!areaText) {
        orderMessage = 'Enter your court dimensions to check online-order eligibility.';
      } else if (result.seriesRequired) {
        orderMessage = 'Select a court tile series before continuing.';
      } else if (result.customSurfaceQuote) {
        orderMessage = 'This cut-out court requires custom pricing.';
      } else if (result.productReady && !result.purchasable) {
        orderMessage = 'The selected product option is currently unavailable.';
      }
      setText(orderStatus, orderMessage);
      orderStatus.hidden = !orderMessage;
    }
    if (contactStatus) {
      var message = '';
      if (result.reason === 'area_limit' && areaText) {
        message = 'Your court is ' + areaText + ' ' + areaUnit + '. Courts of ' + formatArea(displayMaximumArea) + ' ' + areaUnit + ' or more receive custom pricing.';
      } else if (result.reason === 'below_minimum' && areaText) {
        message = 'Your court is ' + areaText + ' ' + areaUnit + ' and requires custom pricing.';
      } else if (result.reason === 'variant_unavailable' || result.reason === 'rule_requires_quote') {
        message = 'This court configuration requires custom pricing.';
      } else if (result.reason === 'hardwood_contact_only') {
        message = 'Hardwood courts receive custom pricing through the contact form.';
      } else if (result.reason === 'custom_surface_quote') {
        message = 'Cut-out courts receive custom pricing through the contact form.';
      }
      setText(contactStatus, message);
      contactStatus.hidden = !message;
    }
  }

  function syncCartProperties(root, panel, result, summary, pricing) {
    var configInput = root.querySelector('[data-bind="config-json"]');
    var previewInput = root.querySelector('[data-bind="preview-url"]');
    var designInput = root.querySelector('[data-bind="design-2d-url"]');
    setOrderProperty(panel, 'config', configInput ? configInput.value : '');
    setOrderProperty(panel, 'preview_url', previewInput ? previewInput.value : '');
    setOrderProperty(panel, 'design_2d_url', designInput ? designInput.value : '');
    setOrderProperty(panel, 'share_url', summary && summary.shareUrl || String(root.dataset.swishcbOrderSourceUrl || window.location.href));
    setOrderProperty(panel, 'sport', result.sport || '');
    setOrderProperty(panel, 'width_m', result.widthM == null ? '' : round(result.widthM, 4));
    setOrderProperty(panel, 'length_m', result.lengthM == null ? '' : round(result.lengthM, 4));
    setOrderProperty(panel, 'area_m2', result.areaM2 == null ? '' : round(result.areaM2, 4));
    setOrderProperty(panel, 'title', summary ? summary.title : '');
    setOrderProperty(panel, 'series', summary ? summary.series : '');
    setOrderProperty(panel, 'specifications', summary ? (summary.checkoutPlainSpecifications || summary.plainSpecifications) : '');
    setOrderProperty(panel, 'price_currency', pricing ? pricing.currencyCode : '');
    setOrderProperty(panel, 'price_unit', pricing ? (pricing.displayAreaUnit || pricing.areaUnit) : '');
    setOrderProperty(panel, 'price_band', pricing ? pricing.bandThreshold : '');
    setOrderProperty(panel, 'unit_rate', pricing ? (pricing.displayUnitRate == null ? pricing.unitRate : pricing.displayUnitRate) : '');
    setOrderProperty(panel, 'price_per_tile', pricing ? pricing.pricePerTile : '');
    setOrderProperty(panel, 'tile_area_m2', pricing ? pricing.tileAreaM2 : '');
    setOrderProperty(panel, 'artwork_area_m2', pricing && pricing.artwork ? round(pricing.artwork.totalAreaM2, 4) : '');
    setOrderProperty(panel, 'artwork_chargeable_area_m2', pricing && pricing.artwork ? round(pricing.artwork.chargeableAreaM2, 4) : '');
    setOrderProperty(panel, 'artwork_surcharge', pricing ? pricing.artworkSurcharge : '');
    setOrderProperty(panel, 'court_tile_total', pricing ? (pricing.courtTotal == null ? pricing.total : pricing.courtTotal) : '');
    setOrderProperty(panel, 'hoop_model', pricing ? pricing.hoopPreset || '' : '');
    setOrderProperty(panel, 'hoop_quantity', pricing ? pricing.hoopQuantity || '' : '');
    setOrderProperty(panel, 'hoop_total', pricing ? pricing.hoopTotal || '' : '');
    setOrderProperty(panel, 'calculated_total', pricing ? pricing.total : '');
  }

  function createCheckoutRequestId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'swishcb-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
  }

  async function sha256Hex(value) {
    try {
      if (!window.crypto || !window.crypto.subtle || typeof TextEncoder !== 'function') return '';
      var bytes = value instanceof Blob
        ? await value.arrayBuffer()
        : new TextEncoder().encode(String(value == null ? '' : value));
      var digest = await window.crypto.subtle.digest('SHA-256', bytes);
      return Array.prototype.map.call(new Uint8Array(digest), function (byte) {
        return byte.toString(16).padStart(2, '0');
      }).join('');
    } catch (_) {
      return '';
    }
  }

  function validateOrderDesignUrl(value) {
    try {
      var url = new URL(String(value || ''));
      var local = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
      if (url.protocol !== 'https:' && !(local && url.protocol === 'http:')) return '';
      return url.href;
    } catch (_) {
      return '';
    }
  }

  // Bound the entire request, including a stalled response body. Never retry a
  // checkout POST automatically: a timeout can occur after Shopify creates it.
  async function checkoutRequest(endpoint, options, timeoutMs) {
    var controller = new AbortController();
    var timer;
    var deadline = new Promise(function (_, reject) {
      timer = window.setTimeout(function () {
        controller.abort();
        reject(new Error('The connection is taking too long. Please try again; we will check the same checkout.'));
      }, timeoutMs);
    });
    try {
      return await Promise.race([deadline, (async function () {
        var response = await window.fetch(endpoint, Object.assign({}, options, { signal: controller.signal }));
        var body = null;
        try { body = await response.json(); } catch (error) { if (controller.signal.aborted) throw error; }
        return { response: response, body: body };
      })()]);
    } finally { window.clearTimeout(timer); }
  }

  async function boundedCheckoutPreparation(work) {
    var timer;
    try {
      return await Promise.race([work, new Promise(function (_, reject) {
        timer = window.setTimeout(function () {
          reject(new Error('Saving your artwork is taking too long. Please try again once your files have finished uploading.'));
        }, 60000);
      })]);
    } finally { window.clearTimeout(timer); }
  }

  function checkoutAttemptSignature(root, panel, result) {
    return JSON.stringify({ shippingPolicy: 'tile-profile-hoop-rates-v1', paymentPolicy: 'full-payment-only-v1', design: orderDesignSignature(root, panel),
      payment: paymentFor(root, result.pricing), country: panel.dataset.swishcbOrderCountryCode,
      hoop: getHoopPurchaseSelection(root, result), units: getOrderDisplayUnits(root, panel) });
  }

  function rememberCheckoutAttempt(root, endpoint, attempt) {
    getState(root).checkoutAttempt = attempt;
    try { window.sessionStorage.setItem('swishcb.checkout.' + endpoint, JSON.stringify(attempt)); } catch (_) {}
  }

  async function currentCheckoutAttempt(root, panel, result, endpoint) {
    var signature = await sha256Hex(checkoutAttemptSignature(root, panel, result));
    // A hashing failure must not let unrelated designs share a request ID.
    signature = signature || checkoutAttemptSignature(root, panel, result);
    var attempt = getState(root).checkoutAttempt;
    if (!attempt) {
      try { attempt = JSON.parse(window.sessionStorage.getItem('swishcb.checkout.' + endpoint) || 'null'); } catch (_) {}
    }
    if (!attempt || attempt.signature !== signature || Date.now() - attempt.createdAt > 24 * 60 * 60 * 1000) {
      attempt = { signature: signature, requestId: createCheckoutRequestId(), createdAt: Date.now() };
    }
    return attempt;
  }

  async function uploadOrderDesign(root, panel, asset, requestId) {
    if (!asset || !asset.blob || !asset.signature) {
      throw new Error('The current 2D design is unavailable.');
    }
    var state = getState(root);
    if (state.uploadedDesignSignature === asset.signature && state.uploadedDesignUrl) {
      return state.uploadedDesignMeta;
    }
    var endpoint = String(panel.dataset.swishcbOrderDesignUploadEndpoint || '').trim();
    if (!endpoint) throw new Error('The 2D design upload service is unavailable.');
    var checksum = await sha256Hex(asset.blob);
    var configurationHash = await sha256Hex(asset.signature);
    var body = new FormData();
    body.append('file', asset.blob, asset.fileName || 'swish-court-design-2d.png');
    body.append('requestId', requestId);
    body.append('configurationHash', configurationHash);
    if (checksum) body.append('checksum', checksum);
    var uploaded = await checkoutRequest(endpoint, {
      method: 'POST', body: body, credentials: 'omit'
    }, 45000);
    var response = uploaded.response;
    var responseBody = uploaded.body;
    var returnedUrl = responseBody && (
      responseBody.designUrl ||
      responseBody.assetUrl ||
      (responseBody.design && responseBody.design.url)
    );
    var designUrl = validateOrderDesignUrl(returnedUrl);
    if (!response.ok || !responseBody || responseBody.ok === false || !designUrl) {
      throw new Error(responseBody && responseBody.message
        ? String(responseBody.message)
        : 'The 2D design upload service returned HTTP ' + response.status + '.');
    }
    var meta = {
      url: designUrl,
      fileName: String((responseBody.design && responseBody.design.fileName) || asset.fileName || 'swish-court-design-2d.png'),
      mimeType: String((responseBody.design && responseBody.design.mimeType) || asset.mimeType || 'image/png'),
      width: Number(asset.width) || null,
      height: Number(asset.height) || null,
      byteSize: Number((responseBody.design && responseBody.design.byteSize) || asset.blob.size) || null,
      checksum: String((responseBody.design && responseBody.design.checksum) || checksum || ''),
      configurationHash: configurationHash,
      signature: asset.signature
    };
    state.uploadedDesignSignature = asset.signature;
    state.uploadedDesignUrl = designUrl;
    state.uploadedDesignMeta = meta;
    setOrderDesignUrl(root, designUrl);
    setOrderProperty(panel, 'design_2d_url', designUrl);
    return meta;
  }

  async function prepareStableOrderDesign(root, panel, requestId) {
    for (var attempt = 0; attempt < 3; attempt += 1) {
      var asset = await generateOrderDesign(root, panel, { checkoutQuality: true });
      if (!asset) continue;
      var design = await uploadOrderDesign(root, panel, asset, requestId);
      if (asset.signature === orderDesignSignature(root, panel)) return design;
    }
    throw new Error(COURT_CHANGED_DURING_PREPARATION_MESSAGE);
  }

  async function prepareOrderOriginalAssets(root) {
    var payload = getBuilderPayload(root);
    var hasLogos = Array.isArray(payload && payload.logos) && payload.logos.length > 0;
    var customFontKeys = new Set();
    if (Array.isArray(payload && payload.texts)) {
      payload.texts.forEach(function (entry) {
        var dataUrl = String(entry && (entry.customFontDataUrl || entry.fontDataUrl) || '').trim();
        if (!dataUrl) return;
        customFontKeys.add(String(entry.customFontFamily || entry.customFontFileName || dataUrl));
      });
    }
    var hasCustomFonts = customFontKeys.size > 0;
    if (!hasLogos && !hasCustomFonts) return { logoManifestUrl: '', assets: [] };
    if (!window.SwishCourtBuilder || typeof window.SwishCourtBuilder.prepareOrderOriginalAssets !== 'function') {
      throw new Error('Original logo and font storage is still loading. Please try again.');
    }
    var bundle = await window.SwishCourtBuilder.prepareOrderOriginalAssets();
    var assets = bundle && Array.isArray(bundle.assets) ? bundle.assets : [];
    var savedLogoCount = assets.filter(function (asset) { return asset && asset.kind === 'logo-original'; }).length;
    var savedFontCount = assets.filter(function (asset) { return asset && asset.kind === 'font-original'; }).length;
    if (hasLogos && savedLogoCount !== payload.logos.length) {
      throw new Error('Every original logo must be saved before checkout. Please try again.');
    }
    if (hasCustomFonts && savedFontCount !== customFontKeys.size) {
      throw new Error('Every custom font must be saved before checkout. Please try again.');
    }
    return {
      logoManifestUrl: String(bundle && bundle.logoManifestUrl || '').trim(),
      assets: assets
    };
  }

  function checkoutConfiguration(value) {
    var raw = String(value || '').trim();
    if (!raw) return '';
    try {
      var parsed = JSON.parse(raw);
      return JSON.stringify(parsed, function (key, entryValue) {
        var normalizedKey = String(key || '').toLowerCase();
        if (normalizedKey === 'customfontdataurl' || normalizedKey === 'fontdataurl' || normalizedKey === 'fd') {
          return undefined;
        }
        return entryValue;
      });
    } catch (_) {
      return raw;
    }
  }

  function checkoutPayload(root, panel, result, context) {
    context = context || {};
    var configInput = root.querySelector('[data-bind="config-json"]');
    var previewInput = root.querySelector('[data-bind="preview-url"]');
    var summary = result && result.summary;
    var design = context.design || null;
    var originalAssetBundle = context.originalAssetBundle || {};
    var hoopPurchase = getHoopPurchaseSelection(root, result);
    var checkoutDetails = summary && Array.isArray(summary.checkoutDetails)
      ? summary.checkoutDetails
      : checkoutDetailsFromSpecs(summary && (summary.checkoutSpecs || summary.specs));
    return {
      version: '2026-09-01',
      requestId: context.requestId || createCheckoutRequestId(),
      countryCode: panel.dataset.swishcbOrderCountryCode || '',
      sport: result && result.sport ? result.sport : '',
      series: result ? getOrderSeriesId(result.seriesId) : '',
      paymentPlan: 'full',
      displayUnits: getOrderDisplayUnits(root, panel),
      installedWidthM: result.installedWidthM || result.widthM,
      installedLengthM: result.installedLengthM || result.lengthM,
      widthM: result ? (positiveNumber(result.installedWidthM) || result.widthM) : null,
      lengthM: result ? (positiveNumber(result.installedLengthM) || result.lengthM) : null,
      artworkPlacements: result && Array.isArray(result.artworkPlacements) ? result.artworkPlacements.map(function (placement) {
        return {
          kind: placement.kind,
          label: placement.label,
          widthM: placement.widthM,
          heightM: placement.heightM
        };
      }) : [],
      checkoutDetails: checkoutDetails,
      hoopPurchase: hoopPurchase ? {
        preset: hoopPurchase.preset,
        quantity: hoopPurchase.quantity,
        handle: hoopPurchase.handle,
        title: hoopPurchase.title,
        variantId: hoopPurchase.variantId,
        available: hoopPurchase.available,
        unitPrice: hoopPurchase.unitPrice,
        total: hoopPurchase.total
      } : null,
      sourceUrl: String(root.dataset.swishcbOrderSourceUrl || window.location.href),
      shareUrl: summary && summary.shareUrl || String(root.dataset.swishcbOrderSourceUrl || window.location.href),
      previewUrl: previewInput ? previewInput.value : '',
      design2dUrl: design ? design.url : '',
      design2dFileName: design ? design.fileName : '',
      design2dMimeType: design ? design.mimeType : '',
      design2dWidth: design ? design.width : null,
      design2dHeight: design ? design.height : null,
      design2dByteSize: design ? design.byteSize : null,
      design2dChecksum: design ? design.checksum : '',
      design2dConfigurationHash: design ? design.configurationHash : '',
      logoManifestUrl: String(originalAssetBundle.logoManifestUrl || ''),
      originalAssets: Array.isArray(originalAssetBundle.assets) ? originalAssetBundle.assets.map(function (asset) {
        return {
          kind: asset.kind,
          label: asset.label,
          fileName: asset.fileName,
          mimeType: asset.mimeType,
          byteSize: Number(asset.byteSize) || 0,
          checksum: asset.checksum || '',
          url: asset.url
        };
      }) : [],
      configuration: checkoutConfiguration(configInput ? configInput.value : ''),
      title: summary ? summary.title : '',
      specifications: summary ? (summary.checkoutPlainSpecifications || summary.plainSpecifications) : ''
    };
  }

  function setCheckoutLoading(button, loading, panel) {
    if (!button) return;
    var label = button.querySelector('[data-swishcb-order-submit-label]');
    var spinner = button.querySelector('.loading__spinner');
    button.disabled = !!loading;
    button.setAttribute('aria-disabled', loading ? 'true' : 'false');
    button.setAttribute('aria-busy', loading ? 'true' : 'false');
    // Dawn's generic .loading state makes the button text transparent. Keep
    // the explicit checkout status visible instead of replacing it with only
    // a spinner.
    button.classList.remove('loading');
    if (spinner) spinner.classList.add('hidden');
    if (label) {
      setText(label, loading
        ? 'Loading...'
        : (panel.dataset.swishcbOrderAddLabel || 'Order'));
    }
  }

  function setExternalCheckoutLoading(button, loading, wasDisabled) {
    if (!button) return;
    var label = button.querySelector('span') || button;
    var spinner = button.querySelector('.loading__spinner');
    if (!button.dataset.swishcbCheckoutLabel) {
      button.dataset.swishcbCheckoutLabel = String(label.textContent || 'Order Now').trim();
    }
    button.disabled = loading ? true : !!wasDisabled;
    button.classList.remove('loading');
    button.setAttribute('aria-busy', loading ? 'true' : 'false');
    if (loading || wasDisabled) button.setAttribute('aria-disabled', 'true');
    else button.removeAttribute('aria-disabled');
    if (spinner) spinner.classList.add('hidden');
    setText(label, loading ? 'Loading...' : button.dataset.swishcbCheckoutLabel);
  }

  function checkoutErrorMessage(error, responseBody) {
    if (responseBody && responseBody.message) return String(responseBody.message);
    if (error && error.message) return String(error.message);
    return 'Unable to start secure checkout. Please try again.';
  }

  // New court orders are paid in full, including restored and embedded previews.
  function paymentFor(root, pricing) {
    if (!pricing || !Number.isFinite(Number(pricing.total))) return null;
    return window.SwishCBPricing.calculatePayment(pricing.total, pricing.currencyCode, 'full');
  }

  async function startSecureCheckout(root, panel, button) {
    if (!root || !panel || !button || button.disabled || panel.dataset.swishcbCheckoutState === 'loading') return false;
    var result = refresh(root);
    var status = root.querySelector('[data-swishcb-checkout-notice]') || panel.querySelector('[data-swishcb-order-status="order"]');
    if (!result || !result.eligible || !result.purchasable || !result.pricing) {
      if (status) {
        setText(status, result && result.seriesRequired
          ? 'Select a court tile series before continuing.'
          : 'Complete an eligible court design before continuing to checkout.');
        status.hidden = false;
      }
      return false;
    }
    var endpoint = String(panel.dataset.swishcbOrderCheckoutEndpoint || '').trim();
    if (!endpoint) {
      if (status) {
        setText(status, 'Secure checkout is temporarily unavailable. Please contact Swish for assistance.');
        status.hidden = false;
      }
      return false;
    }

    panel.dataset.swishcbCheckoutState = 'loading';
    var payment = paymentFor(root, result.pricing);
    var paymentChoices = root.querySelector('[data-swishcb-payment-options]');
    if (paymentChoices) paymentChoices.disabled = true;
    setCheckoutLoading(button, true, panel);
    if (status) {
      setText(status, 'Preparing your secure Shopify checkout…');
      status.hidden = false;
    }

    var responseBody = null;
    try {
      var attempt = await currentCheckoutAttempt(root, panel, result, endpoint);
      var requestId = attempt.requestId;
      var initialSignature = orderDesignSignature(root, panel);
      // On retry, use the exact verified payload, including stored artwork URLs.
      var preparedAssets = attempt.payload
        ? [{ signature: initialSignature }, null]
        : await boundedCheckoutPreparation((async function () {
          var preparationSignature = orderDesignSignature(root, panel);
          // Persist originals before freezing the checkout image. Uploading a logo
          // can replace its local manifest reference with the permanent URL, which
          // is an intentional signature update rather than a customer design edit.
          // Running these jobs in parallel made that update invalidate the image
          // branch and could exhaust its retries in both builder and preview cards.
          var originalAssets = await prepareOrderOriginalAssets(root);
          if (preparationSignature !== orderDesignSignature(root, panel)) {
            throw new Error(COURT_CHANGED_DURING_PREPARATION_MESSAGE);
          }
          var stableDesign = await prepareStableOrderDesign(root, panel, requestId);
          if (!stableDesign || stableDesign.signature !== preparationSignature) {
            throw new Error(COURT_CHANGED_DURING_PREPARATION_MESSAGE);
          }
          return [stableDesign, originalAssets];
        }()));
      var design = preparedAssets[0];
      var originalAssetBundle = preparedAssets[1];
      result = refresh(root);
      if (!result || !result.eligible || !result.purchasable || !result.pricing) {
        throw new Error('The updated court requires custom pricing and cannot continue to online checkout.');
      }
      if (!design || design.signature !== orderDesignSignature(root, panel)) {
        throw new Error('The court changed while checkout was being prepared. Please try again.');
      }
      var currentPayment = paymentFor(root, result.pricing);
      if (currentPayment.plan !== payment.plan || currentPayment.total !== payment.total) {
        throw new Error('Your court price or payment choice changed. Please review the amount and try again.');
      }
      if (status) setText(status, 'Opening your secure Shopify checkout…');
      var finalSignature = await sha256Hex(checkoutAttemptSignature(root, panel, result)) || checkoutAttemptSignature(root, panel, result);
      if (attempt.payload && finalSignature !== attempt.signature) {
        throw new Error('Your court changed while checkout was being prepared. Please review it and try again.');
      }
      attempt.signature = finalSignature;
      attempt.payload = attempt.payload || checkoutPayload(root, panel, result, {
        requestId: requestId, design: design, originalAssetBundle: originalAssetBundle
      });
      rememberCheckoutAttempt(root, endpoint, attempt);
      var checkoutStarted = performance.now();
      var checkoutResponse = await checkoutRequest(endpoint, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'omit',
        body: JSON.stringify(attempt.payload)
      }, 75000);
      var response = checkoutResponse.response;
      responseBody = checkoutResponse.body;
      window.dispatchEvent(new CustomEvent('swish:checkout-timing', { detail: {
        stage: 'checkout-request', durationMs: Math.round(performance.now() - checkoutStarted)
      } }));
      if (!response.ok || !responseBody || !responseBody.checkoutUrl) {
        throw new Error(responseBody && responseBody.message
          ? String(responseBody.message)
          : 'The secure checkout service returned HTTP ' + response.status + '.');
      }
      {
        var latestResult = refresh(root);
        var latestPayment = latestResult && paymentFor(root, latestResult.pricing);
        if (!latestPayment || latestPayment.plan !== payment.plan || latestPayment.total !== payment.total ||
            design.signature !== orderDesignSignature(root, panel)) {
          throw new Error('Your court changed while checkout was being prepared. Please review it and try again.');
        }
        var confirmed = responseBody.payment;
        if (!confirmed || confirmed.plan !== payment.plan || confirmed.currencyCode !== payment.currencyCode ||
            Math.round(Number(confirmed.dueNow) * 100) !== Math.round(payment.dueNow * 100) ||
            Math.round(Number(confirmed.total) * 100) !== Math.round(payment.total * 100)) {
          throw new Error('Checkout could not confirm your selected payment amount. No payment has been taken. Please try again or contact Swish.');
        }
      }
      var checkoutUrl = new URL(String(responseBody.checkoutUrl));
      if (checkoutUrl.protocol !== 'https:') throw new Error('Shopify returned an invalid secure checkout URL.');
      if (root.classList && root.classList.contains('swishcb--preview-order-card')) {
        window.dispatchEvent(new CustomEvent('swish:preview-checkout-ready', {
          detail: { checkoutUrl: checkoutUrl.href }
        }));
        return true;
      }
      window.location.assign(checkoutUrl.href);
      return true;
    } catch (error) {
      panel.dataset.swishcbCheckoutState = 'error';
      setCheckoutLoading(button, false, panel);
      if (status) {
        setText(status, checkoutErrorMessage(error, responseBody));
        status.hidden = false;
      }
      return false;
    } finally {
      if (paymentChoices) paymentChoices.disabled = false;
    }
  }

  function resultsEqual(left, right) {
    if (!left || !right) return false;
    return left.eligible === right.eligible &&
      left.reason === right.reason &&
      left.productReady === right.productReady &&
      left.purchasable === right.purchasable &&
      left.sport === right.sport &&
      round(left.areaM2 || 0, 4) === round(right.areaM2 || 0, 4) &&
      String(left.variantId || '') === String(right.variantId || '') &&
      String(left.seriesId || '') === String(right.seriesId || '') &&
      left.seriesRequired === right.seriesRequired &&
      String(left.priceSignature || '') === String(right.priceSignature || '') &&
      String(left.summarySignature || '') === String(right.summarySignature || '');
  }

  function refresh(root, eventDetail) {
    if (!root || !root.querySelector) return null;
    var orderPanel = root.querySelector('[data-swishcb-order-panel]');
    if (!orderPanel) return null;
    // Main product builders restore their actual design asynchronously. Do
    // not show a quote or move panels using the empty pre-startup controls.
    if (root.matches('.swishcb--summary-advanced:not(.swishcb--preview-order-card)') &&
        root.dataset.swishcbRuntimeMode !== 'order-editor') {
      if (!window.SwishCourtBuilder || typeof window.SwishCourtBuilder.getContactPayload !== 'function') return null;
      var startupPayload = getBuilderPayload(root);
      if (!positiveNumber(startupPayload && startupPayload.widthM) ||
          !positiveNumber(startupPayload && startupPayload.lengthM)) return null;
    }
    var focusedControlAnchor = captureFocusedControlAnchor(root);
    var contactPanel = root.querySelector('[data-swishcb-panel="contact"]');
    var state = getState(root);
    var dimensions = readDimensions(root, eventDetail) || state.dimensions;
    if (dimensions) state.dimensions = dimensions;
    var result = evaluate(root, orderPanel, dimensions);
    // Hoop purchases follow the actual preview; there is no separate quantity.
    if (isHoopPurchaseActive(root) && !getHoopPurchaseQuantity(root, result)) {
      deactivateHoopPurchase(root, false);
    }
    var seriesId = syncOrderSeries(root, orderPanel, result);
    // The product summary has no Order-panel activation to choose a series.
    // Choose its default when a ready design enters inline pricing, including
    // returning from an oversized court. An explicit customer selection remains
    // authoritative across later eligibility changes, including None.
    var inlineBuilderReady = root.matches('.swishcb--summary-advanced')
      && window.SwishCourtBuilder
      && typeof window.SwishCourtBuilder.getContactPayload === 'function'
      && positiveNumber(result.payload && result.payload.widthM)
      && positiveNumber(result.payload && result.payload.lengthM);
    if (inlineBuilderReady) {
      if (result.eligible && !state.inlineOrderEligible && !state.customerSelectedSurface && !root.__swishcbRestoredSurfaceSelection && !getOrderSeriesId(seriesId)) {
        state.applyingAutomaticSurfaceSelection = true;
        try {
          applyBuilderSeries(root, orderPanel, getDefaultOrderSeries(root, result));
        } finally {
          state.applyingAutomaticSurfaceSelection = false;
        }
        // Tile snapping and edge pieces can change the installed area. Price
        // the resulting design and recheck the limit before enabling checkout.
        dimensions = readDimensions(root) || dimensions;
        if (dimensions) state.dimensions = dimensions;
        result = evaluate(root, orderPanel, dimensions);
        seriesId = syncOrderSeries(root, orderPanel, result);
      }
      state.inlineOrderEligible = !!result.eligible;
    }
    var showOrder = !!result.eligible && !result.hardwoodContactOnly;
    if (!showOrder && orderPanel.open && !state.orderPanelTransition && root.dataset.swishcbConfigurator !== 'cards') orderPanel.open = false;
    result.seriesId = seriesId;
    result.seriesRequired = !getOrderSeriesId(seriesId);
    if (result.seriesRequired) {
      result.purchasable = false;
      updateVariantUi(orderPanel, result);
    }
    var seriesPresentation = updateSeriesPresentation(orderPanel, seriesId, Boolean(result && result.eligible));
    var pricing = updateOrderPricing(root, orderPanel, result, seriesId);
    if (result.reason === 'hoop_unavailable') updateVariantUi(orderPanel, result);
    var summary = buildOrderSummary(root, orderPanel, result, seriesId);
    result.seriesPresentation = seriesPresentation;
    result.pricing = pricing;
    result.priceSignature = pricing ? pricing.signature : '';
    result.summary = summary;
    result.summarySignature = summary.signature;
    root.dataset.swishcbOrderMode = 'split';
    root.dataset.swishcbOrderRequestedMode = 'split';
    updateOrderPanelCopy(orderPanel, result);
    setPanelVisible(orderPanel, showOrder);
    setPanelVisible(contactPanel, true);
    var orderModeSwitch = root.querySelector('[data-swishcb-order-mode-switch]');
    var orderModeInputs = root.querySelectorAll('.swishcb__mode-option[data-swishcb-order-mode]');
    setElementVisible(orderModeSwitch, false);
    Array.prototype.forEach.call(orderModeInputs, function (input) {
      input.disabled = !result.eligible;
    });
    setContactContentVisible(root, true);
    setOrderModeLoading(root, false);
    root.dataset.swishcbOrderState = result.eligible ? 'eligible' : 'quote';
    root.dataset.swishcbOrderReason = result.reason;
    root.dataset.swishcbOrderAreaM2 = result.areaM2 == null ? '' : String(round(result.areaM2, 4));
    root.dataset.swishcbOrderInstalledWidthM = result.widthM == null ? '' : String(round(result.widthM, 4));
    root.dataset.swishcbOrderInstalledLengthM = result.lengthM == null ? '' : String(round(result.lengthM, 4));
    updateOrderSummary(orderPanel, summary);
    syncMeasurementState(root, summary);
    updateStatus(root, orderPanel, contactPanel, result);
    syncCartProperties(root, orderPanel, result, summary, pricing);
    if (showOrder) scheduleOrderDesignUpdate(root, orderPanel, ORDER_DESIGN_PREVIEW_DELAY_MS);

    var previousResult = state.result;
    var summaryChanged = !previousResult || previousResult.summarySignature !== result.summarySignature;
    if (!resultsEqual(previousResult, result)) {
      state.result = Object.assign({}, result);
      var detail = Object.assign({ root: root }, result);
      try { root.dispatchEvent(new CustomEvent('swish:order-eligibility-changed', { detail: detail })); } catch (_) {}
      try { window.dispatchEvent(new CustomEvent('swish:order-eligibility-changed', { detail: detail })); } catch (_) {}
    } else {
      state.result = Object.assign({}, result);
    }
    if (summaryChanged) {
      var summaryDetail = Object.assign({ root: root }, summary);
      try { root.dispatchEvent(new CustomEvent('swish:order-summary-changed', { detail: summaryDetail })); } catch (_) {}
      try { window.dispatchEvent(new CustomEvent('swish:order-summary-changed', { detail: summaryDetail })); } catch (_) {}
    }
    stabilizeFocusedControl(focusedControlAnchor);
    return result;
  }

  function scheduleRefresh(root, eventDetail) {
    var state = getState(root);
    // Coalesce refresh requests without postponing them. The builder performs
    // frequent DOM mutations while rendering its live preview; treating this
    // as a trailing-edge debounce allowed those mutations to cancel the timer
    // indefinitely. Controls and the serialized configuration would update,
    // while the Order Now summary and price remained stuck on an older state.
    // Keeping the first timer turns this into a small throttle and guarantees
    // that the latest dimensions/configuration reach the order panel.
    if (eventDetail && typeof eventDetail === 'object') {
      state.pendingRefreshDetail = eventDetail;
    }
    if (state.refreshTimer) return;
    state.refreshTimer = window.setTimeout(function () {
      state.refreshTimer = 0;
      var pendingDetail = state.pendingRefreshDetail;
      state.pendingRefreshDetail = null;
      refresh(root, pendingDetail);
    }, 40);
  }

  function scheduleSettledRefresh(root) {
    var state = getState(root);
    (state.settledRefreshTimers || []).forEach(function (timer) {
      window.clearTimeout(timer);
    });
    state.settledRefreshTimers = [150, 600, 1600].map(function (delay) {
      return window.setTimeout(function () {
        scheduleRefresh(root);
      }, delay);
    });
  }

  function scheduleFontUploadRefresh(root) {
    [800, 1600, 3200].forEach(function (delay) {
      window.setTimeout(function () {
        scheduleRefresh(root);
      }, delay);
    });
  }

  function escapeAttributeValue(value) {
    return String(value || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  function resolveOrderColorInput(root, swatch) {
    var kind = String(swatch.dataset.swishcbOrderColorTargetKind || '');
    var id = String(swatch.dataset.swishcbOrderColorTargetId || '');
    if (kind === 'base' && BASE_COLOR_INPUT_IDS[id]) return root.querySelector('#' + BASE_COLOR_INPUT_IDS[id]);
    if (kind === 'detail') return root.querySelector('[data-detail-color="' + escapeAttributeValue(id) + '"]');
    if (kind === 'extra') return root.querySelector('[data-extra-color="' + escapeAttributeValue(id) + '"]');
    return null;
  }

  function openOrderColorPicker(root, swatch) {
    var input = resolveOrderColorInput(root, swatch);
    if (!input) return false;
    closeMeasurementPanel(root, false);
    // Color edits rebuild the specification rows. Keep following the same
    // control (Edit, HEX or square) in the stable list after that replacement.
    var anchor = swatch;
    var list = swatch.closest('[data-swishcb-order-specifications], [data-swishcb-product-order-specifications]');
    if (list) {
      var anchorKind = swatch.dataset.swishcbOrderColorTargetKind;
      var anchorId = swatch.dataset.swishcbOrderColorTargetId;
      var matchingControls = function () {
        return Array.from(list.querySelectorAll('[data-swishcb-order-color-swatch]')).filter(function (button) {
          return button.dataset.swishcbOrderColorTargetKind === anchorKind && button.dataset.swishcbOrderColorTargetId === anchorId;
        });
      };
      var ordinal = matchingControls().indexOf(swatch);
      var lastRect = swatch.getBoundingClientRect();
      if (ordinal >= 0) anchor = { getFocusTarget: function () { return matchingControls()[ordinal]; }, getBoundingClientRect: function () {
        var current = matchingControls()[ordinal];
        if (current && current.isConnected) lastRect = current.getBoundingClientRect();
        return lastRect;
      } };
    }
    var tools = window.SwishCB && typeof window.SwishCB.getColorTools === 'function'
      ? window.SwishCB.getColorTools(root)
      : null;
    if (tools) {
      try {
        if (typeof tools.ensurePreviewColorPickerContext === 'function') {
          tools.ensurePreviewColorPickerContext({ openColorsPanel: false });
        }
        if (typeof tools.bindPopoverToTarget === 'function') {
          var kind = String(swatch.dataset.swishcbOrderColorTargetKind || '');
          var targetId = String(swatch.dataset.swishcbOrderColorTargetId || '');
          if (
            kind === 'base' &&
            targetId === 'border' &&
            root.classList.contains('swishcb--preview-order-card')
          ) {
            input.dataset.swishcbColorPopoverTitle = 'Edges';
          }
          tools.bindPopoverToTarget(input, {
            kind: kind === 'base' ? 'main' : kind,
            id: targetId
          });
        }
        if (typeof tools.openColorPopoverNearElement === 'function' && tools.openColorPopoverNearElement(anchor)) {
          return true;
        }
      } catch (_) {}
    }
    try { input.click(); return true; } catch (_) { return false; }
  }

  function selectGalleryImage(panel, button) {
    if (!panel || !button) return;
    var source = String(button.dataset.swishcbOrderSeriesThumbnail || '');
    var mainImage = panel.querySelector('[data-swishcb-order-series-image]');
    closeOrderMediaZoom(panel);
    if (source && mainImage) mainImage.setAttribute('src', source);
    panel.querySelectorAll('[data-swishcb-order-series-thumbnail]').forEach(function (candidate) {
      candidate.setAttribute('aria-current', candidate === button ? 'true' : 'false');
    });
  }

  function closeOrderMediaZoom(panel) {
    if (!panel) return;
    var scope = panel.closest('#swish-court-builder') || panel;
    scope.querySelectorAll('[data-swishcb-order-series-zoom], [data-swishcb-order-design-zoom]').forEach(function (trigger) {
      var overlay = trigger.querySelector('[data-swishcb-order-magnify]');
      if (overlay) overlay.remove();
      trigger.setAttribute('aria-pressed', 'false');
    });
  }

  function moveOrderMediaZoom(trigger, event) {
    if (!trigger || !event) return;
    var overlay = trigger.querySelector('[data-swishcb-order-magnify]');
    if (!overlay) return;
    var rect = trigger.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    var hasPointerPosition = Number.isFinite(event.clientX) && Number.isFinite(event.clientY)
      && event.clientX >= rect.left && event.clientX <= rect.right
      && event.clientY >= rect.top && event.clientY <= rect.bottom;
    var x = hasPointerPosition ? (event.clientX - rect.left) / rect.width : 0.5;
    var y = hasPointerPosition ? (event.clientY - rect.top) / rect.height : 0.5;
    overlay.style.backgroundPosition = (x * 100) + '% ' + (y * 100) + '%';
  }

  function openOrderMediaZoom(panel, trigger, event) {
    if (!panel || !trigger) return;
    var existing = trigger.querySelector('[data-swishcb-order-magnify]');
    if (existing) {
      closeOrderMediaZoom(panel);
      return;
    }
    var mainImage = trigger.querySelector('[data-swishcb-order-series-image], [data-swishcb-order-design-image]')
      || panel.querySelector('[data-swishcb-order-series-image]');
    var source = mainImage ? String(mainImage.currentSrc || mainImage.getAttribute('src') || '') : '';
    if (!source) return;
    var overlay = document.createElement('span');
    overlay.className = 'swishcb__order-series-magnify';
    overlay.dataset.swishcbOrderMagnify = 'true';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.backgroundImage = 'url("' + source.replace(/"/g, '%22') + '")';
    overlay.style.backgroundSize = '200%';
    trigger.insertBefore(overlay, mainImage);
    trigger.setAttribute('aria-pressed', 'true');
    moveOrderMediaZoom(trigger, event);
  }

  function isOrderVideoPopupEnabled(panel) {
    return !!panel && String(panel.dataset.swishcbOrderVideoPopupEnabled || '').toLowerCase() === 'true';
  }

  function closeOrderVideoPopup(root, restoreFocus) {
    if (!root || !root._swishcbOrderVideoPopup) return;
    var state = root._swishcbOrderVideoPopup;
    delete root._swishcbOrderVideoPopup;
    try { state.video.pause(); } catch (_) {}
    document.removeEventListener('keydown', state.onKeydown);
    if (state.rootObserver) state.rootObserver.disconnect();
    if (state.element && state.element.parentNode) state.element.remove();
    if (!document.querySelector('[data-swishcb-order-video-popup]')) {
      document.body.classList.remove('swishcb-order-video-popup-open');
    }
    if (restoreFocus !== false && state.opener && state.opener.isConnected && typeof state.opener.focus === 'function') {
      try { state.opener.focus({ preventScroll: true }); } catch (_) { try { state.opener.focus(); } catch (_) {} }
    }
  }

  function applyOrderVideoPopupAspect(dialog, video, sourceVideo) {
    if (!dialog || !video) return;
    var width = toNumber(video.videoWidth) || toNumber(sourceVideo && sourceVideo.videoWidth)
      || toNumber(video.getAttribute('width')) || toNumber(sourceVideo && sourceVideo.getAttribute('width'));
    var height = toNumber(video.videoHeight) || toNumber(sourceVideo && sourceVideo.videoHeight)
      || toNumber(video.getAttribute('height')) || toNumber(sourceVideo && sourceVideo.getAttribute('height'));
    if (!width || !height) return;
    dialog.style.setProperty('--swishcb-order-video-popup-aspect', String(width / height));
  }

  function openOrderVideoPopup(root, sourceHost, opener) {
    if (!root || !sourceHost) return false;
    var panel = sourceHost.closest('[data-swishcb-order-panel]');
    var sourceVideo = sourceHost.querySelector('video');
    if (!panel || !sourceVideo || !isOrderVideoPopupEnabled(panel)) return false;

    closeOrderVideoPopup(root, false);
    try { sourceVideo.pause(); } catch (_) {}

    var popup = document.createElement('div');
    popup.className = 'swishcb__order-video-popup';
    popup.dataset.swishcbOrderVideoPopup = 'true';
    popup.innerHTML = [
      '<button class="swishcb__order-video-popup-backdrop" type="button" data-swishcb-order-video-popup-close aria-label="Close video"></button>',
      '<div class="swishcb__order-video-popup-dialog" role="dialog" aria-modal="true" aria-label="Video player">',
        '<button class="swishcb__order-video-popup-close" type="button" data-swishcb-order-video-popup-close aria-label="Close video">',
          '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6 6 18"></path></svg>',
        '</button>',
        '<div class="swishcb__order-video-popup-player modern-video-host" data-show-cover-play-icon="false"></div>',
      '</div>'
    ].join('');

    var dialog = popup.querySelector('.swishcb__order-video-popup-dialog');
    var popupHost = popup.querySelector('.swishcb__order-video-popup-player');
    var closeButton = popup.querySelector('.swishcb__order-video-popup-close');
    var popupVideo = sourceVideo.cloneNode(true);
    popupVideo.className = 'swishcb__order-video-popup-video';
    popupVideo.removeAttribute('id');
    popupVideo.removeAttribute('style');
    popupVideo.removeAttribute('data-product-video-bound');
    // The popup opens only after Play is clicked; activate its deferred sources now.
    if (popupVideo.dataset.src && !popupVideo.getAttribute('src')) {
      popupVideo.setAttribute('src', popupVideo.dataset.src);
    }
    popupVideo.querySelectorAll('source[data-src]').forEach(function (source) {
      if (!source.getAttribute('src')) source.setAttribute('src', source.dataset.src);
    });
    popupVideo.removeAttribute('data-defer-video-until-play');
    popupVideo.removeAttribute('data-deferred-video-source-loaded');
    popupVideo.setAttribute('data-enable-modern-video-player', 'true');
    popupVideo.setAttribute('controls', 'controls');
    popupVideo.setAttribute('playsinline', '');
    popupVideo.setAttribute('preload', 'auto');
    popupVideo.autoplay = true;
    popupHost.appendChild(popupVideo);

    applyOrderVideoPopupAspect(dialog, popupVideo, sourceVideo);
    popupVideo.addEventListener('loadedmetadata', function () {
      applyOrderVideoPopupAspect(dialog, popupVideo, sourceVideo);
    });

    var onKeydown = function (event) {
      if (event.key === 'Escape' && !document.fullscreenElement) {
        event.preventDefault();
        closeOrderVideoPopup(root, true);
      }
    };
    root._swishcbOrderVideoPopup = {
      element: popup,
      video: popupVideo,
      opener: opener || sourceHost,
      onKeydown: onKeydown,
      rootObserver: null
    };
    if (typeof MutationObserver === 'function') {
      var rootObserver = new MutationObserver(function () {
        if (!root.isConnected) closeOrderVideoPopup(root, false);
      });
      rootObserver.observe(document.documentElement, { childList: true, subtree: true });
      root._swishcbOrderVideoPopup.rootObserver = rootObserver;
    }
    popup.addEventListener('click', function (event) {
      if (event.target && event.target.closest && event.target.closest('[data-swishcb-order-video-popup-close]')) {
        event.preventDefault();
        closeOrderVideoPopup(root, true);
      }
    });
    document.addEventListener('keydown', onKeydown);
    document.body.appendChild(popup);
    document.body.classList.add('swishcb-order-video-popup-open');

    window.requestAnimationFrame(function () {
      popup.classList.add('is-open');
      if (closeButton) closeButton.focus({ preventScroll: true });
      var playPromise = popupVideo.play();
      if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(function () {});
    });
    return true;
  }

  function bindOrderVideoPopup(root) {
    root.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      var sourceHost = target.closest('.swishcb__order-series-video');
      if (!sourceHost || !root.contains(sourceHost)) return;
      var panel = sourceHost.closest('[data-swishcb-order-panel]');
      if (!isOrderVideoPopupEnabled(panel)) return;
      if (target.closest('[data-product-video-controls]')) return;
      if (!target.matches('video') && !target.closest('[data-video-cover-play]')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      openOrderVideoPopup(root, sourceHost, target.closest('button') || sourceHost);
    }, true);
  }

  function bindQuantityButtons(root) {
    root.addEventListener('click', function (event) {
      var button = event.target && event.target.closest && event.target.closest('[data-swishcb-order-quantity-step]');
      if (!button || !root.contains(button)) return;
      var panel = button.closest('[data-swishcb-order-panel]');
      var input = panel && panel.querySelector('[data-swishcb-order-quantity]');
      if (!input) return;
      var direction = toNumber(button.dataset.swishcbOrderQuantityStep) || 0;
      var value = toNumber(input.value) || toNumber(input.min) || 1;
      var step = toNumber(input.step) || 1;
      var min = toNumber(input.min) || step;
      var max = toNumber(input.max);
      value = Math.max(min, value + (direction * step));
      if (max != null) value = Math.min(max, value);
      input.value = String(round(value, 4));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function bindDesignSummaryControls(root) {
    Array.prototype.forEach.call(
      root.querySelectorAll('#swishcb-logo, [aria-label="Court text"], [aria-label="Text font"], [data-text-font-upload]'),
      function (control) {
        if (control.dataset.swishcbOrderSummaryBound === 'true') return;
        control.dataset.swishcbOrderSummaryBound = 'true';
        var requestRefresh = function () {
          scheduleRefresh(root);
          scheduleSettledRefresh(root);
          if (control.matches && control.matches('[data-text-font-upload]')) {
            scheduleFontUploadRefresh(root);
          }
        };
        control.addEventListener('input', requestRefresh);
        control.addEventListener('change', requestRefresh);
      }
    );
  }

  function bind(root) {
    if (!root || root.dataset.swishcbOrderingBound === 'true') return;
    root.dataset.swishcbOrderingBound = 'true';
    bindQuantityButtons(root);
    bindOrderVideoPopup(root);
    bindDesignSummaryControls(root);
    ['pointerdown', 'keydown', 'input'].forEach(function (type) {
      root.addEventListener(type, function (event) {
        if (event.isTrusted) getState(root).measurementDefaultsLocked = true;
      }, true);
    });
    root.addEventListener('keydown', function (event) {
      if (event.target.matches && event.target.matches('[data-swishcb-measurement-trigger]') && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        openMeasurementPanel(root, event.target);
      }
    });
    var orderPanel = root.querySelector('[data-swishcb-order-panel]');
    if (orderPanel && orderPanel.dataset.swishcbOrderToggleBound !== 'true' && !root.matches('.swishcb--summary-advanced')) {
      orderPanel.dataset.swishcbOrderToggleBound = 'true';
      orderPanel.open = false;
      orderPanel.dataset.swishcbOrderDefaultClosed = 'true';
      var orderSummary = orderPanel.querySelector('summary');
      if (orderSummary) {
        orderSummary.addEventListener('click', function (event) {
          event.preventDefault();
          orderPanel.open = !orderPanel.open;
        }, true);
      }
      orderPanel.addEventListener('toggle', function () {
        setOrderPanelActive(root, orderPanel, !!orderPanel.open);
      });
    }
    var orderModeSwitch = root.querySelector('[data-swishcb-order-mode-switch]');
    var activateOrderMode = function (target) {
      if (!target || !target.matches || !target.matches('.swishcb__mode-option[data-swishcb-order-mode]')) return;
      var state = getState(root);
      var nextMode = target.getAttribute('data-swishcb-order-mode') === 'order' ? 'order' : 'contact';
      if (isHardwoodSeriesSelected(root, getBuilderPayload(root))) nextMode = 'contact';
      state.orderMode = nextMode;
      root.dataset.swishcbOrderRequestedMode = nextMode;
      Array.prototype.forEach.call(root.querySelectorAll('.swishcb__mode-option[data-swishcb-order-mode]'), function (control) {
        var selected = control.getAttribute('data-swishcb-order-mode') === nextMode;
        control.setAttribute('aria-checked', selected ? 'true' : 'false');
        control.classList.toggle('is-selected', selected);
      });
      var orderPanel = root.querySelector('[data-swishcb-order-panel]');
      var showOrder = nextMode === 'order' && root.dataset.swishcbOrderState === 'eligible';
      root.dataset.swishcbOrderMode = showOrder ? 'order' : 'contact';
      if (!showOrder && nextMode === 'contact') {
        setContactContentVisible(root, true);
        setPanelVisible(orderPanel, false);
        setOrderModeLoading(root, false);
      } else {
        setContactContentVisible(root, false);
        setPanelVisible(orderPanel, false);
        setOrderModeLoading(root, true);
      }
      if (showOrder && orderPanel && orderPanel.open) {
        scheduleOrderDesignUpdate(root, orderPanel, 0);
      }
      scheduleRefresh(root);
    };
    root.addEventListener('click', function (event) {
      var target = event && event.target && event.target.closest
        ? event.target.closest('.swishcb__mode-option[data-swishcb-order-mode]')
        : null;
      if (!target || !root.contains(target)) return;
      activateOrderMode(target);
    }, true);
    if (orderModeSwitch && orderModeSwitch.dataset.swishcbOrderModeSwitchBound !== 'true') {
      orderModeSwitch.dataset.swishcbOrderModeSwitchBound = 'true';
      orderModeSwitch.addEventListener('click', function (event) {
        var control = event.target && event.target.closest ? event.target.closest('.swishcb__mode-option[data-swishcb-order-mode]') : null;
        if (!control || !orderModeSwitch.contains(control)) return;
        activateOrderMode(control);
      });
    }
    var boundState = getState(root);
    if (!boundState.designWatchTimer && !root.hasAttribute('data-swish-preview-order-context')) {
      boundState.designWatchTimer = window.setInterval(function () {
        var panel = root.querySelector('[data-swishcb-order-panel]');
        if (!panel) return;
        var signature = orderDesignSignature(root, panel);
        if (!signature || signature === boundState.designSignature) {
          boundState.observedDesignSignature = '';
          boundState.observedDesignSignatureCount = 0;
          return;
        }
        if (boundState.observedDesignSignature === signature) {
          boundState.observedDesignSignatureCount += 1;
        } else {
          boundState.observedDesignSignature = signature;
          boundState.observedDesignSignatureCount = 1;
        }
        if (boundState.observedDesignSignatureCount >= 2) {
          scheduleOrderDesignUpdate(root, panel, 0);
        }
      }, ORDER_DESIGN_WATCH_INTERVAL_MS);
    }
    window.addEventListener('pagehide', function () {
      var state = getState(root);
      if (state.designTimer) window.clearTimeout(state.designTimer);
      if (state.designWatchTimer) window.clearInterval(state.designWatchTimer);
      (state.settledRefreshTimers || []).forEach(function (timer) {
        window.clearTimeout(timer);
      });
      state.settledRefreshTimers = [];
      state.designWatchTimer = 0;
      revokeOrderDesignUrl(state.designDisplayUrl);
      state.designDisplayUrl = '';
      state.designAsset = null;
    }, { once: true });
    var configInput = root.querySelector('[data-bind="config-json"]');
    if (configInput && configInput.dataset.swishcbOrderConfigBound !== 'true') {
      configInput.dataset.swishcbOrderConfigBound = 'true';
      configInput.addEventListener('input', function () {
        scheduleRefresh(root);
      }, true);
      if (typeof MutationObserver === 'function') {
        try {
          configInput.swishcbOrderConfigObserver = new MutationObserver(function () {
            scheduleRefresh(root);
          });
          configInput.swishcbOrderConfigObserver.observe(configInput, {
            attributes: true,
            attributeFilter: ['value']
          });
        } catch (_) {}
      }
    }
    root.querySelectorAll('[data-swishcb-order-series-zoom], [data-swishcb-order-design-zoom]').forEach(function (zoomButton) {
      if (zoomButton.dataset.swishcbOrderZoomBound === 'true') return;
      zoomButton.dataset.swishcbOrderZoomBound = 'true';
      zoomButton.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        openOrderMediaZoom(zoomButton.closest('[data-swishcb-order-panel]') || root, zoomButton, event);
      });
      zoomButton.addEventListener('pointermove', function (event) {
        moveOrderMediaZoom(zoomButton, event);
      });
      zoomButton.addEventListener('pointerleave', function (event) {
        if (event.pointerType !== 'touch') closeOrderMediaZoom(zoomButton.closest('[data-swishcb-order-panel]') || root);
      });
    });
    var checkoutButton = root.querySelector('[data-swishcb-order-checkout]');
    if (checkoutButton && checkoutButton.dataset.swishcbOrderCheckoutBound !== 'true') {
      checkoutButton.dataset.swishcbOrderCheckoutBound = 'true';
      checkoutButton.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        startSecureCheckout(root, checkoutButton.closest('[data-swishcb-order-panel]'), checkoutButton);
      });
    }

    root.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-swishcb-product-series-trigger]');
      if (trigger && root.contains(trigger)) { event.preventDefault(); openSummarySeriesPanel(root, trigger); }
    });
    root.addEventListener('change', function (event) {
      var target = event && event.target;
      var orderPanel = root.querySelector('[data-swishcb-order-panel]');
      // Explicit material choices, including None, remain authoritative when
      // the floor changes its online-order eligibility after resizing.
      if (target && target.matches && target.matches('#swishcb-tile-series, [data-swishcb-order-series-select], [data-swishcb-product-series-select]')) {
        var surfaceSelectionState = getState(root);
        if (!surfaceSelectionState.applyingAutomaticSurfaceSelection) surfaceSelectionState.customerSelectedSurface = true;
      }
      if (orderPanel) delete orderPanel.dataset.swishcbCheckoutState;
      if (target && target.matches && target.matches('[data-swishcb-product-series-select]')) {
        if (root.matches('[data-swishcb-configurator="cards"]') && !root.classList.contains('swishcb--preview-order-card')) {
          var nativeSeries = root.querySelector('#swishcb-tile-series');
          var nativeSeriesOption = nativeSeries && Array.from(nativeSeries.options).find(function (option) { return option.value === target.value; });
          if (nativeSeriesOption && !nativeSeriesOption.disabled && nativeSeries.value !== nativeSeriesOption.value) {
            nativeSeries.value = nativeSeriesOption.value;
            nativeSeries.dispatchEvent(new Event('change', { bubbles: true }));
          }
          scheduleRefresh(root);
          scheduleSettledRefresh(root);
          return;
        }
        var selectedProductSeries = getOrderSeriesId(target.value) || 'elastic';
        if (root.dataset.swishcbPickleballSeriesRestricted === 'true' && selectedProductSeries !== 'elastic') {
          selectedProductSeries = 'elastic';
        }
        target.value = applyBuilderSeries(root, orderPanel, selectedProductSeries);
        scheduleRefresh(root);
        scheduleSettledRefresh(root);
        return;
      }
      if (target && target.matches && target.matches('[data-swishcb-hoop-purchase-toggle]')) {
        if (target.checked) activateHoopPurchase(root);
        else deactivateHoopPurchase(root, true);
        return;
      }
      if (target && target.matches && target.matches('[data-swishcb-hoop-purchase-select]')) {
        var selectedHoopPreset = normalizeHoopPurchasePreset(target.value);
        if (findHoopPurchaseProduct(root, selectedHoopPreset)) {
          applyPurchasedHoopPreset(root, selectedHoopPreset);
          loadHoopPurchaseProduct(root, selectedHoopPreset);
          scheduleRefresh(root);
          scheduleSettledRefresh(root);
        }
        return;
      }
      if (target && target.matches && target.matches('#swishcb-hoop-preset')) {
        var hoopState = getState(root);
        if (isHoopPurchaseActive(root) && !hoopState.hoopPurchaseApplyingPreset) {
          var matchingProduct = findHoopPurchaseProduct(root, target.value);
          var purchaseSelect = root.querySelector('[data-swishcb-hoop-purchase-select]');
          if (matchingProduct && purchaseSelect) {
            purchaseSelect.value = matchingProduct.preset;
            loadHoopPurchaseProduct(root, matchingProduct.preset);
          } else {
            deactivateHoopPurchase(root, false);
          }
        }
      }
      if (target && target.matches && target.matches('.swishcb__mode-option[data-swishcb-order-mode]')) {
        var selectedMode = target.getAttribute('data-swishcb-order-mode') === 'order' ? 'order' : 'contact';
        getState(root).orderMode = selectedMode;
        root.dataset.swishcbOrderRequestedMode = selectedMode;
      }
      if (target && target.matches && target.matches('[data-swishcb-order-series-select]')) {
        var builderSeries = root.querySelector('#swishcb-tile-series');
        var selectedSeries = getOrderSeriesId(target.value) || 'none';
        if (root.dataset.swishcbPickleballSeriesRestricted === 'true' && (selectedSeries === 'elite' || selectedSeries === 'large-elite')) {
          selectedSeries = 'elastic';
          target.value = selectedSeries;
        }
        updateSeriesPresentation(orderPanel, selectedSeries, true);
        if (builderSeries && builderSeries.value !== selectedSeries) {
          builderSeries.value = selectedSeries;
          builderSeries.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      if (target && target.matches && target.matches('[data-swishcb-product-overhang-input]')) {
        updateProductOverhang(root, target);
      }
      if (target && target.matches && target.matches('[data-swishcb-product-dimension-input]')) {
        updateProductDimension(root, target);
      }
      scheduleRefresh(root);
      scheduleSettledRefresh(root);
      if (target && target.matches && target.matches('#swishcb-logo, [aria-label="Court text"], [aria-label="Text font"], [data-text-font-upload]')) {
        if (target.matches('[data-text-font-upload]')) scheduleFontUploadRefresh(root);
      }
    }, true);
    root.addEventListener('focusin', function (event) {
      var target = event && event.target;
      if (!target || !target.matches || !target.matches('[data-swishcb-product-overhang-input], [data-swishcb-product-dimension-input]')) return;
      if (target.hasAttribute('data-swishcb-measurement-trigger')) {
        openMeasurementPanel(root, target);
        return;
      }
      var list = target.closest('[data-swishcb-product-order-specifications]');
      if (list) list.dataset.swishcbOrderEditorActive = 'true';
    }, true);
    root.addEventListener('focusout', function (event) {
      var target = event && event.target;
      if (!target || !target.matches || !target.matches('[data-swishcb-product-overhang-input], [data-swishcb-product-dimension-input]')) return;
      var list = target.closest('[data-swishcb-product-order-specifications]');
      window.setTimeout(function () {
        if (list) delete list.dataset.swishcbOrderEditorActive;
        scheduleRefresh(root);
        scheduleSettledRefresh(root);
      }, 0);
    }, true);
    root.addEventListener('click', function (event) {
      var measurementEditor = event.target.closest && event.target.closest('.swishcb__product-order-dimension-editor, .swishcb__product-order-overhang-editor');
      var measurement = event.target.closest && event.target.closest('[data-swishcb-measurement-edit]');
      if (!measurement) measurement = measurementEditor && measurementEditor.querySelector('[data-swishcb-measurement-trigger]');
      if (measurement && root.contains(measurement)) {
        event.preventDefault();
        openMeasurementPanel(root, measurement);
        return;
      }
      var dimensionResetButton = event.target && event.target.closest && event.target.closest('[data-swishcb-product-dimension-reset]');
      if (dimensionResetButton && root.contains(dimensionResetButton)) {
        event.preventDefault();
        event.stopPropagation();
        resetProductDimension(root, dimensionResetButton);
        scheduleRefresh(root);
        scheduleSettledRefresh(root);
        return;
      }
      var addArtworkButton = event.target && event.target.closest && event.target.closest('[data-swishcb-product-add-artwork]');
      if (addArtworkButton && root.contains(addArtworkButton)) {
        event.preventDefault();
        event.stopPropagation();
        openProductArtworkManager(
          root,
          addArtworkButton.dataset.swishcbProductAddArtwork,
          addArtworkButton.dataset.swishcbProductAddArtworkMode === 'new'
        );
        return;
      }
      var addHoopButton = event.target && event.target.closest && event.target.closest('[data-swishcb-hoop-add]');
      if (addHoopButton && root.contains(addHoopButton)) {
        event.preventDefault();
        event.stopPropagation();
        activateHoopPurchase(root);
        return;
      }
      var removeHoopButton = event.target && event.target.closest && event.target.closest('[data-swishcb-hoop-remove]');
      if (removeHoopButton && root.contains(removeHoopButton)) {
        event.preventDefault();
        event.stopPropagation();
        deactivateHoopPurchase(root, true);
        return;
      }
      var checkoutButton = event.target && event.target.closest && event.target.closest('[data-swishcb-order-checkout]');
      if (checkoutButton && root.contains(checkoutButton)) {
        event.preventDefault();
        startSecureCheckout(root, checkoutButton.closest('[data-swishcb-order-panel]'), checkoutButton);
        return;
      }
      var zoom = event.target && event.target.closest && event.target.closest('[data-swishcb-order-series-zoom], [data-swishcb-order-design-zoom]');
      if (zoom && root.contains(zoom)) {
        event.preventDefault();
        openOrderMediaZoom(zoom.closest('[data-swishcb-order-panel]') || root, zoom, event);
        return;
      }
      var thumbnail = event.target && event.target.closest && event.target.closest('[data-swishcb-order-series-thumbnail]');
      if (thumbnail && root.contains(thumbnail)) {
        event.preventDefault();
        selectGalleryImage(thumbnail.closest('[data-swishcb-order-panel]'), thumbnail);
        return;
      }
      var swatch = event.target && event.target.closest && event.target.closest('[data-swishcb-order-color-swatch]');
      if (swatch && root.contains(swatch)) {
        event.preventDefault();
        openOrderColorPicker(root, swatch);
      }
    });
    root.addEventListener('input', function (event) {
      if (!event.target || !event.target.matches) return;
      if (event.target.matches('[data-swishcb-product-overhang-input]')) {
        syncProductMeasurementEditorWidth(event.target);
        updateProductOverhang(root, event.target);
        return;
      }
      if (event.target.matches('[data-swishcb-product-dimension-input]')) {
        syncProductMeasurementEditorWidth(event.target);
        updateProductDimension(root, event.target);
        return;
      }
      if (event.target.matches('#swishcb-width, #swishcb-length, #swishcb-logo, [aria-label="Court text"], [aria-label="Text font"], [data-text-font-upload], [data-bind="config-json"], [data-bind="preview-url"]')) {
        var orderPanel = root.querySelector('[data-swishcb-order-panel]');
        if (orderPanel) delete orderPanel.dataset.swishcbCheckoutState;
        scheduleRefresh(root);
        scheduleSettledRefresh(root);
        if (event.target.matches('#swishcb-logo, [aria-label="Court text"], [aria-label="Text font"], [data-text-font-upload]')) {
          if (event.target.matches('[data-text-font-upload]')) scheduleFontUploadRefresh(root);
        }
      }
    }, true);

    if (typeof MutationObserver === 'function') {
      try {
        var observer = new MutationObserver(function (mutations) {
          var relevant = mutations.some(function (mutation) {
            var target = mutation.target;
            if (!target || !target.getAttribute) return false;
            if (mutation.type === 'childList') {
              // Summary text, rendered prices and video controls are output,
              // not design changes. Only added/removed design controls need
              // rebinding; input/config events handle their value changes.
              if (target.closest('[data-swishcb-order-panel], [data-swishcb-product-summary], [data-swishcb-product-order-specifications]')) return false;
              var sourceControls = 'input, select, textarea, [data-logo-remove], [data-text-remove], [data-detail-row]';
              return Array.prototype.some.call(mutation.addedNodes, function (node) {
                return node.nodeType === 1 && (node.matches(sourceControls) || node.querySelector(sourceControls));
              }) || Array.prototype.some.call(mutation.removedNodes, function (node) {
                return node.nodeType === 1 && (node.matches(sourceControls) || node.querySelector(sourceControls));
              });
            }
            if (mutation.attributeName === 'value' && target.matches && target.matches('[data-bind="config-json"]')) return true;
            if (mutation.attributeName === 'data-swishcb-three-point-line-visible' && target === root) return true;
            return THREE_POINT_DETAIL_IDS.indexOf(String(target.getAttribute('data-detail-row') || '')) >= 0;
          });
          if (relevant) {
            bindDesignSummaryControls(root);
            scheduleRefresh(root);
          }
        });
        observer.observe(root, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['hidden', 'aria-hidden', 'style', 'value', 'data-swishcb-three-point-line-visible']
        });
      } catch (_) {}
    }
    scheduleRefresh(root);
  }

  function init() {
    if (!globalRefreshBound) {
      globalRefreshBound = true;
      document.addEventListener('click', function (event) {
      var control = event && event.target && event.target.closest
        ? event.target.closest('.swishcb__mode-option[data-swishcb-order-mode]')
        : null;
      if (!control) return;
      var root = control.closest(ROOT_SELECTOR);
      if (!root) return;
      window.setTimeout(function () {
        api.setMode(root, control.getAttribute('data-swishcb-order-mode'));
      }, 0);
      }, true);
      var refreshFromControl = function (event) {
        var target = event && event.target;
        var root = target && target.closest ? target.closest(ROOT_SELECTOR) : null;
        if (!root) return;
        bind(root);
        scheduleRefresh(root, event && event.detail);
        scheduleSettledRefresh(root);
      };
      document.addEventListener('change', refreshFromControl, true);
      document.addEventListener('input', refreshFromControl, true);
      EVENT_NAMES.forEach(function (eventName) {
        window.addEventListener(eventName, function (event) {
          getRoots().forEach(function (root) {
            bind(root);
            scheduleRefresh(root, event && event.detail);
            scheduleSettledRefresh(root);
          });
        });
      });
      globalConfigWatchTimer = window.setInterval(function () {
        getRoots().forEach(function (root) {
          bind(root);
          var input = root.querySelector('[data-bind="config-json"]');
          var widthInput = root.querySelector('#swishcb-width');
          var lengthInput = root.querySelector('#swishcb-length');
          var seriesInput = root.querySelector('#swishcb-tile-series');
          // The displayed totals can settle a frame after the serialized
          // builder state (notably after tile-grid and edge calculations).
          // Include both sources so Order Now cannot retain the pre-settle
          // dimensions even when that final display write emits no DOM event.
          var value = [
            String(input && input.value || ''),
            String(widthInput && widthInput.value || ''),
            String(lengthInput && lengthInput.value || ''),
            String(seriesInput && seriesInput.value || '')
          ].join('\n');
          var state = getState(root);
          if (value === state.globalObservedConfigValue) return;
          state.globalObservedConfigValue = value;
          scheduleRefresh(root);
          scheduleSettledRefresh(root);
        });
      }, 250);
    }
    getRoots().forEach(bind);
  }

  window.SwishCB = window.SwishCB || {};
  var api = window.SwishCB.ordering || {};
  api.version = '1.1.0';
  api.ruleResolvers = Array.isArray(api.ruleResolvers) ? api.ruleResolvers : [];
  api.registerRuleResolver = function (resolver) {
    if (typeof resolver !== 'function') return function () {};
    api.ruleResolvers.push(resolver);
    getRoots().forEach(function (root) { scheduleRefresh(root); });
    return function () {
      var index = api.ruleResolvers.indexOf(resolver);
      if (index >= 0) api.ruleResolvers.splice(index, 1);
      getRoots().forEach(function (root) { scheduleRefresh(root); });
    };
  };
  api.refresh = function (root) {
    if (root) return refresh(root);
    return getRoots().map(function (node) { return refresh(node); });
  };
  api.getPreviewHoopPurchaseState = function (root) {
    var ui = getHoopPurchaseUi(root);
    if (!ui) return null;
    var select = ui.querySelector('[data-swishcb-hoop-purchase-select]');
    return {
      active: isHoopPurchaseActive(root),
      preset: select ? select.value : '',
      original: JSON.parse(JSON.stringify(getState(root).hoopPurchaseOriginal || null))
    };
  };
  api.restorePreviewHoopPurchaseState = function (root, snapshot, options) {
    if (!root || root.dataset.swishcbRuntimeMode !== 'order-editor') return;
    var ui = getHoopPurchaseUi(root);
    if (!ui) return;
    var state = getState(root);
    state.hoopPurchaseContextToken = (state.hoopPurchaseContextToken || 0) + 1;
    state.hoopPurchaseApplyingPreset = false;
    var preset = normalizeHoopPurchasePreset(snapshot && snapshot.preset);
    var active = !!(snapshot && snapshot.active &&
      ui.dataset.swishcbHoopPurchaseEligible === 'true' && findHoopPurchaseProduct(root, preset));
    state.hoopPurchaseOriginal = active
      ? JSON.parse(JSON.stringify(snapshot.original || null)) : null;
    ui.dataset.swishcbHoopPurchaseActive = String(active);
    var select = ui.querySelector('[data-swishcb-hoop-purchase-select]');
    if (select) select.value = active ? preset : getHoopPurchaseCatalog(root).defaultPreset;
    setHoopPurchaseStatus(root, '');
    syncHoopPurchaseVisibility(root);
    // The card's share hash already restores its model and overhangs. Restoring
    // purchase state must not apply a default model over that saved geometry.
    if (active) loadHoopPurchaseProduct(root, preset);
    if (!options || options.refresh !== false) refresh(root);
  };
  api.getState = function (root) {
    var target = root || getRoots()[0];
    if (!target) return null;
    return getState(target).result;
  };
  api.getSummary = function (root) {
    var result = api.getState(root);
    return result && result.summary ? result.summary : null;
  };
  api.getPricing = function (root) {
    var result = api.getState(root);
    return result && result.pricing ? result.pricing : null;
  };
  api.getPayment = function (root, pricing) {
    var target = root || getRoots()[0];
    return target ? paymentFor(target, pricing || api.getPricing(target)) : null;
  };
  api.setPaymentPlan = function (root, plan) {
    if (!root) return;
    if (root.querySelector('[data-swishcb-order-panel]')?.dataset.swishcbCheckoutState === 'loading') return;
    root.dataset.swishcbPaymentPlan = 'full';
    var notice = root.querySelector('[data-swishcb-checkout-notice]');
    if (notice) notice.hidden = true;
    // This does not mutate the design or cause a new 2D/3D render.
    root.dispatchEvent(new CustomEvent('swish:order-price-changed', {detail: {paymentPlan: 'full'}}));
    window.SwishCBSidebar?.refreshReview(root);
  };
  api.startCheckout = function (root, triggerButton) {
    var target = root || getRoots()[0];
    var panel = target && target.querySelector('[data-swishcb-order-panel]');
    var checkoutButton = panel && panel.querySelector('[data-swishcb-order-checkout]');
    if (!target || !panel || !checkoutButton) return Promise.resolve(false);

    var externalButton = triggerButton && triggerButton !== checkoutButton ? triggerButton : null;
    var wasDisabled = externalButton ? externalButton.disabled : false;
    if (externalButton) setExternalCheckoutLoading(externalButton, true, wasDisabled);

    return Promise.resolve(startSecureCheckout(target, panel, checkoutButton)).then(function (started) {
      if (started === false && externalButton && externalButton.isConnected) {
        setExternalCheckoutLoading(externalButton, false, wasDisabled);
      }
      return started;
    }, function (error) {
      if (externalButton && externalButton.isConnected) {
        setExternalCheckoutLoading(externalButton, false, wasDisabled);
      }
      throw error;
    });
  };
  api.prepareCheckoutDesign = function (root) {
    var target = root || getRoots()[0];
    var panel = target && target.querySelector('[data-swishcb-order-panel]');
    if (!target || !panel) return Promise.resolve(null);
    return generateOrderDesign(target, panel, { checkoutQuality: true });
  };
  api.setMode = function (root, mode) {
    var target = root || getRoots()[0];
    if (!target) return null;
    var state = getState(target);
    state.orderMode = 'split';
    target.dataset.swishcbOrderRequestedMode = 'split';
    var orderPanel = target.querySelector('[data-swishcb-order-panel]');
    var eligible = target.dataset.swishcbOrderState === 'eligible';
    var showOrder = eligible && !isHardwoodSeriesSelected(target, getBuilderPayload(target));
    target.dataset.swishcbOrderMode = 'split';
    Array.prototype.forEach.call(target.querySelectorAll('.swishcb__mode-option[data-swishcb-order-mode]'), function (control) {
      control.setAttribute('aria-checked', 'false');
      control.classList.remove('is-selected');
    });
    setContactContentVisible(target, true);
    setPanelVisible(orderPanel, showOrder);
    setOrderModeLoading(target, false);
    scheduleRefresh(target);
    return state.result;
  };
  window.SwishCB.ordering = api;

  // Returning from Shopify with Back can restore the page from the browser's
  // cache, including the checkout lock. Keep the selected plan, unlock editing.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    getRoots().forEach(function (root) {
      var panel = root.querySelector('[data-swishcb-order-panel]');
      if (!panel) return;
      panel.dataset.swishcbCheckoutState = 'ready';
      setCheckoutLoading(panel.querySelector('[data-swishcb-order-checkout]'), false, panel);
      var notice = root.querySelector('[data-swishcb-checkout-notice]');
      if (notice) notice.hidden = true;
      refresh(root);
      window.SwishCBSidebar?.refreshReview(root);
    });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
})();
